import { WebGPUCanvas, Earth, Moon, Camera, PostProcessing } from './components/webgpu/webgpuExports'
import { cameraTransitionState } from './components/camera/Camera'
import { RendererStatsTracker, RendererStatsUI } from './components/ui/debug/RendererStats'
import { HUDFrame } from './components/ui/HUDFrame'
import { Logo } from './components/ui/Logo'
import DSL_Socials from './components/ui/DSL_Socials'
import { Subheading, parseHighlights } from './components/ui/Subheading'
import { ScreenOverlay } from './components/ui/ScreenOverlay'
import DSL_Cards from './components/ui/DSL_Cards'
import { DSL_ScrollBar } from './components/ui/DSL_ScrollBar'
import DSL_Window from './components/ui/DSL_Window'
import DSL_SmallWindow from './components/ui/DSL_SmallWindow'
import { CrewProfileScreen } from './components/ui/CrewProfileScreen'
import { DidYouKnow } from './components/ui/DidYouKnow'
import { CyclingText } from './components/ui/CyclingText'
import { SatelliteInfoPanel } from './components/ui/SatelliteInfoPanel'
import starcadeGames from './data/starcadeGames.json'
import portfolioProjects from './data/portfolioProjects.json'
import { useNavigation } from './components/ui/NavigationManager'
import { Lighting } from './components/lighting/Lighting'
import { OrionFocusLighting } from './components/lighting/OrionFocusLighting'
import { Starfield } from './components/starfield/Starfield'
import { SatelliteOrbit } from './components/earth/SatelliteOrbit'
import { OrbitMarker, StaticMarker, EarthSurfaceMarker, MoonSurfaceMarker, SatelliteHotspot } from './components/ui/hotspots/Hotspots2D'
import { Environment, useTexture, useGLTF } from '@react-three/drei'
import { useState, useMemo, useRef, useCallback, useEffect, useLayoutEffect } from 'react'
import { useControls } from 'leva'
import { CustomLoader } from './components/CustomLoader'
import { useFrame, useThree } from '@react-three/fiber'
import lodConfig from './components/earth/config/lodConfig.json'
import navigationCameraConfig from './components/camera/config/navigationCameraConfig.json'
import postProcessingConfig from './components/webgpu/config/postProcessingConfig.json'
import { getDeviceInfo, getMediaQueryKey } from './utils/deviceDetection'
import { TrajectoryEditor } from './components/rocket/TrajectoryEditor'
import { Instances as GatewayInstances, Model as GatewayModel } from './components/models/Gateway'
import { DslSatInstances, DslSat } from './components/models/dsl_sat.jsx'
import { IrusModel } from './components/models/iris.jsx'
import { Prospero } from './components/models/prospero.jsx'
import { Model as SLSModel } from './components/models/Artemis_SLS_Spinny.jsx'
import { Instances as OrionInstances, Model as OrionModel } from './components/models/Artemis_Orion_Spinny.jsx'
import { MeridianModel } from './components/models/meridian.jsx'
import { LocationSpinController } from './components/locations/LocationSpinController'
import { DotGridBackground } from './components/ui/DotGridBackground'
import { ArtemisOnPath } from './components/models/ArtemisOnPath'
import locationsData from './data/locations.json'

// Hot-reload locations.json in place instead of a full page refresh.
// Vite can't HMR a static JSON import on its own (no module to "accept" from
// inside a React effect — accept() must be registered at module-eval time),
// so we mutate the existing object and notify subscribers to re-render.
// Shorthand component/hotspot labels: use `mobileText`/`tabletText` from the
// JSON when present (a shorter abbreviation), else fall back to `text`. Used by
// the SLS / Orion attachment + section hotspot labels so more fits on a phone/
// tablet screen. Module-level so both SceneContent and OrionDeployedScene can
// call it. NOTE: previously only checked mobilePortrait, so a tabletText value
// (e.g. Orion's ESM/OCM abbreviations) was silently ignored on tablet — the
// on-model floating labels always showed the full name there.
const mobileLabel = (item, mediaQueryKey) => {
  if (mediaQueryKey === 'mobilePortrait' && item?.mobileText) return item.mobileText
  if ((mediaQueryKey === 'tabletLandscape' || mediaQueryKey === 'tabletPortrait') && item?.tabletText) return item.tabletText
  return item?.text
}

// Whether a hotspot's `tabletLine` override should apply. Fires on EITHER the
// width-based tablet media keys OR the UA-based tablet flag, because neither
// alone is reliable: getMediaQueryKey() maps any landscape viewport ≥1920px to
// 'desktop1080' (so a large tablet reports a desktop key), and getDeviceInfo()
// .isTablet is UA-based (so an iPad with "Request Desktop Website" reports
// false). Together they cover both a width-detected and a UA-detected tablet.
const isTabletLineActive = (mediaQueryKey) =>
  mediaQueryKey === 'tabletLandscape'
  || mediaQueryKey === 'tabletPortrait'
  || getDeviceInfo().isTablet

const locationsHotSubscribers = new Set()
if (import.meta.hot) {
  import.meta.hot.accept('./data/locations.json', (newModule) => {
    if (!newModule) return
    Object.keys(locationsData).forEach(key => delete locationsData[key])
    Object.assign(locationsData, newModule.default)
    locationsHotSubscribers.forEach(fn => fn())
  })
}
import { ArtemisModeProvider, useArtemisMode } from './components/artemis/ArtemisModeContext'
import { PasswordGate } from './components/ui/PasswordGate'
import { ArtemisChaseCamera } from './components/camera/ArtemisChaseCamera'
import { ArtemisChaseCameraView } from './components/camera/ArtemisChaseCameraView'
import { MobilePortraitLaunchCamera } from './components/camera/MobilePortraitLaunchCamera'
import { MissionStageMarker } from './components/artemis/ArtemisMissionStages'
import { MissionLog } from './components/artemis/MissionLog'
import { ArtemisSeekBar } from './components/artemis/ArtemisSeekBar'
import { ArtemisMediaLightbox } from './components/artemis/ArtemisMediaLightbox'
import missionStagesConfig from './components/artemis/artemisMissionStages.json'

// Caption text for the "focus areas" callout boxes (see FOCUS_STAGE_IDS) — not
// derivable from the lightbox JSON, so kept as an explicit per-stage lookup.
// Each entry's image is the first slide of that stage/sub-point's lightbox.
const MISSION_FOCUS_CONTENT = {
  stage_11: { caption: 'Vote on your favourite crew photo from the lunar flyby' },
  stage_8_1: { caption: 'See planet Earth from a new perspective' },
}

// Badge (shape/color/number) for a mission stage or sub-point's lightbox — same
// derivation as the marker render loop (circleCount for primary stages, `label`
// for sub-points), so the lightbox badge always matches the marker that opened it.
function getMissionStageBadge(stageId) {
  let circleCount = 0
  for (const stage of missionStagesConfig.missionStages) {
    const isCircle = (stage.dot?.shape ?? 'circle') === 'circle'
    if (isCircle) circleCount++
    if (stage.id === stageId) {
      return { number: isCircle ? circleCount : null, shape: stage.dot?.shape ?? 'circle', color: stage.dot?.color ?? '#EE3A17' }
    }
    const sub = stage.subPoints?.find(p => p.id === stageId)
    if (sub) {
      return { number: sub.label ?? null, shape: sub.dot?.shape ?? 'diamond', color: sub.dot?.color ?? '#60A5FA' }
    }
  }
  return null
}
import { QRCodeSVG } from 'qrcode.react'
import { AmbientMusic } from './components/ui/AmbientMusic'
import { playSound, setMuted } from './utils/soundFX'
import { trackEvent } from './utils/analytics'
import { EquirectangularReflectionMapping, SpriteMaterial, CanvasTexture, AdditiveBlending, Quaternion, Vector3, Euler, Box3, Matrix4 } from 'three'
import { ParticleBurst } from './components/effects/ParticleBurst'
import { useIframeStore } from './store/iframeStore'

// SunRotation: updates sunPositionRef imperatively every frame — no React state, no re-renders.
// When targetRotation is provided the sun animates to that angle over the same duration as the
// camera transition (read from cameraTransitionState.duration at the moment the target changes),
// using the same easeInOutCubic curve so sun and camera move in lockstep.
// When targetRotation is null the sun orbits continuously.
function easeInOutCubicSun(t) {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2
}

function SunRotation({ rotationSpeed, sunDistance, sunPositionRef, targetRotation = null, artemisTRef = null, artemisStartAngle = 0 }) {
  const angleRef = useRef(0)
  const prevTargetRef = useRef(targetRotation)
  // Active tween: { startAngle, deltaAngle, elapsed, duration }
  // deltaAngle is the shortest-arc difference so the sun never spins the long way round.
  const sunTweenRef = useRef(null)

  useLayoutEffect(() => {
    if (targetRotation !== null && targetRotation !== prevTargetRef.current) {
      const start = angleRef.current
      // Compute shortest-arc delta so continuous orbit accumulation doesn't cause over-spin
      let delta = ((targetRotation - start) % (Math.PI * 2))
      if (delta > Math.PI)  delta -= Math.PI * 2
      if (delta < -Math.PI) delta += Math.PI * 2
      sunTweenRef.current = {
        startAngle: start,
        deltaAngle: delta,
        elapsed: 0,
        duration: cameraTransitionState.duration,  // match camera transition exactly
      }
    }
    prevTargetRef.current = targetRotation
  }, [targetRotation])

  useFrame((_, delta) => {
    if (artemisTRef?.current !== null && artemisTRef?.current !== undefined) {
      // Artemis II mission: 9d 1h 32m 15s = 9.0640625 solar days, offset from start angle
      angleRef.current = artemisStartAngle + artemisTRef.current * Math.PI * 2 * 9.0640625
    } else if (sunTweenRef.current) {
      const tw = sunTweenRef.current
      tw.elapsed = Math.min(tw.elapsed + delta, tw.duration)
      const t = easeInOutCubicSun(tw.elapsed / tw.duration)
      angleRef.current = tw.startAngle + tw.deltaAngle * t
      if (tw.elapsed >= tw.duration) {
        angleRef.current = tw.startAngle + tw.deltaAngle   // snap to exact end value
        sunTweenRef.current = null
      }
    } else if (targetRotation === null) {
      angleRef.current += rotationSpeed * delta
    }

    const a = angleRef.current % (Math.PI * 2)
    sunPositionRef.current[0] = Math.cos(a) * sunDistance * 0.2
    sunPositionRef.current[1] = 0
    sunPositionRef.current[2] = Math.sin(a) * sunDistance * 0.2
  })
  return null
}

// Billboard sprite at the sun's world position — visible to all cameras including chase cam
function SunSprite({ sunPositionRef }) {
  const meshRef = useRef()
  const material = useMemo(() => {
    const size = 256
    const canvas = document.createElement('canvas')
    canvas.width = size
    canvas.height = size
    const ctx = canvas.getContext('2d')
    const grad = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2)
    grad.addColorStop(0, 'rgba(255, 255, 255, 1)')
    grad.addColorStop(0.1, 'rgba(255, 230, 200, 1)')
    grad.addColorStop(0.25, 'rgba(220, 130, 100, 0.6)')
    grad.addColorStop(0.5, 'rgba(180, 80, 60, 0.25)')
    grad.addColorStop(1, 'rgba(140, 60, 40, 0)')
    ctx.fillStyle = grad
    ctx.fillRect(0, 0, size, size)
    const tex = new CanvasTexture(canvas)
    return new SpriteMaterial({
      map: tex,
      blending: AdditiveBlending,
      depthWrite: false,
      transparent: true,
    })
  }, [])

  useEffect(() => () => {
    material.map?.dispose()
    material.dispose()
  }, [material])

  useFrame(() => {
    if (!meshRef.current || !sunPositionRef?.current) return
    const [x, y, z] = sunPositionRef.current
    meshRef.current.position.set(x, y, z)
  })

  useEffect(() => {
    if (meshRef.current) meshRef.current.layers.set(4)
  }, [])

  return <sprite ref={meshRef} material={material} scale={[10, 10, 10]} />
}

// SatelliteScaleAnimator: drives scale on each satellite group ref imperatively
// every frame — no React state, no re-renders.
// targetScalesRef is an array of { ref, target } where target is the desired
// uniform scale value (0 = hidden, >0 = visible at that scale).
// Each frame each model's scale lerps toward its target so models scale in/out
// smoothly instead of popping.
const SAT_SCALE_SPEED = 8  // higher = faster scale transition
function SatelliteScaleAnimator({ targetScalesRef }) {
  useFrame((_, delta) => {
    const entries = targetScalesRef.current
    if (!entries) return
    for (const { ref, target } of entries) {
      if (!ref.current) continue
      const s = ref.current.scale
      const cur = s.x
      if (Math.abs(cur - target) < 0.0001) {
        if (cur !== target) s.setScalar(target)
        continue
      }
      const next = cur + (target - cur) * Math.min(delta * SAT_SCALE_SPEED, 1)
      s.setScalar(next)
    }
  })
  return null
}

// SlsRotationDebug: uses rAF directly — no React, reads quaternion every frame
function SlsRotationDebug({ groupRef }) {
  useEffect(() => {
    const d = document.createElement('div')
    d.style.cssText = 'position:fixed;top:16px;right:16px;z-index:99999;background:rgba(0,0,0,0.8);color:#EE3A17;font-family:monospace;font-size:13px;padding:8px 12px;line-height:1.8;pointer-events:none;border:1px solid #EE3A17'
    document.body.appendChild(d)
    let raf
    const e = new Euler()
    const tick = () => {
      if (groupRef.current) {
        e.setFromQuaternion(groupRef.current.quaternion, 'YXZ')
        const worldY = e.y * (180 / Math.PI)
        d.textContent = `world Y: ${worldY.toFixed(1)}°`
      } else {
        d.textContent = 'no ref'
      }
      raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => { cancelAnimationFrame(raf); d.remove() }
  }, [groupRef])
  return null
}


// Separates ESM_Ctrl and Integrity_Ctrl along Z after umbilical rotation completes
// Settled deployed-separation z per section, keyed by deployedLabel id. These are
// the positions OrionSeparationAnimator eases each section toward while deployed.
// The section look-at capture (focusOrionSection + the inline label onClick) reads
// them so it always projects the pivot from the SETTLED z, not the section's live
// in-flight z — otherwise the captured look-at depends on how far the separation
// ease had progressed AND whether the section was skipped while another was focused
// (the animator only advances non-focused sections). That made ICPS/OCM frame at a
// different point on first visit vs. a return from another section. Must match the
// targets in OrionSeparationAnimator's deployed branch. (ESM stays at z=0.)
const ORION_SECTION_SETTLED_Z = {
  icps: -4.6 - 5,   // ICSP_TARGET
  integrity: 2.65 + 5, // 2.65 + INT_TARGET
  esm: 0,
}

function OrionSeparationAnimator({ esmRef, intRef, icspRef, deployed, onReverseComplete, focusedSection }) {
  const INT_TARGET     =  5
  const ICSP_REST      = -4.6
  const ICSP_TARGET    = ICSP_REST - 5
  const DELAY_ICSP     = 0
  const DELAY_INT      = 0.8 // matches OrionUmbilicalAnimator's DELAY — OCM separates as the umbilical starts rotating
  const timerRef       = useRef(0)
  const reverseDoneRef = useRef(false)

  useEffect(() => {
    if (!deployed) {
      timerRef.current     = 0
      reverseDoneRef.current = false
    } else {
      // Reset the one-shot reverse guard at the start of each deploy too, so a new
      // deploy→back cycle can always re-fire onReverseComplete even if the previous
      // cycle's reverse was interrupted (rapid back presses).
      reverseDoneRef.current = false
    }
  }, [deployed])

  useFrame((_, delta) => {
    if (!deployed) {
      // Rest-pose guard: once the reverse animation has settled, hard-hold the
      // exact rest transforms instead of easing forever. The exponential ease is
      // asymptotic — it never actually lands — so every deploy cycle used to
      // start from wherever the previous cut-off left the groups.
      if (reverseDoneRef.current) {
        applyOrionSectionRest(esmRef, intRef, icspRef)
        return
      }
      const rt = 1 - Math.pow(0.05, delta)
      if (esmRef.current) esmRef.current.position.z += (0 - esmRef.current.position.z) * rt
      if (intRef.current) intRef.current.position.z += (2.65 - intRef.current.position.z) * rt
      if (icspRef.current) icspRef.current.position.z += (ICSP_REST - icspRef.current.position.z) * rt
      if (!reverseDoneRef.current && esmRef.current && intRef.current) {
        if (Math.abs(esmRef.current.position.z) < 0.05 && Math.abs(intRef.current.position.z - 2.65) < 0.05) {
          // Settled — snap to the exact rest pose (full position AND rotation,
          // see applyOrionSectionRest) so the assembly is verifiably back where
          // it should be at the end of every reverse animation.
          applyOrionSectionRest(esmRef, intRef, icspRef)
          reverseDoneRef.current = true
          if (onReverseComplete) onReverseComplete()
        }
      }
      return
    }

    timerRef.current += delta

    const ft = 1 - Math.pow(0.05, delta)
    // While a section is focused, the SectionSpinController owns that group's
    // transform — skip it here so the two don't fight over position.
    // Each ease snaps onto its exact target once within a whisker so the settled
    // deployed z is always precisely ORION_SECTION_SETTLED_Z (the section look-at
    // capture and basePos snapshots read the live value).
    if (timerRef.current >= DELAY_ICSP && icspRef.current && focusedSection !== 'icps') {
      icspRef.current.position.z += (ICSP_TARGET - icspRef.current.position.z) * ft
      if (Math.abs(icspRef.current.position.z - ICSP_TARGET) < 0.001) icspRef.current.position.z = ICSP_TARGET
    }
    if (timerRef.current >= DELAY_INT && intRef.current && focusedSection !== 'integrity') {
      const target = 2.65 + INT_TARGET
      intRef.current.position.z += (target - intRef.current.position.z) * ft
      if (Math.abs(intRef.current.position.z - target) < 0.001) intRef.current.position.z = target
    }
  })
  return null
}

// Exact rest transforms for the three Orion ctrl groups — must match the rest
// positions seeded in Artemis_Orion_Spinny.jsx's Model mount effect. Both
// position AND rotation are asserted: the separation animator only ever eases
// position.z, but the SectionSpinController writes the full position and
// rotation of these same groups, so an interrupted section return can leave
// x/y/rotation residue that a z-only ease would never correct — over a long
// unattended session those residues accumulate into visible drift.
function applyOrionSectionRest(esmRef, intRef, icspRef) {
  if (esmRef.current)  { esmRef.current.position.set(0, 0, 0);      esmRef.current.quaternion.identity() }
  if (intRef.current)  { intRef.current.position.set(0, 0, 2.65);   intRef.current.quaternion.identity() }
  if (icspRef.current) { icspRef.current.position.set(0, 0, -4.6);  icspRef.current.quaternion.identity() }
}

// keyed by deployedLabel id (note: "icps", not "icsp")
const ORION_SECTION_KEYS = ['icps', 'esm', 'integrity']

// Hides non-focused Orion sections when one section is focused.
// drei's <Instances> rewrites every instance matrix from instance.matrixWorld each
// frame and ignores `visible`, so toggling `visible` alone can't hide instanced
// geometry. Collapsing the ctrl group's scale to ~0 propagates through matrixWorld
// and zeroes every descendant instance; `visible` handles the plain meshes too.
function OrionSectionVisibility({ icspRef, esmRef, intRef, focusedSection, enabled }) {
  const refs = useMemo(() => ({ icps: icspRef, esm: esmRef, integrity: intRef }), [icspRef, esmRef, intRef])
  const progress = useRef({ icps: 1, esm: 1, integrity: 1 })

  useFrame((_, delta) => {
    const rt = 1 - Math.pow(0.0001, delta)
    for (const key of ORION_SECTION_KEYS) {
      const group = refs[key].current
      if (!group) continue
      // target: 1 = shown, 0 = hidden. Hidden only when a *different* section is focused.
      const target = (enabled && focusedSection && focusedSection !== key) ? 0 : 1
      const p = progress.current
      p[key] += (target - p[key]) * rt
      if (target === 1 && p[key] > 0.999) p[key] = 1
      if (target === 0 && p[key] < 0.001) p[key] = 0
      const s = Math.max(p[key], 0.0001)
      group.scale.setScalar(s)
      group.visible = p[key] > 0.001
    }
  })
  return null
}

// Drag-to-spin controller for a single deployed Orion section.
// Rotates the ctrl group about world-Y, pivoting around the section's own
// geometric centre so it spins in place rather than orbiting the assembly.
// The ctrl group's position.z is owned by OrionSeparationAnimator; this only
// adds an in-place yaw, so it composes T(sepPos) · T(centre) · Ryaw · T(-centre).
const _secDragSensitivity = 0.005
const _secFriction        = 0.015
const _secReturnSmooth     = 0.5

// Shared flip-band test. `band` is [startDeg, endDeg] — the two trigger angles
// (0–360, measured from the section's rest). The label is "flipped" while the
// yaw is inside the arc that runs clockwise from startDeg to endDeg, so you can
// place each trigger point explicitly. Used both per-frame and at view
// transitions so labels render correct on their first frame.
//
// `prev` enables hysteresis: near either trigger the result sticks to the
// previous state within ±FLIP_HYSTERESIS, so a section resting on a boundary
// can't chatter true/false every frame (which would re-render the tree).
const FLIP_HYSTERESIS = 3.6 // degrees — 1% of a full 360° rotation
const DEFAULT_FLIP_BAND = [225, 45] // equivalent to the old (-135°, 45°] window
// Abbreviated labels for the Orion section-switcher contextual buttons.
const ORION_SECTION_ABBR = { icps: 'ICPS', esm: 'ESM', integrity: 'OCM' }
// Shortest signed angular distance a→b in degrees, result in (-180, 180].
function _angDelta(a, b) {
  let d = (b - a) % 360
  if (d > 180) d -= 360
  if (d <= -180) d += 360
  return d
}
// Is `deg` inside the clockwise arc from start to end?
function _inArc(deg, start, end) {
  const span = (((end - start) % 360) + 360) % 360 // 0–360 clockwise
  const rel  = (((deg - start) % 360) + 360) % 360
  return rel <= span
}
function computeYFlip(yawRad, band = DEFAULT_FLIP_BAND, prev = null) {
  let deg = (yawRad * (180 / Math.PI)) % 360
  if (deg < 0) deg += 360
  const [start, end] = band
  const inside = _inArc(deg, start, end)
  if (prev === null) return inside
  const nearEdge = Math.abs(_angDelta(deg, start)) < FLIP_HYSTERESIS
    || Math.abs(_angDelta(deg, end)) < FLIP_HYSTERESIS
  return nearEdge ? prev : inside
}

function SectionSpinController({ groupRef, isFocused, pivot, pivotOffset, lockYaw = null, lockPitch = 0, lockRoll = 0, locked = false, onYFlip = null, flipBand = DEFAULT_FLIP_BAND }) {
  const { gl } = useThree()
  const lockYawRef = useRef(lockYaw)
  const lockPitchRef = useRef(lockPitch)
  const lockRollRef  = useRef(lockRoll)
  const lockedRef  = useRef(locked)
  const frozenYawRef = useRef(null) // yaw captured when level 3 engaged with no target
  const flippedRef = useRef(null)
  // pitch/roll (radians) — only driven in the locked (level 3) state, eased to
  // their targets and back to 0 on return. Free spin (level 2) stays yaw-only.
  const pitchRef = useRef(0)
  const rollRef  = useRef(0)
  useEffect(() => { lockYawRef.current = lockYaw }, [lockYaw])
  useEffect(() => { lockPitchRef.current = lockPitch }, [lockPitch])
  useEffect(() => { lockRollRef.current = lockRoll }, [lockRoll])
  // Invalidate the cached flip state on focus/lock changes so the next active
  // frame re-emits onYFlip with the correct value (e.g. returning from level 3).
  useEffect(() => { flippedRef.current = null }, [isFocused, locked])
  const yawRef        = useRef(0)            // current yaw (radians) around section centre
  useEffect(() => {
    const wasLocked = lockedRef.current
    lockedRef.current = locked
    if (!locked) {
      frozenYawRef.current = null
      // Leaving level 3 — return the section to its rest orientation. Normalise
      // yaw to the shortest path first so it doesn't unwind multiple turns.
      if (wasLocked) {
        const TWO_PI = Math.PI * 2
        yawRef.current = ((yawRef.current % TWO_PI) + TWO_PI) % TWO_PI
        if (yawRef.current > Math.PI) yawRef.current -= TWO_PI
        returningRef.current = true
      }
    }
  }, [locked]) // eslint-disable-line react-hooks/exhaustive-deps
  const velRef        = useRef(0)            // angular velocity (rad/s) for inertia
  const draggingRef   = useRef(false)
  const returningRef  = useRef(false)
  const lastXRef      = useRef(0)
  const samplesRef    = useRef([])
  const rawCentreRef  = useRef(new Vector3()) // measured section-local geometry centre
  const centreRef     = useRef(new Vector3()) // rawCentre + pivotOffset, recomputed each frame
  const measuredRef   = useRef(false)
  const basePosRef    = useRef(new Vector3()) // settled separation position, snapshotted on focus
  const basePosSetRef = useRef(false)         // true once basePos has been snapshotted from a real focus

  // Measure the section's local-space geometry centre once it's available
  const measureCentre = () => {
    const group = groupRef.current
    if (!group) return false
    const box = new Box3()
    // Temporarily neutralise our yaw so the box reflects the un-spun pose
    const savedQuat = group.quaternion.clone()
    group.quaternion.identity()
    group.updateMatrixWorld(true)
    box.setFromObject(group)
    if (box.isEmpty()) { group.quaternion.copy(savedQuat); return false }
    const worldCentre = box.getCenter(new Vector3())
    // Convert world centre into the group's local space
    const inv = new Matrix4().copy(group.matrixWorld).invert()
    rawCentreRef.current.copy(worldCentre).applyMatrix4(inv)
    group.quaternion.copy(savedQuat)
    return true
  }

  useEffect(() => {
    if (!isFocused) {
      draggingRef.current  = false
      samplesRef.current   = []
      velRef.current       = 0
      // Normalise accumulated yaw to (-π, π] so the return takes the shortest
      // path instead of unwinding every full turn the user spun through.
      const TWO_PI = Math.PI * 2
      yawRef.current = ((yawRef.current % TWO_PI) + TWO_PI) % TWO_PI
      if (yawRef.current > Math.PI) yawRef.current -= TWO_PI
      returningRef.current = true
      return
    }
    returningRef.current = false
    if (!measuredRef.current) measuredRef.current = measureCentre()
    // Snapshot the settled separation position — it's static while focused
    if (groupRef.current) { basePosRef.current.copy(groupRef.current.position); basePosSetRef.current = true }

    const canvas = gl.domElement
    const onDown = (e) => {
      if (lockedRef.current) return  // level 3 — rotation is locked
      draggingRef.current = true
      lastXRef.current    = e.clientX
      samplesRef.current  = []
      velRef.current      = 0
      canvas.setPointerCapture(e.pointerId)
    }
    const onMove = (e) => {
      if (!draggingRef.current || lockedRef.current) return
      const now = performance.now()
      const dx  = e.clientX - lastXRef.current
      lastXRef.current = e.clientX
      yawRef.current += dx * _secDragSensitivity
      samplesRef.current.push({ t: now, dx })
      samplesRef.current = samplesRef.current.filter(s => s.t >= now - 120)
    }
    const onUp = (e) => {
      draggingRef.current = false
      canvas.releasePointerCapture(e.pointerId)
      const now = performance.now()
      // Keep only samples within the velocity window measured from the RELEASE
      // moment — so a pause before lifting (no move events fire) leaves an empty
      // window and the model stops dead instead of coasting on stale motion.
      const s = samplesRef.current.filter(p => p.t >= now - 120)
      if (s.length >= 1) {
        // dt spans from the oldest kept sample to the actual release time, so a
        // gap between last movement and release decays the released speed.
        const dt = (now - s[0].t) / 1000
        if (dt > 0) {
          const totalDx = s.reduce((a, p) => a + p.dx, 0)
          velRef.current = (totalDx * _secDragSensitivity) / dt
        }
      }
      samplesRef.current = []
    }
    canvas.addEventListener('pointerdown',   onDown)
    canvas.addEventListener('pointermove',   onMove)
    canvas.addEventListener('pointerup',     onUp)
    canvas.addEventListener('pointercancel', onUp)
    return () => {
      canvas.removeEventListener('pointerdown',   onDown)
      canvas.removeEventListener('pointermove',   onMove)
      canvas.removeEventListener('pointerup',     onUp)
      canvas.removeEventListener('pointercancel', onUp)
      draggingRef.current = false
    }
  }, [isFocused, gl]) // eslint-disable-line react-hooks/exhaustive-deps

  // On unmount, snap the section's rotation back to rest. The deployed scene
  // unmounts when leaving Orion (e.g. pressing HOME deep in a section), which can
  // cut off the spring-return mid-animation and leave the sub-section rotated;
  // resetting here guarantees a clean orientation on the next deploy.
  useEffect(() => {
    return () => {
      yawRef.current = 0
      pitchRef.current = 0
      rollRef.current = 0
      velRef.current = 0
      returningRef.current = false
      if (groupRef.current) groupRef.current.quaternion.identity()
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useFrame((_, delta) => {
    const group = groupRef.current
    if (!group) return

    // Perf #4: idle fast-path. When not focused and not mid-return, there is nothing
    // to drive — bail before touching pivot/centre refs or the branch ladder. (The
    // section's transform is owned by OrionSeparationAnimator while idle.)
    if (!isFocused && !returningRef.current) return

    // Never write position from an un-snapshotted basePos. On first mount the
    // unfocused effect sets returningRef=true, but basePos is still (0,0,0) until
    // a real focus snapshots it — driving the group toward (0,0,0) would fight
    // OrionSeparationAnimator and yank the section to the origin. Until then the
    // separation animator owns the transform, so bail.
    if (!basePosSetRef.current) { returningRef.current = false; return }

    // Prefer the baked absolute centre from config; only measure at runtime if
    // none was supplied.
    if (pivot) {
      rawCentreRef.current.set(pivot[0], pivot[1], pivot[2])
      measuredRef.current = true
    } else if (!measuredRef.current && isFocused) {
      measuredRef.current = measureCentre()
    }

    if (isFocused && lockedRef.current) {
      // Level 3 — rotation is locked. Ease yaw to the target (or freeze at the
      // current yaw when no target is set). User input + coast are suppressed.
      velRef.current = 0
      returningRef.current = false
      draggingRef.current = false
      if (lockYawRef.current === null && frozenYawRef.current === null) {
        frozenYawRef.current = yawRef.current  // freeze in place until a target exists
      }
      const target = lockYawRef.current !== null ? lockYawRef.current : frozenYawRef.current
      const TWO_PI = Math.PI * 2
      let diff = (target - yawRef.current) % TWO_PI
      if (diff > Math.PI) diff -= TWO_PI
      if (diff < -Math.PI) diff += TWO_PI
      const t = 1 - Math.pow(2, -delta / _secReturnSmooth)
      yawRef.current += diff * t
      // Ease pitch/roll toward their locked targets too.
      pitchRef.current += (lockPitchRef.current - pitchRef.current) * t
      rollRef.current  += (lockRollRef.current  - rollRef.current)  * t
    } else if (returningRef.current) {
      if (draggingRef.current) {
        returningRef.current = false
      } else {
        const t = 1 - Math.pow(2, -delta / _secReturnSmooth)
        yawRef.current   += (0 - yawRef.current) * t
        pitchRef.current += (0 - pitchRef.current) * t
        rollRef.current  += (0 - rollRef.current)  * t
        if (Math.abs(yawRef.current) < 0.001 && Math.abs(pitchRef.current) < 0.001 && Math.abs(rollRef.current) < 0.001) {
          yawRef.current = 0; pitchRef.current = 0; rollRef.current = 0; returningRef.current = false
        }
      }
    } else if (isFocused && !draggingRef.current) {
      const friction = Math.pow(_secFriction, delta)
      velRef.current *= friction
      if (Math.abs(velRef.current) > 0.0001) yawRef.current += velRef.current * delta
    } else if (!isFocused) {
      // Idle and settled — hand the group back to OrionSeparationAnimator
      return
    }

    // Effective pivot = measured centre + manual offset (recomputed each frame so
    // pivotOffset can be tuned live via locations.json).
    const po = pivotOffset ?? _secZero
    const c = centreRef.current.set(
      rawCentreRef.current.x + po[0],
      rawCentreRef.current.y + po[1],
      rawCentreRef.current.z + po[2]
    )
    // basePos was snapshotted on focus (separation is settled while focused)
    // Compose local matrix: T(basePos) · T(centre) · R · T(-centre)
    // R = yaw·pitch·roll about WORLD axes, expressed in this group's parent space
    // so they stay screen-stable regardless of how the outer/model groups are tilted.
    // Capture the parent's inverse world rotation once.
    const haveParent = !!group.parent
    if (haveParent) group.parent.getWorldQuaternion(_secParentQuat).invert()
    const axisInParent = (worldAxis, out) => {
      out.copy(worldAxis)
      if (haveParent) out.applyQuaternion(_secParentQuat).normalize()
      return out
    }
    // Yaw (world-Y)
    _secYawQuat.setFromAxisAngle(axisInParent(_secWorldY, _secAxis), yawRef.current)
    if (pitchRef.current || rollRef.current) {
      _secPitchQuat.setFromAxisAngle(axisInParent(_secWorldX, _secAxis), pitchRef.current)
      _secRollQuat.setFromAxisAngle(axisInParent(_secWorldZ, _secAxis), rollRef.current)
      _secYawQuat.multiply(_secPitchQuat).multiply(_secRollQuat)
    }
    _secMat.makeRotationFromQuaternion(_secYawQuat)
    _secMat.setPosition(
      basePosRef.current.x + c.x - (_secMat.elements[0] * c.x + _secMat.elements[4] * c.y + _secMat.elements[8]  * c.z),
      basePosRef.current.y + c.y - (_secMat.elements[1] * c.x + _secMat.elements[5] * c.y + _secMat.elements[9]  * c.z),
      basePosRef.current.z + c.z - (_secMat.elements[2] * c.x + _secMat.elements[6] * c.y + _secMat.elements[10] * c.z)
    )
    group.quaternion.copy(_secYawQuat)
    group.position.set(_secMat.elements[12], _secMat.elements[13], _secMat.elements[14])

    if (onYFlip) {
      const flipped = computeYFlip(yawRef.current, flipBand, flippedRef.current)
      if (flipped !== flippedRef.current) {
        flippedRef.current = flipped
        onYFlip(flipped)
      }
    }
  })
  return null
}
const _secYawQuat   = new Quaternion()
const _secPitchQuat = new Quaternion()
const _secRollQuat  = new Quaternion()
const _secWorldX    = new Vector3(1, 0, 0)
const _secWorldY    = new Vector3(0, 1, 0)
const _secWorldZ    = new Vector3(0, 0, 1)
const _secMat       = new Matrix4()
const _secAxis      = new Vector3()
const _secParentQuat = new Quaternion()
const _secZero      = [0, 0, 0]
const _flipEulerTmp = new Euler()
// Scratch for neutralising the Orion outer group to its REST pose (not identity)
// before reading a section's settled world position. The outer rest pose is
// [0, π/4, 0] on mobile portrait, [0,0,0] otherwise — see the outer
// LocationSpinController's initialRotation. Identity would mis-read on mobile.
const _outerRestQuat = new Quaternion()
const _outerRestEuler = new Euler()
function orionOuterRestQuat(mediaQueryKey) {
  _outerRestEuler.set(0, mediaQueryKey === 'mobilePortrait' ? Math.PI / 4 : 0, 0, 'XYZ')
  return _outerRestQuat.setFromEuler(_outerRestEuler)
}

// Dev-only marker placed at a section's spin pivot. Follows the section's live
// world transform each frame (so it rides along as the section spins/separates)
// and offsets by the local `pivot` point. Render as a sibling of the scene root
// so its local space equals world space.
function SectionPivotGizmo({ groupRef, pivot }) {
  const gizmoRef = useRef()
  useFrame(() => {
    const group = groupRef.current
    const gizmo = gizmoRef.current
    if (!group || !gizmo || !pivot) return
    group.updateWorldMatrix(true, false)
    _gizmoVec.set(pivot[0], pivot[1], pivot[2]).applyMatrix4(group.matrixWorld)
    gizmo.position.copy(_gizmoVec)
    gizmo.visible = group.visible
  })
  return (
    <group ref={gizmoRef}>
      <axesHelper args={[0.6]} />
      <mesh>
        <sphereGeometry args={[0.005, 16, 16]} />
        <meshBasicMaterial color="#ff00ff" depthTest={false} transparent opacity={0.9} />
      </mesh>
    </group>
  )
}
const _gizmoVec = new Vector3()

// Level 3 — keeps the camera target locked onto an attachment point's live world
// position. The section animates (lockYaw) into place, moving the attachment, so
// the lookAt must track it every frame to avoid drift. Runs after the camera's
// own useFrame and eases the CameraControls target via its smoothTime. Owns only
// the target while active — camera position + FOV are still handled by Camera.jsx.
// lookAtOffset is camera-relative [right, up, forward] in world units, so a
// positive X pushes the target to screen-right (e.g. to clear space for a text
// box) regardless of how the section is oriented.
function OrionAttachmentLookAtTracker({ active, lookAtRef, lookAtOffset, sectionGroupRef, sectionPivot }) {
  const { camera } = useThree()
  useFrame(() => {
    if (!active) return
    const controls = window.orbitControlsRef
    if (!controls) return
    // Look-at depends ONLY on lookAtOffset (not the attachment mesh or the
    // section rotation). Anchor to the section's ROTATION-FREE origin in world
    // space, fully decoupled from spinPivot: take the group's settled position
    // (parent space) with its own spin quaternion stripped, then transform by the
    // parent's world matrix. Because the section spin (R about the pivot c) is
    // removed, two points sharing a lookAtOffset resolve to the identical target
    // and the camera holds still regardless of how the section is spun.
    //   group.position = basePos + c - R·c   →   basePos = group.position - R·c + c... but
    // we only need a rotation-free reference, so reconstruct restPos = group.position
    // with the spin's effect on the pivot removed: restPos = position + R·c - c.
    const sg = sectionGroupRef?.current
    if (!sg) return
    const c = sectionPivot ?? _secZero
    sg.updateWorldMatrix(true, false)
    // R·c (spin applied to pivot, in parent space)
    _attachVec.set(c[0], c[1], c[2]).applyQuaternion(sg.quaternion)
    // restPos = group.position + R·c − c  (rotation-free section origin in parent space)
    _attachVec.x += sg.position.x - c[0]
    _attachVec.y += sg.position.y - c[1]
    _attachVec.z += sg.position.z - c[2]
    // Into world space via the PARENT (excludes the section's own spin)
    if (sg.parent) { sg.parent.updateWorldMatrix(true, false); _attachVec.applyMatrix4(sg.parent.matrixWorld) }

    applyCameraOffset(_attachVec, camera, lookAtOffset)
    // Publish for activeCameraConfig (so the entry transition aims here too)
    if (lookAtRef) lookAtRef.current = [_attachVec.x, _attachVec.y, _attachVec.z]
    // While Camera.jsx's entry transition is running, it already eases the lookAt
    // toward this same (predicted) point — don't add a second ease on top, or the
    // motion reads as two stages. Once the transition finishes, we hold/refine the
    // target by snapping (it's already there, so no visible motion).
    if (cameraTransitionState.active) return
    controls.setTarget(_attachVec.x, _attachVec.y, _attachVec.z, false)
  }, 1) // priority 1: after Camera.jsx (priority 0 / -1) so we own the final target
  return null
}
const _attachVec   = new Vector3()
const _attachRight = new Vector3()
const _attachUp    = new Vector3()
const _attachFwd   = new Vector3()

// Compute an SLS attachment's world position AS IF the model were already at its
// REST pose (modelRoot quaternion = restQuat), regardless of any current drag
// rotation. Focusing a part locks the model and springs it back to rest, which
// moves the attachment — seeding/tracking off the live (rotated) pose makes the
// camera snap when the spring finishes. Anchoring to the rest pose removes that:
// temporarily set the root's quaternion to rest, read the attachment's world
// position, then restore the live quaternion (no visible change — done within a
// frame's matrix update, not committed to a render).
const _slsRestQuat = new Quaternion()
const _slsRestEuler = new Euler()
const _slsSavedQuat = new Quaternion()
const _slsExtraYQuat = new Quaternion()
const _slsWorldY = new Vector3(0, 1, 0)
function slsAttachmentRestWorldPos(out, attachment, modelRoot, restRotation, extraWorldYRad = 0) {
  if (!attachment || !modelRoot) { attachment?.getWorldPosition(out); return out }
  // Rest pose = world-Y(extraWorldYRad) ∘ base(restRotation Euler) — must match
  // LocationSpinController.buildRestQuat so the look-at anchor lands exactly where
  // the model settles (incl. the per-part spin), otherwise the camera would snap.
  _slsRestEuler.set(restRotation[0], restRotation[1], restRotation[2], 'XYZ')
  _slsRestQuat.setFromEuler(_slsRestEuler)
  if (extraWorldYRad) {
    _slsExtraYQuat.setFromAxisAngle(_slsWorldY, extraWorldYRad)
    _slsRestQuat.premultiply(_slsExtraYQuat)
  }
  _slsSavedQuat.copy(modelRoot.quaternion)
  modelRoot.quaternion.copy(_slsRestQuat)
  modelRoot.updateWorldMatrix(true, true) // root + descendants at rest pose
  attachment.getWorldPosition(out)
  // Restore the live quaternion + matrices so we don't disturb the spring/render
  modelRoot.quaternion.copy(_slsSavedQuat)
  modelRoot.updateWorldMatrix(true, true)
  return out
}

// SLS attachment look-at tracker — keeps the camera target locked onto the focused
// SLS touch point. Uses the attachment's REST-pose world position (not its live,
// possibly mid-spring position) so the camera holds the exact point the entry
// transition aimed at — no snap when the model settles. lookAtOffset is
// camera-relative [right, up, forward] world units.
function SlsAttachmentLookAtTracker({ active, attachmentRef, modelRef, restRotation, extraWorldYRad = 0, lookAtRef, lookAtOffset }) {
  const { camera } = useThree()
  useFrame(() => {
    if (!active) return
    const controls = window.orbitControlsRef
    const target = attachmentRef?.current
    if (!controls || !target) return
    slsAttachmentRestWorldPos(_attachVec, target, modelRef?.current, restRotation, extraWorldYRad)
    applyCameraOffset(_attachVec, camera, lookAtOffset)
    if (lookAtRef) lookAtRef.current = [_attachVec.x, _attachVec.y, _attachVec.z]
    // While Camera.jsx's entry transition runs it already eases toward this same
    // (seeded) point — don't double-ease. After it finishes, snap to hold the target.
    if (cameraTransitionState.active) return
    controls.setTarget(_attachVec.x, _attachVec.y, _attachVec.z, false)
  }, 1) // priority 1: after Camera.jsx so we own the final target
  return null
}

// Shift a world point by a camera-relative offset [right, up, forward] (world
// units). Shared by the live tracker and the entry seed so both land on the
// exact same point — the camera makes a single move, not a two-stage ease.
//
// The basis is built from the camera's POSITION and the un-offset target
// (i.e. "if the camera looked straight at this point, which way is right/up/
// forward") rather than read from camera.matrixWorld. The live orientation
// changes every frame while the lookAt transition is easing toward this exact
// point, so a matrixWorld-read basis differs between the pre-transition seed
// (camera still aimed at the PREVIOUS target) and the post-transition tracker
// (camera now aimed at THIS target) — two different offsets computed for what
// should be one fixed point, which read as a jolt/snap when the tracker took
// over. Camera position doesn't move during these hotspot-to-hotspot moves
// (only lookAt/fov ease), so this direction is stable across the whole
// transition and matches where the camera ends up, closing the gap.
function applyCameraOffset(vec, camera, offset) {
  if (!offset || (!offset[0] && !offset[1] && !offset[2]) || !camera) return vec
  _attachFwd.copy(vec).sub(camera.position).normalize()
  _attachRight.crossVectors(_attachFwd, camera.up).normalize()
  _attachUp.crossVectors(_attachRight, _attachFwd).normalize()
  return vec
    .addScaledVector(_attachRight, offset[0])
    .addScaledVector(_attachUp, offset[1])
    .addScaledVector(_attachFwd, offset[2]) // +forward = toward the scene (already the sign here)
}

// Picks the right lookAtOffset field for the current breakpoint. Mirrors the
// camera field's own tiering (camera / tabletCamera / mobileCamera): a hotspot
// or deployedLabel can define lookAtOffset (base/desktop), tabletLookAtOffset,
// and mobileLookAtOffset side by side. Falls through desktop → tablet → mobile
// so a tier without its own override still gets the (closest) configured value
// instead of silently landing on desktop's. Before tabletLookAtOffset existed,
// every call site here only checked `mediaQueryKey === 'mobilePortrait'`, so
// tabletLandscape/tabletPortrait always used the desktop offset no matter what
// — editing "the tablet look-at" had nowhere to go.
function resolveLookAtOffset(obj, mediaQueryKey) {
  if (!obj) return undefined
  if (mediaQueryKey === 'mobilePortrait' && obj.mobileLookAtOffset) return obj.mobileLookAtOffset
  if ((mediaQueryKey === 'tabletLandscape' || mediaQueryKey === 'tabletPortrait') && obj.tabletLookAtOffset) return obj.tabletLookAtOffset
  return obj.lookAtOffset
}

// deployedLabels use a DIFFERENT shape than hotspots: lookAtOffset is nested
// INSIDE each camera tier (label.camera.lookAtOffset / label.tabletCamera.
// lookAtOffset / label.mobileCamera.lookAtOffset) rather than as sibling fields.
// Picks the tier object for the current breakpoint, matching activeCameraConfig's
// own camera/tabletCamera/mobileCamera resolution — previously this only ever
// checked mobileCamera vs camera, so tabletCamera.lookAtOffset (which already
// exists in locations.json for the Orion module labels) was never read.
function resolveLabelCameraConfig(label, mediaQueryKey) {
  if (!label) return undefined
  if (mediaQueryKey === 'mobilePortrait' && label.mobileCamera) return label.mobileCamera
  if ((mediaQueryKey === 'tabletLandscape' || mediaQueryKey === 'tabletPortrait') && label.tabletCamera) return label.tabletCamera
  return label.camera
}

// ESM_umbilical JSX rotation — used to derive rest/deploy quats statically
const _umbilicalRestQuat   = new Quaternion().setFromEuler(new Euler(0, 0, -0.29, 'XYZ'))
const _umbilicalDeployQuat = _umbilicalRestQuat.clone().multiply(
  new Quaternion().setFromAxisAngle(new Vector3(1, 0, 0), -Math.PI / 2)
)

// Animates the Orion umbilical arm rotating -90° on its local X when deployed
function OrionUmbilicalAnimator({ umbilicalRef, deployed }) {
  const DELAY        = 0.8
  const timerRef     = useRef(0)
  const activeRef    = useRef(false)
  const returningRef = useRef(false)
  const prevDeployed = useRef(deployed)

  useFrame((_, delta) => {
    if (!umbilicalRef.current) return

    // Detect transitions and reset state immediately in the same frame
    if (deployed !== prevDeployed.current) {
      if (deployed) {
        timerRef.current    = 0
        activeRef.current   = false
        returningRef.current = false
      } else {
        returningRef.current = true
      }
      prevDeployed.current = deployed
    }

    if (deployed) {
      if (!activeRef.current) {
        timerRef.current += delta
        if (timerRef.current < DELAY) return
        activeRef.current = true
      }
      umbilicalRef.current.quaternion.slerp(_umbilicalDeployQuat, 1 - Math.pow(0.05, delta))
      if (umbilicalRef.current.quaternion.angleTo(_umbilicalDeployQuat) < 0.001) {
        umbilicalRef.current.quaternion.copy(_umbilicalDeployQuat)
      }
      return
    }

    if (!returningRef.current) return
    umbilicalRef.current.quaternion.slerp(_umbilicalRestQuat, 1 - Math.pow(0.05, delta))
    if (umbilicalRef.current.quaternion.angleTo(_umbilicalRestQuat) < 0.001) {
      // Snap exactly onto the rest quat — the slerp is asymptotic and previously
      // stopped here with a residual ~0.06° that every cycle inherited.
      umbilicalRef.current.quaternion.copy(_umbilicalRestQuat)
      returningRef.current = false
    }
  })
  return null
}


// SpaceEnvironment: always mounted so the texture is preloaded at startup and
// location transitions never trigger a Suspense boundary.
//
// PMREMGenerator warm-up: Three.js compiles the IBL cube-map shader the first
// time environmentIntensity goes above 0. To avoid that GPU stall on the first
// location transition we keep a near-zero base intensity (0.0001) at all times —
// visually identical to off but keeps the shader live. When a location with
// hdri:true is focused the intensity rises to ENV_ACTIVE_VALUE.
const SPACE_ENV_PATH    = '/hdri/earth.webp'
const ENV_ACTIVE_VALUE  = 0.15
const ENV_WARMUP_VALUE  = 0.05
useTexture.preload(SPACE_ENV_PATH)

function SpaceEnvironment({ active = false, intensity = ENV_ACTIVE_VALUE }) {
  const texture      = useTexture(SPACE_ENV_PATH)
  const { scene }    = useThree()
  texture.mapping    = EquirectangularReflectionMapping
  const activeIntensity = active ? intensity : ENV_WARMUP_VALUE

  useFrame(() => {
    scene.environmentIntensity = activeIntensity
  })

  return <Environment map={texture} background={false} environmentIntensity={activeIntensity} />
}

function MoonOrbit({
  earthPosition, 
  orbitRadius, 
  orbitSpeed, 
  onPositionUpdate,
  isOrbiting = false,
  orbitRotation =.75, // Rotation offset in radians to control starting position
  children 
}) {
  const angleRef = useRef(orbitRotation)
  const lastUpdateTimeRef = useRef(0)
  const UPDATE_THROTTLE = 100 // Only update parent state every 100ms
  
  // Reusable position array to prevent recreation
  const positionRef = useRef([
    earthPosition[0] + Math.cos(orbitRotation) * orbitRadius,
    earthPosition[1],
    earthPosition[2] + Math.sin(orbitRotation) * orbitRadius
  ])

  useFrame((state, delta) => {
    if (!isOrbiting) return
    
    // Update angle continuously but don't trigger state update
    angleRef.current += orbitSpeed * delta
    
    // Throttle parent state updates to reduce re-renders
    const now = state.clock.getElapsedTime() * 1000
    if (now - lastUpdateTimeRef.current > UPDATE_THROTTLE) {
      lastUpdateTimeRef.current = now
      const angle = -angleRef.current + orbitRotation
      // Reuse position array instead of creating new one
      positionRef.current[0] = earthPosition[0] + Math.cos(angle) * orbitRadius
      positionRef.current[1] = earthPosition[1]
      positionRef.current[2] = earthPosition[2] + Math.sin(angle) * orbitRadius
      onPositionUpdate(positionRef.current)
    }
  })

  // Calculate Moon's orbital position for rendering (static — isOrbiting is always false)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const moonPosition = useMemo(() => [
    earthPosition[0] + Math.cos(orbitRotation) * orbitRadius,
    earthPosition[1],
    earthPosition[2] + Math.sin(orbitRotation) * orbitRadius
  ], []) // empty — values never change, computed once at mount
  
  return <>{children(moonPosition)}</>
}


function SceneContent() {
  const { isArtemisModeActive } = useArtemisMode()

  // 4K detection for QR code sizing
  const [is4K, setIs4K] = useState(() => window.matchMedia('(min-width: 2560px)').matches)
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 2560px)')
    const handler = (e) => setIs4K(e.matches)
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [])

  // True-4K detection (>= 3840) — Mission Complete QR scales up at the 4K tier only,
  // matching the panel's exact-px resize (1440p stays on the 260px size).
  const [isTrue4K, setIsTrue4K] = useState(() => window.matchMedia('(min-width: 3840px)').matches)
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 3840px)')
    const handler = (e) => setIsTrue4K(e.matches)
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [])
  
  // Timeout tracking for cleanup on unmount
  const timeoutsRef = useRef([])
  
  // Helper to create tracked timeouts that auto-cleanup
  const createTimeout = useCallback((callback, delay) => {
    const timeoutId = setTimeout(() => {
      callback()
      // Remove from tracking array after execution
      timeoutsRef.current = timeoutsRef.current.filter(id => id !== timeoutId)
    }, delay)
    timeoutsRef.current.push(timeoutId)
    return timeoutId
  }, [])
  
  // Cleanup all timeouts on unmount
  useEffect(() => {
    return () => {
      timeoutsRef.current.forEach(timeoutId => clearTimeout(timeoutId))
      timeoutsRef.current = []
    }
  }, [])

  // Re-render and re-derive locationsData memos when locations.json hot-reloads
  const [locationsVersion, setLocationsVersion] = useState(0)
  useEffect(() => {
    const onHotUpdate = () => setLocationsVersion(v => v + 1)
    locationsHotSubscribers.add(onHotUpdate)
    return () => locationsHotSubscribers.delete(onHotUpdate)
  }, [])

  // Memoize Gateway position/scale so .find() doesn't create new references every render
  // (re-derived on locationsVersion bump when locations.json hot-reloads)
  const gatewaySpacestation = useMemo(
    () => locationsData.locations.find(loc => loc.id === 'spacestation'),
    [locationsVersion]
  )
  const launchPlatform = useMemo(
    () => locationsData.locations.find(loc => loc.content?.type === 'launchplatform'),
    [locationsVersion]
  )
  // Ordered list of Earth surface launch platform locations that can be cycled through via the panel arrow.
  // Order is determined by their position in locations.json. To add/remove/reorder, edit that file only.
  const earthLocations = useMemo(
    () => locationsData.locations.filter(loc => loc.content?.type === 'launchplatform'),
    [locationsVersion]
  )
  const lunarOpsLocation = useMemo(
    () => locationsData.locations.find(loc => loc.id === 'lunarops'),
    [locationsVersion]
  )
  const satelliteLocation = useMemo(
    () => locationsData.locations.find(loc => loc.id === 'satellite'),
    [locationsVersion]
  )
  const slsLocation = useMemo(
    () => locationsData.locations.find(loc => loc.id === 'sls'),
    [locationsVersion]
  )
  const orionLocation = useMemo(
    () => locationsData.locations.find(loc => loc.id === 'orion'),
    [locationsVersion]
  )
  const satellitePosition = useMemo(
    () => satelliteLocation?.worldPosition || [-1.06, -0.92, 0.53],
    [satelliteLocation]
  )
  const slsPosition = useMemo(
    () => slsLocation?.worldPosition || [-1.06, -0.52, 0.53],
    [slsLocation]
  )
  const gatewayPosition = useMemo(
    () => gatewaySpacestation?.content?.modelPosition || [3.5, 0, 3.5],
    [gatewaySpacestation]
  )
  const gatewayScale = gatewaySpacestation?.content?.modelScale ?? 1
  const gatewayRotation = useMemo(
    () => gatewaySpacestation?.content?.modelRotation || [0, 0, 0],
    [gatewaySpacestation]
  )
  const satelliteRotation = useMemo(
    () => satelliteLocation?.content?.modelRotation || [0, 0, 0],
    [satelliteLocation]
  )
  const satelliteModels = useMemo(() => satelliteLocation?.models ?? [], [satelliteLocation])

  // Control points for Artemis trajectory (extracted from TrajectoryEditor)
  const artemisControlPoints = useMemo(() => [
    {
      name: 'Point 1',
      pos: [-0.5990537004352219, 0.7855195297516792, -0.015056705748236254],
      handleIn: [0.17231374545989778, 0.020685094624550964, -0.7698241135063516],
      handleOut: [-0.1366204869874188, -0.016400361407295172, 0.6103619012005782],
      locked: true
    },
    {
      name: 'Point 2',
      pos: [0.19920606923859396, 0.4641273684325985, 0.9631936230747897],
      handleIn: [-0.5400209792508709, 0.16869982411821652, 0.03130916282808059],
      handleOut: [0.5005732592972175, -0.15637655581245974, -0.02902207559502757],
      locked: false
    },
    {
      name: 'Point 3',
      pos: [1.1360250575041544, 0.14628628755888196, 0],
      handleIn: [-0.006225333587299708, 0.06953680113103006, 0.5573671007202332],
      handleOut: [0.007621412455356378, -0.08513096283335884, -0.6823609536846734],
      locked: false
    },
    {
      name: 'Point 4',
      pos: [-0.045605215967133295, 0, -1.1218609913715833],
      handleIn: [0.5490623052680781, 0.001104998585977096, 0],
      handleOut: [-0.5801297251004675, -0.0011675223736335547, 0],
      locked: true
    },
    {
      name: 'Point 5',
      pos: [-1.173700828296186, 0, 0.010998775134302474],
      handleIn: [0.019267985273808907, -0.009710730234103515, -0.6726912436012178],
      handleOut: [-0.01906009558740589, 0.00960595743952098, 0.6654333196568599],
      locked: true
    },
    {
      name: 'Point 6',
      pos: [1.3170889472460063, 0, 1.2632736533722495],
      handleIn: [-1.9990330967394867, 0.02952984854042162, 0.19531135426002022],
      handleOut: [2.924277012049639, -0.043197612584258326, -0.285710378875879],
      locked: true
    },
    {
      name: 'Point 7',
      pos: [1.2789711483813635, 0, -1.356448160914335],
      handleIn: [2.781139651229224, -0.0015982770829863918, 0.14831933456843155],
      handleOut: [-1.8466222815784155, 0.0010612246933642266, -0.0984811344809291],
      locked: true
    },
    {
      name: 'Point 8',
      pos: [-1.2408941292727858, 0, 0.01621809208281144],
      handleIn: [-0.04653962240511139, -0.00827200656078133, -0.8925306548351956],
      handleOut: [0.04062002289606337, 0.007219850066046529, 0.7790053670668001],
      locked: true
    },
    {
      name: 'Point 9',
      pos: [1.7492852394532346, 0, 2.4679249697712318],
      handleIn: [-2.710614835969686, 0, -0.723598180434647],
      handleOut: [1.3467833278764352, 0, 0.35952358577808863],
      locked: true
    },
    {
      name: 'Point 10',
      pos: [4.701569373495156, 0, 3.3659997767833763],
      handleIn: [-0.7723560282918651, 0, -0.6905871785094391],
      handleOut: [0.8322553791688849, 0, 0.7441450225625139],
      locked: true
    },
    {
      name: 'Point 11',
      pos: [3.676694238331191, 0.030746050111627038, 4.450018793131993],
      handleIn: [0.7713003064512374, -0.038065752961593424, 0.7443337562510185],
      handleOut: [-0.6944774711929971, 0.034274338587334724, -0.6701968357087287],
      locked: true
    },
    {
      name: 'Point 12',
      pos: [2.479538541538331, 0.2603853518843079, 1.443347661397742],
      handleIn: [0.2, -0.05617233715136877, 0.7233801520565262],
      handleOut: [-0.21042157031776762, 0.05909935695905046, -0.7610739376621987],
      locked: true
    },
    {
      name: 'Point 13',
      pos: [0.9342015624483669, 0.5980233353682007, -0.8726750112293497],
      handleIn: [0.7835577501344905, -0.07178613322595573, 0.3090824118679104],
      handleOut: [-0.5289469402699052, 0.048459804675736635, -0.20864855975288688],
      locked: true
    },
    {
      name: 'Point 14',
      pos: [-0.5077051140903899, 0.692249269791709, -0.47785596292875454],
      handleIn: [0.21421220640607774, 0, -0.4500981099257835],
      handleOut: [-0.2, 0, 0.420235725570691],
      locked: true
    }
  ], [])
  
  // Check for dev mode from URL query parameter
  const isDevMode = new URLSearchParams(window.location.search).get('dev') === 'true'

  // Kiosk mode from URL query parameter (?kiosk=true). The Festival of Speed
  // conference link sets this true; the public build defaults to false. Surfaced in
  // the app_loaded dataLayer so GTM can segment FoS traffic from public traffic.
  const isKioskMode = new URLSearchParams(window.location.search).get('kiosk') === 'true'

  // Detect device and orientation — tracked as state so orientation changes
  // (portrait ↔ landscape) trigger a re-render and update UI labels/layout
  const [deviceInfo, setDeviceInfo] = useState(() => getDeviceInfo())
  const isMobile = deviceInfo.isMobile
  
  // Scrollbar refs
  const starcadeScrollRef = useRef(null)
  const portfolioScrollRef = useRef(null)
  const windowScrollRef = useRef(null)
  
  // Get media query key for camera config — tracked as state so orientation
  // changes (portrait ↔ landscape) trigger a re-render and update the camera config
  const [mediaQueryKey, setMediaQueryKey] = useState(() => getMediaQueryKey())
  // When mediaQueryKey changes due to orientation, snap the camera instantly (no animation)
  const snapCameraRef = useRef(false)
  useEffect(() => {
    const update = () => {
      const newKey = getMediaQueryKey()
      setMediaQueryKey(prev => {
        if (prev !== newKey) snapCameraRef.current = true
        return newKey
      })
      setDeviceInfo(getDeviceInfo())
    }
    const handleResize = () => update()
    // iOS fires orientationchange before dimensions update — delay to get correct values
    const handleOrientationChange = () => setTimeout(update, 100)
    window.addEventListener('resize', handleResize)
    window.addEventListener('orientationchange', handleOrientationChange)
    return () => {
      window.removeEventListener('resize', handleResize)
      window.removeEventListener('orientationchange', handleOrientationChange)
    }
  }, [])
  
  // Track Earth's rotation for syncing hotspot markers (ref avoids React state lag)
  const earthRotationRef = useRef(0)
  // Stable callback — inline arrow would be recreated every render and could
  // cause Earth to miss one rotation update on unrelated SceneContent re-renders
  const handleRotationUpdate = useCallback((r) => { earthRotationRef.current = r }, [])

  // Ref to Earth's inner spinning group — used by EarthSurfaceMarker to read
  // Earth's world matrix directly (includes tilt + rotation), eliminating desync.
  const earthInnerGroupRef = useRef(null)

  // Moon orbital configuration
  const earthPosition = [0, 0, 0]
  const moonOrbitRadius = 6
  const earthRotationSpeed = 0.05

  // Occluder spheres (Earth + Moon) used to hide mission-stage markers that sit
  // behind a planetoid relative to the camera. Mirrors MoonOrbit's static
  // position math (orbitRotation 0.75) and the Moon's render radius (0.183).
  // Radii are padded slightly so markers grazing the limb hide cleanly.
  const missionMarkerOccluders = useMemo(() => {
    const moonAngle = 0.75
    return [
      { center: earthPosition, radius: 1.02 },
      {
        center: [
          earthPosition[0] + Math.cos(moonAngle) * moonOrbitRadius,
          earthPosition[1],
          earthPosition[2] + Math.sin(moonAngle) * moonOrbitRadius,
        ],
        radius: 0.2,
      },
    ]
  }, [])

  // Base offset for all Earth surface coordinates (lat/long adjustments)
  const earthCoordinateOffset = {
    latitude: 0.7,   // Offset in degrees (-90 to 90)
    longitude: 10.5   // Offset in degrees (-180 to 180)
  }
  
  // Sun — ref-based imperative position, no state, no re-renders
  const sunDistance = 250
  const sunRotationSpeed = earthRotationSpeed
  // Sun angle (radians) the sun tweens to when entering Artemis mode.
  // 0 = right of Earth (+X), π/2 = behind (+Z), π = left (−X), 3π/2 = front (−Z)
  const ARTEMIS_SUN_START_ROTATION = Math.PI * 1.15
  
  // LEVA controls for Moon marker positioning
  const { lunarOpsLat, lunarOpsLon } = useControls('Moon Marker', {
    // Hidden — only the "Mobile Portrait — SLS/Orion Pos" panel is shown for now.
    lunarOpsLat: { value: -39, min: -90, max: 90, step: 0.1, render: () => false },
    lunarOpsLon: { value: 145, min: -180, max: 180, step: 0.1, render: () => false }
  })

  // TEMP dev controls (mobilePortrait only) — dial in a scene-position offset for
  // the SLS / Orion Artemis models. The chosen offset is added to the model's
  // single scene-graph position, so it follows the model everywhere it appears on
  // mobile portrait (Scene 1 AND the focused view — both reuse the same group).
  // Once values are locked, bake them into MP_SLS_OFFSET / MP_ORION_OFFSET below
  // and remove these controls.
  const { mpSlsOffset, mpOrionOffset } = useControls('Mobile Portrait — SLS/Orion Pos', {
    mpSlsOffset:   { value: [0, 0, 0], step: 0.05, render: () => isDevMode && mediaQueryKey === 'mobilePortrait' },
    mpOrionOffset: { value: [0, 0, 0], step: 0.05, render: () => isDevMode && mediaQueryKey === 'mobilePortrait' },
  })

  // LEVA controls for the Orion focused-module three-point lighting rig. Light
  // geometry (positions/cone shape) is shared across sections; the three light
  // intensities are per-section (OCM = integrity, ESM, ICPS) since each module
  // wants a different look. The component picks the active section's trio.
  const orionLightCtl = useControls('Orion Focus Light', {
    helper: { value: isDevMode, render: () => false },
    keyPos: { value: [0.25, 0.3, 0.35], step: 0.02, render: () => false },
    keyAngle: { value: 0.9, min: 0.1, max: 1.5, step: 0.05, render: () => false },
    keyPenumbra: { value: 0.6, min: 0, max: 1, step: 0.05, render: () => false },
    fillPos: { value: [-0.35, 0.12, 0.25], step: 0.02, render: () => false },
    fillAngle: { value: 1.0, min: 0.1, max: 1.5, step: 0.05, render: () => false },
    fillPenumbra: { value: 0.7, min: 0, max: 1, step: 0.05, render: () => false },
    rimPos: { value: [-0.12, 0.25, -0.4], step: 0.02, render: () => false },
    rimAngle: { value: 0.8, min: 0.1, max: 1.5, step: 0.05, render: () => false },
    rimPenumbra: { value: 0.5, min: 0, max: 1, step: 0.05, render: () => false },
    // Per-section intensities (OCM = the `integrity` section abbreviation)
    ocmKey: { value: 1, min: 0, max: 200, step: 0.1, render: () => false },
    ocmFill: { value: 1, min: 0, max: 200, step: 0.1, render: () => false },
    ocmRim: { value: 1, min: 0, max: 200, step: 0.1, render: () => false },
    esmKey: { value: 1, min: 0, max: 200, step: 0.1, render: () => false },
    esmFill: { value: 0, min: 0, max: 200, step: 0.1, render: () => false },
    esmRim: { value: 1, min: 0, max: 200, step: 0.1, render: () => false },
    icpsKey: { value: 0, min: 0, max: 200, step: 0.1, render: () => false },
    icpsFill: { value: 0, min: 0, max: 200, step: 0.1, render: () => false },
    icpsRim: { value: 1, min: 0, max: 200, step: 0.1, render: () => false },
  }, { collapsed: true })
  
  const starfieldRotationSpeed = earthRotationSpeed * 1
  const sunPositionRef = useRef([sunDistance * 0.2, 0, 0]) // initial: angle=0

  // Track preload and scene ready state
  const [preloadProgress, setPreloadProgress] = useState(0)
  const [preloadComplete, setPreloadComplete] = useState(false)
  const [sceneReady, setSceneReady] = useState(false)
  const [loadingMessage, setLoadingMessage] = useState('LOADING TEXTURES')

  const handlePreloadProgress = useCallback((progress, complete, currentPhase) => {
    setPreloadProgress(progress)
    setPreloadComplete(complete)
    if (currentPhase) {
      setLoadingMessage(currentPhase)
    }
  }, [])
  
  // Fire a single on-first-load analytics event the moment the experience is fully
  // ready (preload complete + all scene components ready → loader dismissed). Guarded
  // so it can never double-fire if the WebGPU canvas remounts (e.g. WebGL2 fallback).
  const appLoadedRef = useRef(false)
  const handleSceneReady = useCallback(() => {
    setSceneReady(true)
    if (!appLoadedRef.current) {
      appLoadedRef.current = true
      trackEvent('app_loaded', {
        load_time_ms: Math.round(performance.now()),
        screen_dimension: `${window.innerWidth}x${window.innerHeight}`,
        kiosk: isKioskMode,
      })
    }
  }, [isKioskMode])
  
  
  const nav = useNavigation()
  const [currentSection, setCurrentSection] = useState(isArtemisModeActive ? nav.sections.ARTEMIS : nav.sections.MAIN)
  const [isTransitioning, setIsTransitioning] = useState(false)
  const [currentMode, setCurrentMode] = useState(isArtemisModeActive ? nav.modes.ARTEMIS : nav.modes.MAIN)
  const [showJoinMission, setShowJoinMission] = useState(false)
  const [showCrewProfile, setShowCrewProfile] = useState(false)
  // Pause the WebGPU render loop while the Crew Profile Screen (2D overlay) is
  // open — the 3D scene is fully obscured, so rendering it wastes GPU/battery.
  useEffect(() => {
    useIframeStore.getState().setCrewProfileOpen(showCrewProfile)
  }, [showCrewProfile])
  // DSL_Window (join mission) is a DOM sibling of .crew-profile-container, not a
  // descendant — its z-index alone can't be scoped above the crew screen's very
  // high stack (16777280+) via a descendant selector. This body class lets
  // style.css lift it above crew specifically when both are open at once
  // (pressing JOIN US from inside the crew screen).
  useEffect(() => {
    document.body.classList.toggle('join-mission-open', showJoinMission)
    return () => document.body.classList.remove('join-mission-open')
  }, [showJoinMission])
  const [orionDeployed, setOrionDeployed] = useState(false)
  const [orionReverseComplete, setOrionReverseComplete] = useState(true)
  const [orionHotspotsReady, setOrionHotspotsReady] = useState(false)
  const [orionDetailsVisible, setOrionDetailsVisible] = useState(false)
  const [orionSnapComplete, setOrionSnapComplete] = useState(false)
  const [orionFocusedSection, setOrionFocusedSection] = useState(null)
  // Level 3 — a specific attachment point within the focused section is selected.
  // Holds the hotspot id (e.g. 'esm-engine') or null. Reuses the section's spin
  // controller but locks it to a target rotation with its own camera lookAt.
  const [orionFocusedAttachment, setOrionFocusedAttachment] = useState(null)
  const orionAttachmentLookAtRef = useRef(null)
  // Leaving a section (level 2) also exits level 3
  useEffect(() => { if (!orionFocusedSection) setOrionFocusedAttachment(null) }, [orionFocusedSection])
  // The section/attachment controllers re-emit their flip state on focus change
  // (flippedRef invalidated), so no manual reset is needed here — doing so would
  // fight the controller's authoritative value when returning to the deployed view.
  useEffect(() => {
    if (!orionDeployed) { setOrionHotspotsReady(false); setOrionSnapComplete(false); setOrionFocusedSection(null); setOrionFocusedAttachment(null); setOrionSectionLabelsFlipped(false); setOrionAttachmentLabelsFlipped(false); return }
    // Mobile portrait has no deploy camera transition to wait for, so start the
    // separation animation immediately (snap delay 0) and bring the hotspots in
    // after the animation rather than after the (absent) camera move.
    const snapDelay = mediaQueryKey === 'mobilePortrait' ? 0 : 1200
    const hotspotsDelay = mediaQueryKey === 'mobilePortrait' ? 1000 : 2200
    const snapId = setTimeout(() => setOrionSnapComplete(true), snapDelay)
    const hotspotsId = setTimeout(() => setOrionHotspotsReady(true), hotspotsDelay)
    return () => { clearTimeout(snapId); clearTimeout(hotspotsId) }
  }, [orionDeployed, mediaQueryKey])
  const [isClosingWindow, setIsClosingWindow] = useState(false)
  const [showMissionComplete, setShowMissionComplete] = useState(false)
  const [isClosingMissionComplete, setIsClosingMissionComplete] = useState(false)
  const [isClosingStarcade, setIsClosingStarcade] = useState(false)
  const [isClosingArtemis, setIsClosingArtemis] = useState(false)
  const [earthAtArtemisTarget, setEarthAtArtemisTarget] = useState(false)
  const [showTrajectory, setShowTrajectory] = useState(false)
  const [artemisProgress, setArtemisProgress] = useState(0)
  const [artemisComplete, setArtemisComplete] = useState(false)
  const [trajectoryDrawComplete, setTrajectoryDrawComplete] = useState(false)
  const [artemisMissionLaunched, setArtemisMissionLaunched] = useState(false)
  
  // Sun target rotation for Artemis mode entry tween (null = no override)
  const [artemisSunTarget, setArtemisSunTarget] = useState(null)

  const [artemisPaused, setArtemisPaused] = useState(false)
  const [artemisStage, setArtemisStage] = useState(1)
  const [artemisGuidedMode, setArtemisGuidedMode] = useState(true)
  // Ref mirror so the ArtemisOnPath per-frame stage-crossing callback
  // (onMarkerReached) always reads the CURRENT guided setting, not a value
  // captured in a stale closure — the cause of the auto-lightbox firing at a stage
  // even after guided was toggled off.
  const artemisGuidedModeRef = useRef(artemisGuidedMode)
  artemisGuidedModeRef.current = artemisGuidedMode
  const [artemisLaunchChosen, setArtemisLaunchChosen] = useState(false)

  // First-time (per device) explainer for GUIDED vs UNGUIDED mode, shown once
  // before the user launches the mission on mobile portrait. Persisted in
  // localStorage so a returning visitor never sees it again. Lazy-read so we
  // touch localStorage only at mount (guarded for private-mode throws).
  const [modeIntroSeen, setModeIntroSeen] = useState(() => {
    try {
      return localStorage.getItem('dsl-artemis-mode-intro-seen') === 'true'
    } catch {
      return false
    }
  })
  const dismissModeIntro = useCallback(() => {
    playSound('DataPoint_Close')
    setModeIntroSeen(true)
    try {
      localStorage.setItem('dsl-artemis-mode-intro-seen', 'true')
    } catch {
      // localStorage unavailable (private mode / disabled) — the in-memory
      // flag still hides it for the rest of this session.
    }
  }, [playSound])

  const [missionStagesVisible, setMissionStagesVisible] = useState(true)

  // Stage/sub-point markers the user has clicked on in the pre-launch Mission
  // Profile view (Scene 2) — their idle fill switches from black to a 50%
  // tint of their own color once visited, so progress through the points is
  // visible at a glance. Resets when leaving Mission Profile.
  const [visitedStageIds, setVisitedStageIds] = useState(() => new Set())
  useEffect(() => {
    if (artemisStage < 2) setVisitedStageIds(new Set())
  }, [artemisStage])

  const pauseAtPercents = useMemo(() => {
    if (!missionStagesVisible) return []
    const percents = []
    missionStagesConfig.missionStages.filter(s => s.visible).forEach(s => {
      if (s.trajectoryPercent > 0) percents.push(s.trajectoryPercent)
      s.subPoints?.filter(p => p.visible).forEach(p => {
        if (p.trajectoryPercent > 0) percents.push(p.trajectoryPercent)
      })
    })
    return percents.sort((a, b) => a - b)
  }, [missionStagesVisible])

  // Stable props for ArtemisSeekBar (React.memo) — avoid rebuilding arrays/objects every render
  const seekBarMissionStages = useMemo(() => (
    missionStagesVisible ? missionStagesConfig.missionStages : []
  ), [missionStagesVisible])

  const seekBarExtraLayers = useMemo(() => (
    missionStagesVisible ? [{
      id: 'subpoints',
      color: '#60A5FA',
      visible: true,
      markers: missionStagesConfig.missionStages.flatMap(s =>
        (s.subPoints ?? []).filter(p => p.visible).map(p => ({
          id: p.id,
          trajectoryPercent: p.trajectoryPercent,
          visible: true,
        }))
      ),
    }] : []
  ), [missionStagesVisible])

  // "Focus areas" — stage 11 (Lunar Flyby) and sub-point 8.5 (Earth photos) take
  // turns drawing extra attention on the mission profile: every 15s the focused
  // one swaps its dot marker for an image + caption callout. Only one is focused
  // at a time. Pre-launch only — once the mission launches, markers switch to
  // tracking real flight progress and shouldn't be interrupted by the callout.
  const FOCUS_STAGE_IDS = ['stage_11', 'stage_8_1']
  const FOCUS_INTERVAL_MS = 15000
  const [focusedStageIndex, setFocusedStageIndex] = useState(0)
  const missionMarkersOnScreen = trajectoryDrawComplete && !showJoinMission && missionStagesVisible && !artemisMissionLaunched
  useEffect(() => {
    if (!missionMarkersOnScreen) return
    const id = setInterval(() => {
      setFocusedStageIndex((i) => (i + 1) % FOCUS_STAGE_IDS.length)
    }, FOCUS_INTERVAL_MS)
    return () => clearInterval(id)
  }, [missionMarkersOnScreen])
  const focusedStageId = missionMarkersOnScreen ? FOCUS_STAGE_IDS[focusedStageIndex] : null

  const [activeLightboxMedia, setActiveLightboxMedia] = useState(null)
  const [activeLightboxLayerLabel, setActiveLightboxLayerLabel] = useState(null)

  // Track Artemis position for mobile portrait launch camera
  // artemisPosition as a ref — updated every frame by ArtemisOnPath, never triggers re-render
  const artemisPositionRef = useRef([0, 0, 0])
  const artemisSeekRef = useRef(null)
  // Raw animation t (0–1) written every frame by ArtemisOnPath — drives sun smoothly
  const artemisTRef = useRef(null)
  const tooltipTimerRef = useRef(null)
  
  // Follow mode state (desktop/tablet toggle)
  const [followModeActive, setFollowModeActive] = useState(false)

  // Focused location state — set to a location data object when a location hotspot is clicked, null otherwise
  const [focusedLocation, setFocusedLocation] = useState(null)
  const [focusedSatelliteIndex, setFocusedSatelliteIndex] = useState(0)
  // Marks that a location (SLS / Orion / ISS / …) is focused. On mobile portrait
  // the OPS panel is hidden in this state, so style.css drops the NAV window to
  // the OPS height (parity with crew / JOIN).
  useEffect(() => {
    document.body.classList.toggle('location-focused', !!focusedLocation)
    return () => document.body.classList.remove('location-focused')
  }, [focusedLocation])
  // Marks the Artemis mission profile (SIMULATE, stage >= 2). On mobile portrait
  // the OPS panel is hidden here too, so the NAV window drops to the OPS height
  // (parity with focused locations / crew / JOIN).
  useEffect(() => {
    const inMissionProfile = currentMode === nav.modes.ARTEMIS && artemisStage >= 2
    document.body.classList.toggle('mission-profile-active', inMissionProfile)
    return () => document.body.classList.remove('mission-profile-active')
  }, [currentMode, artemisStage, nav.modes.ARTEMIS])
  // SLS/Orion render at 2x their authored scale at ALL times so they fill ~2x
  // the frame in Scene 1. The focus view compensates by zooming the camera out
  // (wider fov) so they frame the same as before when focused — see the fov
  // adjustments in locations.json (sls/orion camera + tablet/mobile variants).
  const slsScale = 0.006 * 2
  const orionScale = 0.01 * 2
  // SLS sits lower + further left than its authored worldPosition, in BOTH the
  // Scene-1 and focused views (so it never flicks position on focus). The focus
  // camera lookAt in locations.json is shifted by this same offset to keep the
  // focused framing correct. Camera looks down -X/-Z, so -X = screen-left.
  const SLS_SCENE_OFFSET = [-0.4, -0.4, 0.2]
  // Mobile-portrait-only position offsets for the SLS / Orion Artemis models.
  // Added to each model's single scene-graph position, so they shift in BOTH
  // Scene 1 and the focused view (both reuse the same group). Dial these in via
  // the "Mobile Portrait — SLS/Orion Pos" Leva panel, then bake the values here.
  const MP_SLS_OFFSET   = [-0.55, 0, -1.6]
  const MP_ORION_OFFSET = [-0.15, -0.25, 0]
  // Live offset = baked constant + (dev Leva override, mobile portrait only).
  const mpSlsOff = useMemo(
    () => (mediaQueryKey === 'mobilePortrait'
      ? [MP_SLS_OFFSET[0] + mpSlsOffset[0], MP_SLS_OFFSET[1] + mpSlsOffset[1], MP_SLS_OFFSET[2] + mpSlsOffset[2]]
      : [0, 0, 0]),
    [mediaQueryKey, mpSlsOffset]
  )
  const mpOrionOff = useMemo(
    () => (mediaQueryKey === 'mobilePortrait'
      ? [MP_ORION_OFFSET[0] + mpOrionOffset[0], MP_ORION_OFFSET[1] + mpOrionOffset[1], MP_ORION_OFFSET[2] + mpOrionOffset[2]]
      : [0, 0, 0]),
    [mediaQueryKey, mpOrionOffset]
  )
  const slsScenePosition = useMemo(
    () => [
      slsPosition[0] + SLS_SCENE_OFFSET[0] + mpSlsOff[0],
      slsPosition[1] + SLS_SCENE_OFFSET[1] + mpSlsOff[1],
      slsPosition[2] + SLS_SCENE_OFFSET[2] + mpSlsOff[2],
    ],
    [slsPosition, mpSlsOff]
  )
  // Orion's outer group base position (was the hardcoded [3.5, 0.2, 3.5] literal),
  // now carrying the mobile-portrait offset so it moves in Scene 1 + focused view.
  const ORION_SCENE_BASE = [3.5, 0.2, 3.5]
  const orionScenePosition = useMemo(
    () => [
      ORION_SCENE_BASE[0] + mpOrionOff[0],
      ORION_SCENE_BASE[1] + mpOrionOff[1],
      ORION_SCENE_BASE[2] + mpOrionOff[2],
    ],
    [mpOrionOff]
  )
  const [activeSatHotspot, setActiveSatHotspot] = useState(null)
  const [gatewayHotspotActive, setGatewayHotspotActive] = useState(false)
  const [munPanelActive, setMunPanelActive] = useState(false)
  const [meridianPanelActive, setMeridianPanelActive] = useState(false)
  const locationPanelScrollRef = useRef(null)
  const [locationPanelScrolled, setLocationPanelScrolled] = useState(false)
  const [locationPanelHasOverflow, setLocationPanelHasOverflow] = useState(false)
  // Mobile portrait: location panel text box starts collapsed, toggle reveals it
  const [locationPanelCollapsed, setLocationPanelCollapsed] = useState(true)
  const locationModelRef = useRef()
  const satelliteModelRef = useRef()
  const irusModelRef = useRef()
  const prosperoModelRef = useRef()
  const slsModelRef = useRef()
  const slsSnapToRestRef = useRef(false)
  const [slsHotspotsFlipped, setSlsHotspotsFlipped] = useState(false)
  // SLS attachment focus (the interactive touch points). Mirrors Orion's
  // orionFocusedAttachment: the focused hotspot id, plus a ref holding the live
  // camera look-at (the attachment's world position, the "origin location the
  // button points to") that SlsAttachmentLookAtTracker keeps updated each frame.
  const [slsFocusedHotspot, setSlsFocusedHotspot] = useState(null)
  const slsAttachmentLookAtRef = useRef(null)
  // Clear any focused SLS touch point when we leave the SLS location, so the
  // camera/panel don't linger into another location.
  useEffect(() => {
    if (focusedLocation?.id !== 'sls') {
      slsAttachmentLookAtRef.current = null
      setSlsFocusedHotspot(null)
    }
  }, [focusedLocation])
  // Orion label flips — section labels track the outer (level-1) spin; attachment
  // labels track the focused section's own (level-2) spin.
  const [orionSectionLabelsFlipped, setOrionSectionLabelsFlipped] = useState(false)
  const [orionAttachmentLabelsFlipped, setOrionAttachmentLabelsFlipped] = useState(false)
  const slsAttachmentRefs = {
    RS25Engine:          useRef(),
    LiquidHydrogen:      useRef(),
    CoreStage:           useRef(),
    SolidRocketBoosters: useRef(),
    LiquidOxygen:        useRef(),
    LVSA:                useRef(),
    ICPS:                useRef(),
    OrionMPCV:           useRef(),
    LaunchAbortSystem:   useRef(),
  }
  // Initial rest rotation for the SLS model. Desktop/tablet keeps the original
  // angled pose (matches the angle of the SLS in the launch location). Mobile
  // portrait stands the rocket more upright (points straighter up) — the Y term
  // (Math.PI/2 = straight up in this model's frame) is the "up-tilt" axis; nudge
  // it if it needs to be more/less vertical. Consumed by the SLS LocationSpinController
  // rest pose AND the attachment world-pos / lookAt trackers, so they stay in sync.
  const slsInitialRotation = useMemo(() => (
    mediaQueryKey === 'mobilePortrait'
      ? [Math.PI / 2, Math.PI / 1.0, -Math.PI] // mobile: more upright (Y past the 120° base)
      : [Math.PI / 2, Math.PI / 1.5, -Math.PI] // desktop/tablet: original angled pose
  ), [mediaQueryKey])
  const orionModelRef      = useRef()
  const orionOuterRef      = useRef()
  const orionUmbilicalRef  = useRef()
  const orionEsmCtrlRef    = useRef()
  const orionIntCtrlRef    = useRef()
  const orionIcspRef       = useRef()
  const orionSEsmRef       = useRef()
  const orionSIcpsRef          = useRef()
  const orionSIntegrityRef     = useRef()
  const orionSectionLookAtRef  = useRef(null)
  const orionSnapToRestRef     = useRef(false)
  const orionOuterSnapToRestRef = useRef(false)
  const orionAttachmentRefs = {
    ESMUmbilical:      useRef(),
    ESMAuxThrusters:   useRef(),
    ESMCrewAdapter:    useRef(),
    ESMAvionics:       useRef(),
    ESMMainEngine:     useRef(),
    ESMManThrusters:   useRef(),
    ESMSolarWings:     useRef(),
    ESMConsumables:    useRef(),
    ICPSAvionicsShelf:     useRef(),
    ICPSHeliumBottles:     useRef(),
    ICPSLH2Tank:           useRef(),
    ICPSLOXTank:           useRef(),
    ICPSOrionStageAdapter: useRef(),
    ICPSProximityTestTarget: useRef(),
    ICPSRL10Engine:        useRef(),
    ICPSSpacecraftAdapter: useRef(),
    IntHeatShield:     useRef(),
    IntDocking:        useRef(),
    IntLASAttach:      useRef(),
    IntWindows:        useRef(),
    IntPitchThrusters: useRef(),
    IntRollThrusters:  useRef(),
    IntUmbilical:      useRef(),
  }
  // Initial rest rotation for the Orion model. Desktop/tablet keeps the original
  // pose; mobile portrait stands it more upright. [x, y, z] radians — z rolls
  // around the forward axis; x is the pitch (up/down tilt) nudged here for mobile.
  // Consumed by the Orion model group rest rotation AND its LocationSpinController
  // rest pose, so both stay in sync.
  const orionInitialRotation = useMemo(() => (
    mediaQueryKey === 'mobilePortrait'
      ? [-0.2, 1.0, Math.PI]  // mobile: more upright (x pitched toward vertical)
      : [-0.5, 1.0, Math.PI]  // desktop/tablet: original pose
  ), [mediaQueryKey])
  const orionDetailsRotation = [Math.PI, Math.PI/2, -Math.PI/1.75]  // rotation used when details are shown — adjust to taste
  // Tracks the previously-active satellite index while a camera transition is in flight.
  // Both models stay visible until the transition ends, then the old one is hidden.
  const prevSatelliteIndexRef = useRef(null)

  const activeSatModel = useMemo(
    () => satelliteModels[focusedSatelliteIndex] ?? satelliteModels[0],
    [satelliteModels, focusedSatelliteIndex]
  )

  // Shadow target ref — points the directional light's shadow frustum at the focused model.
  // Updated whenever focusedLocation changes; read imperatively by Lighting each frame.
  const shadowTargetRef = useRef(null)
  useEffect(() => {
    if (!focusedLocation) {
      shadowTargetRef.current = null
      return
    }
    if (focusedLocation.id === 'sls') {
      // Track the SLS's ACTUAL scene position (it's offset from worldPosition by
      // SLS_SCENE_OFFSET), so the shadow look-at stays centred on the model.
      const base = slsScenePosition
      shadowTargetRef.current = [base[0], base[1] + 0.4, base[2]]
    } else if (focusedLocation.id === 'orion') {
      // Track Orion's ACTUAL scene position (base + mobile-portrait offset) so the
      // shadow look-at follows the model when it's shifted on mobile portrait.
      const base = orionScenePosition
      shadowTargetRef.current = [base[0], base[1] - 0.15, base[2]]
    } else if (focusedLocation.id === 'spacestation') {
      const pos = focusedLocation.content?.modelPosition || [3.5, 0, 3.5]
      shadowTargetRef.current = pos
    } else if (focusedLocation.id === 'satellite') {
      const base = focusedLocation.worldPosition || [-1.06, -0.92, 0.53]
      const offset = activeSatModel?.positionOffset || [0, 0, 0]
      shadowTargetRef.current = [base[0] + offset[0], base[1] + offset[1], base[2] + offset[2]]
    } else {
      shadowTargetRef.current = focusedLocation.worldPosition || null
    }
  }, [focusedLocation, activeSatModel, slsScenePosition, orionScenePosition])

  // Deferred shadow frustum — the frustum parameters change whenever focusedLocation
  // changes, but updating them in the same frame as HDRI / model visibility / camera
  // changes stacks too much GPU work onto a single frame.
  // We hold the "committed" values in state and only update them one macrotask later
  // (via setTimeout 0) so the frustum rebuild is pushed to a subsequent frame.
  const [deferredShadow, setDeferredShadow] = useState({
    frustumSize: 5,
    near: 40,
    far: 60,
  })
  useEffect(() => {
    const next = {
      frustumSize: focusedLocation?.shadow?.frustumSize ?? 5,
      near:        focusedLocation?.shadow?.near        ?? 40,
      far:         focusedLocation?.shadow?.far         ?? 60,
    }
    // Small delay to push the frustum update off the transition frame
    const id = setTimeout(() => setDeferredShadow(next), 0)
    return () => clearTimeout(id)
  }, [focusedLocation])

  // Deferred HDRI activation — satellite and spacestation both have hdri:true.
  // The PMREMGenerator IBL re-evaluation (first frame that environmentIntensity
  // leaves near-zero) is expensive. Deferring it by one macrotask staggers it
  // away from the camera config change, CRT glitch, and shadow-target update
  // that all land on the same frame as focusedLocation changing.
  const [deferredHdri, setDeferredHdri] = useState(0)
  useEffect(() => {
    if (focusedLocation?.id === 'sls') return
    if (!focusedLocation?.hdri) {
      const id = setTimeout(() => setDeferredHdri(0), 0)
      return () => clearTimeout(id)
    }
    // When in satellite location, each model can override the intensity
    const intensity = (focusedLocation.id === 'satellite' && activeSatModel?.hdriIntensity != null)
      ? activeSatModel.hdriIntensity
      : (focusedLocation.hdriIntensity ?? ENV_ACTIVE_VALUE)
    const id = setTimeout(() => setDeferredHdri(intensity), 0)
    return () => clearTimeout(id)
  }, [focusedLocation, activeSatModel])

  // Satellite visibility — immediate (not deferred).
  // deferredShadow resets one macrotask after focusedLocation changes. Hiding the
  // model in the same render as focusedLocation → null guarantees the model is gone
  // before the shadow frustum resets, so there is never a frame with shadow but no model.
  const satModelVisible = focusedLocation?.id === 'satellite'

  // GLB mount gates — broad "is this model ever needed in the current view?" predicates,
  // a superset of each model's per-frame `visible` prop. Used to defer GLB load out of the
  // boot spike (the satellites reuse `satModelVisible`). See MEMORY_LEAK_ACTION_PLAN.md #2.
  const gatewayMounted = useMemo(
    () => !isArtemisModeActive &&
      ((currentMode === nav.modes.MAIN && !focusedLocation) || focusedLocation?.id === 'spacestation'),
    [isArtemisModeActive, currentMode, focusedLocation, nav.modes.MAIN]
  )
  // Mount gate is intentionally broader than the `visible` prop (which is still
  // Stage 1 / focused only). On desktop/tablet, once Artemis mode is active these
  // stay MOUNTED for the whole mission, just invisible during Mission Profile
  // (Stage 2/3): gating mount on `artemisStage === 1` made both GLB scene graphs
  // unmount entering Mission Profile and re-mount synchronously on HOME — a heavy
  // single-commit cost that was the second half of the HOME freeze. Keeping them
  // resident makes the return a cheap `.visible` flip. On phones (VRAM-constrained)
  // we keep the original Stage-1-only mount so two static GLBs aren't held resident
  // through the launched flight — phone may still hitch slightly on HOME.
  const keepArtemisModelsResident = !deviceInfo.isMobile
  const slsMounted = useMemo(
    () => (isArtemisModeActive && (keepArtemisModelsResident ? artemisStage >= 1 : artemisStage === 1)) || focusedLocation?.id === 'sls',
    [isArtemisModeActive, artemisStage, focusedLocation, keepArtemisModelsResident]
  )
  const orionMounted = useMemo(
    () => (isArtemisModeActive && (keepArtemisModelsResident ? artemisStage >= 1 : artemisStage === 1)) || focusedLocation?.id === 'orion',
    [isArtemisModeActive, artemisStage, focusedLocation, keepArtemisModelsResident]
  )

  // Navigate to/from a location with the HUD glitch transition
  // targetScalesRef holds the desired scale for each satellite model.
  // SatelliteScaleAnimator (inside the canvas) lerps toward these each frame.
  const targetScalesRef = useRef([
    { ref: satelliteModelRef, target: 0 },
    { ref: irusModelRef,      target: 0 },
    { ref: prosperoModelRef,  target: 0 },
  ])

  // Scale all satellites to zero immediately; after the camera zoom finishes (600ms),
  // scale up only the currently-focused satellite to its configured scale.
  useEffect(() => {
    // Dismiss any open hotspot info panel whenever the satellite or location changes.
    setActiveSatHotspot(null)
    setGatewayHotspotActive(false)
    setMunPanelActive(false)
    setMeridianPanelActive(false)

    // Immediately collapse all models to zero.
    targetScalesRef.current.forEach(e => { e.target = 0 })

    if (focusedLocation?.id !== 'satellite') return

    const fullScales = [1, 1, 1]

    const id = setTimeout(() => {
      targetScalesRef.current.forEach((e, i) => {
        e.target = i === focusedSatelliteIndex ? fullScales[i] : 0
      })
    }, 600)

    return () => clearTimeout(id)
  }, [focusedLocation, focusedSatelliteIndex, satelliteModels])

  // Idle-preheat the deferred GLBs after the scene is ready — warms drei's cache before the
  // user navigates, but the decode happens AFTER the boot spike (the iOS OOM window), one model
  // per idle slot. Smallest first, Gateway (17MB) last. Works on iPhone (no hover needed).
  useEffect(() => {
    if (!sceneReady) return

    const paths = [
      '/models/Beta_Eater.glb',
      '/models/Satellite_Generic.glb',
      '/models/IRUS.glb',
      '/models/Artemis_Orion_Spinny.glb',
      '/models/Black_Arrow.glb',
      '/models/Artemis_SLS_Spinny.glb',
      '/models/Gateway_Lunar_SpaceStation_v2.glb',
    ]

    const gapMs = deviceInfo.isPhone ? 1200 : 250
    const ric = window.requestIdleCallback || ((cb) => setTimeout(() => cb(), gapMs))
    const cancelRic = window.cancelIdleCallback || clearTimeout

    let i = 0
    let cancelled = false
    let idleHandle = null
    let timerHandle = null

    const step = () => {
      if (cancelled || i >= paths.length) return
      useGLTF.preload(paths[i])
      i += 1
      timerHandle = setTimeout(() => { idleHandle = ric(step) }, gapMs)
    }

    idleHandle = ric(step)

    return () => {
      cancelled = true
      if (idleHandle != null) { try { cancelRic(idleHandle) } catch { clearTimeout(idleHandle) } }
      if (timerHandle != null) clearTimeout(timerHandle)
    }
  }, [sceneReady, deviceInfo.isPhone])

  const handleFocusLocation = useCallback((location) => {
    if (location) {
      trackEvent('location_focus', { location_id: location.id })
    }
    // Collapse all satellite models to zero immediately — the useEffect above will
    // scale up the correct one after the camera zoom completes (600ms).
    targetScalesRef.current.forEach(e => { e.target = 0 })
    // Set HDRI intensity synchronously so SpaceEnvironment renders at the correct
    // intensity on the same commit that makes the models visible — never a frame without it.
    const enteringSatellite = location?.id === 'satellite'
    if (enteringSatellite) {
      const intensity = activeSatModel?.hdriIntensity ?? location.hdriIntensity ?? ENV_ACTIVE_VALUE
      setDeferredHdri(intensity)
    } else if (location?.hdri) {
      setDeferredHdri(location.hdriIntensity ?? ENV_ACTIVE_VALUE)
    } else if (!location) {
      setDeferredHdri(0)
    }
    if (!location || location.id !== 'satellite') {
      setFocusedSatelliteIndex(0)
    }
    if (!location || location.id !== 'orion') {
      setOrionDeployed(false)
      setOrionDetailsVisible(false)
      // Guarantee a clean deploy-button state on (re)entry: if the user left Orion
      // while a reverse animation was mid-flight, orionReverseComplete could be stuck
      // at false, which hides the DEPLOY / EXPLORE MISSION PROFILE button block.
      setOrionReverseComplete(true)
    }
    // Reset scroll hint so the arrow reappears for each new location
    setLocationPanelScrolled(false)
    setLocationPanelHasOverflow(false)
    setIsTransitioning(true)
    setFocusedLocation(location)
    createTimeout(() => {
      setIsTransitioning(false)
    }, 600)
  }, [createTimeout, activeSatModel])

  // Detect scroll on the location panel content to hide the scroll hint arrow
  useEffect(() => {
    const el = locationPanelScrollRef.current
    if (!el) return
    // Check after DOM has painted whether content actually overflows
    const checkOverflow = () => {
      setLocationPanelHasOverflow(el.scrollHeight > el.clientHeight)
    }
    const raf = requestAnimationFrame(checkOverflow)
    const handleScroll = () => { setLocationPanelScrolled(el.scrollTop > 0) }
    el.addEventListener('scroll', handleScroll, { passive: true })
    // Mobile portrait: re-check once the collapse/expand max-height transition finishes
    const wrapper = el.closest('.dsl-small-window')
    const handleTransitionEnd = (e) => {
      if (e.propertyName === 'max-height') checkOverflow()
    }
    wrapper?.addEventListener('transitionend', handleTransitionEnd)
    return () => {
      cancelAnimationFrame(raf)
      el.removeEventListener('scroll', handleScroll)
      wrapper?.removeEventListener('transitionend', handleTransitionEnd)
    }
  }, [focusedLocation, focusedSatelliteIndex, locationPanelCollapsed])

  // Mobile portrait: collapse the location panel text box whenever the focused
  // location OR the focused sub-component (SLS touch point / Orion section /
  // attachment) changes — so each detail panel starts collapsed and is revealed
  // by the "i" toggle.
  useEffect(() => {
    setLocationPanelCollapsed(true)
  }, [focusedLocation?.id, focusedSatelliteIndex, slsFocusedHotspot, orionFocusedSection, orionFocusedAttachment])

  // Global audio mute state — starts unmuted by default
  const [audioMuted, setAudioMuted] = useState(false)
  useEffect(() => { setMuted(false) }, [])
  const handleToggleMute = useCallback(() => {
    setAudioMuted(prev => {
      const next = !prev
      setMuted(next)
      trackEvent('audio_mute_toggle', { muted: next })
      return next
    })
  }, [])
  
  // Chase camera state
  const [chaseCameraRenderTarget, setChaseCameraRenderTarget] = useState(null)
  const [webgpuRenderer, setWebgpuRenderer] = useState(null)
  

  // Hotspot blink refs (no position state needed - rendered in 3D with Html)
  const hotspotBlinkRef = useRef(1)
  const spacestationHotspotBlinkRef = useRef(1)
  const lunarOpsHotspotBlinkRef = useRef(1)
  const londonHotspotBlinkRef = useRef(1)
  const artemisBlinkRef = useRef(1)
  const orionCapsuleRef = useRef(null)
  const orionArmatureRef = useRef(null)
  const earthOpacityOverrideRef = useRef([])
  // Holds the starfield's size-scale uniform so the chase camera can shrink the
  // stars just for its own render (they look oversized in the small chase view).
  const starSizeScaleRef = useRef(null)

  useEffect(() => {
    const nodes = orionArmatureRef.current
    if (!nodes?.length) return
    // Layer 3: visible to main camera (enableAll) but not chase camera (layer 0 only)
    nodes.forEach((node) => {
      node.traverse((child) => {
        child.layers.set(followModeActive ? 3 : 0)
      })
    })
  }, [followModeActive])

  const moonObjectiveHotspotBlinkRef = useRef(1)
  
  // Mission stage blink refs (no position state needed - rendered in 3D)
  const missionStageBlinkRefs = useRef({})
  
  // Initialize blink refs for each mission stage
  useEffect(() => {
    missionStagesConfig.missionStages.forEach(stage => {
      if (!missionStageBlinkRefs.current[stage.id]) {
        missionStageBlinkRefs.current[stage.id] = 1
      }
    })
  }, [])
  
  
  // Play CourseDraw_Short when the trajectory starts drawing (earthAtArtemisTarget flips to true), at 1.1x speed
  useEffect(() => {
    if (earthAtArtemisTarget) {
      playSound('CourseDraw_Short', 0.8, 1.33)
    }
  }, [earthAtArtemisTarget])

  // Track which mission stages have already triggered a DataBeep so we only play once per stage per mission
  const reachedStagesSoundedRef = useRef(new Set())

  // Reset the sounded set whenever the mission resets
  useEffect(() => {
    if (!artemisMissionLaunched) {
      reachedStagesSoundedRef.current.clear()
    }
  }, [artemisMissionLaunched])

  // Dismiss location focus when leaving MAIN mode (sls is exempt — it lives in ARTEMIS mode)
  useEffect(() => {
    if (currentMode !== nav.modes.MAIN) {
      setFocusedLocation(prev => (prev?.id === 'sls' || prev?.id === 'orion') ? prev : null)
    }
  }, [currentMode, nav.modes.MAIN])

  // Play DataBeep each time a mission stage point turns white (reached)
  useEffect(() => {
    if (!artemisMissionLaunched) return
    missionStagesConfig.missionStages.forEach(stage => {
      if (
        artemisProgress >= stage.trajectoryPercent &&
        !reachedStagesSoundedRef.current.has(stage.id)
      ) {
        reachedStagesSoundedRef.current.add(stage.id)
        playSound('DataBeep')
      }
    })
  }, [artemisProgress, artemisMissionLaunched])

  // Blinking animation for hotspots - uses refs to avoid state updates on every frame
  const rafIdRef = useRef(null)
  
  useEffect(() => {
    if (currentMode !== nav.modes.MAIN && currentMode !== nav.modes.ARTEMIS) return
    
    const animate = () => {
      const time = Date.now() * 0.001
      
      // Update refs every frame (cheap, no re-render)
      if (currentMode === nav.modes.MAIN) {
        // Satellite hotspot - blinkSpeed = 2.0
        const blinkValue1 = Math.sin(time * 2.0) * 0.5 + 0.5
        hotspotBlinkRef.current = blinkValue1 * 0.8 + 0.2
        
        // Space station hotspot - blinkSpeed = 1.5 (slower)
        const blinkValue2 = Math.sin(time * 1.5) * 0.5 + 0.5
        spacestationHotspotBlinkRef.current = blinkValue2 * 0.8 + 0.2
        
        // Lunar Ops hotspot - blinkSpeed = 1.8
        const blinkValue3 = Math.sin(time * 1.8) * 0.5 + 0.5
        lunarOpsHotspotBlinkRef.current = blinkValue3 * 0.8 + 0.2
        
        // London hotspot - blinkSpeed = 2.2
        const blinkValue4 = Math.sin(time * 2.2) * 0.5 + 0.5
        londonHotspotBlinkRef.current = blinkValue4 * 0.8 + 0.2
      }
      
      // Artemis brackets - hard blink on/off (1 second cycle)
      if (currentMode === nav.modes.ARTEMIS) {
        artemisBlinkRef.current = Math.floor((time * 1.0) % 2) // 0 or 1 (off or on)
        
        // Moon objective hotspot - blinkSpeed = 1.6
        const blinkValue5 = Math.sin(time * 1.6) * 0.5 + 0.5
        moonObjectiveHotspotBlinkRef.current = blinkValue5 * 0.8 + 0.2
      }
      
      rafIdRef.current = requestAnimationFrame(animate)
    }
    
    rafIdRef.current = requestAnimationFrame(animate)
    
    return () => {
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current)
        rafIdRef.current = null
      }
    }
  }, [currentMode, nav.modes.MAIN, nav.modes.ARTEMIS])

  // Prevent body scroll when modal is open
  useEffect(() => {
    // Store original styles for proper restoration
    const originalBodyOverflow = document.body.style.overflow
    const originalBodyTouchAction = document.body.style.touchAction
    
    if (showJoinMission) {
      // Lock scroll on body
      document.body.style.overflow = 'hidden'
      document.body.style.touchAction = 'none'
      
      // Prevent scroll on the canvas container
      const canvas = document.querySelector('canvas')
      const originalCanvasTouchAction = canvas?.parentElement?.style.touchAction || ''
      
      if (canvas?.parentElement) {
        canvas.parentElement.style.touchAction = 'none'
      }
      
      // Cleanup function restores original values
      return () => {
        document.body.style.overflow = originalBodyOverflow
        document.body.style.touchAction = originalBodyTouchAction
        if (canvas?.parentElement) {
          canvas.parentElement.style.touchAction = originalCanvasTouchAction
        }
      }
    } else {
      // Restore scroll when modal closes
      document.body.style.overflow = originalBodyOverflow
      document.body.style.touchAction = originalBodyTouchAction
    }
    
    // Cleanup on unmount - restore to original state
    return () => {
      document.body.style.overflow = originalBodyOverflow
      document.body.style.touchAction = originalBodyTouchAction
    }
  }, [showJoinMission])

  // Resolve a friendly section label for the join_mission_open analytics event
  // (Home | Mission Profile | Active Mission | Artemis SLS | Orion Spacecraft | Crew)
  const resolveJoinSection = useCallback(() => {
    if (showCrewProfile) return 'Crew'
    if (focusedLocation?.id === 'sls') return 'Artemis SLS'
    if (focusedLocation?.id === 'orion') return 'Orion Spacecraft'
    if (currentMode === nav.modes.ARTEMIS) return artemisMissionLaunched ? 'Active Mission' : 'Mission Profile'
    return 'Home'
  }, [showCrewProfile, focusedLocation, currentMode, artemisMissionLaunched, nav.modes.ARTEMIS])

  // Handle window open/close with flickering
  const handleOpenWindow = useCallback(() => {
    trackEvent('join_mission_open', { section: resolveJoinSection() })
    setIsTransitioning(true)
    setShowJoinMission(true)
    setIsClosingWindow(false)
    createTimeout(() => {
      setIsTransitioning(false)
    }, 600)
  }, [createTimeout, resolveJoinSection])

  const handleCloseWindow = useCallback(() => {
    playSound('JoinOurMission_Close')
    setIsTransitioning(true)
    setIsClosingWindow(true)
    createTimeout(() => {
      setShowJoinMission(false)
      setIsClosingWindow(false)
      setIsTransitioning(false)
    }, 400)
  }, [createTimeout, playSound])

  const handleCloseCrewProfile = useCallback(() => {
    playSound('JoinOurMission_Close')
    trackEvent('crew_profile_close')
    setShowCrewProfile(false)
  }, [])

  const handleOpenIframe = useCallback((url) => {
    window.open(url, '_blank', 'noopener,noreferrer')
  }, [])

  const handleOpenMissionComplete = useCallback(() => {
    trackEvent('artemis_mission_complete')
    setIsTransitioning(true)
    setShowMissionComplete(true)
    setIsClosingMissionComplete(false)
    createTimeout(() => {
      setIsTransitioning(false)
    }, 600)
  }, [createTimeout])

  const handleCloseMissionComplete = useCallback(() => {
    trackEvent('artemis_mission_complete_close')
    setIsTransitioning(true)
    setIsClosingMissionComplete(true)
    createTimeout(() => {
      setShowMissionComplete(false)
      setIsClosingMissionComplete(false)
      setIsTransitioning(false)
    }, 400)
  }, [createTimeout])

  const handleCloseStarcade = useCallback(() => {
    setIsTransitioning(true)
    setIsClosingStarcade(true)
    createTimeout(() => {
      setCurrentSection(nav.sections.MAIN)
      setCurrentMode(nav.modes.MAIN)
      setIsClosingStarcade(false)
      setIsTransitioning(false)
    }, 400)
  }, [nav, createTimeout])

  const [isClosingPortfolio, setIsClosingPortfolio] = useState(false)

  const handleClosePortfolio = useCallback(() => {
    setIsTransitioning(true)
    setIsClosingPortfolio(true)
    createTimeout(() => {
      setCurrentSection(nav.sections.MAIN)
      setCurrentMode(nav.modes.MAIN)
      setIsClosingPortfolio(false)
      setIsTransitioning(false)
    }, 400)
  }, [nav, createTimeout])

  const handleCloseArtemis = useCallback(() => {
    setIsTransitioning(true)
    setIsClosingArtemis(true)
    createTimeout(() => {
      setCurrentSection(nav.sections.MAIN)
      setCurrentMode(nav.modes.MAIN)
      setIsClosingArtemis(false)
      setIsTransitioning(false)
    }, 400)
  }, [nav, createTimeout])

  const handleArtemisClick = useCallback(() => {
    if (currentMode === nav.modes.ARTEMIS) {
      // Trigger trajectory erase animation but change mode immediately
      if (typeof window.trajectoryErase === 'function') {
        window.trajectoryErase()
      }

      // Start camera transition immediately - stop mission tracking right away
      setIsTransitioning(true)
      setArtemisMissionLaunched(false) // Stop mobile portrait camera immediately
      setCurrentSection(nav.sections.MAIN)
      setCurrentMode(nav.modes.MAIN)
      setArtemisSunTarget(null)
      setArtemisStage(1)
      setArtemisGuidedMode(true)
      setTrajectoryDrawComplete(false) // triggers marker glitch-out immediately (500ms), well before R3F tree unmounts
      setArtemisLaunchChosen(false)
      setShowJoinMission(false)

      // Keep trajectory visible for erase animation, then hide everything
      createTimeout(() => {
        setShowTrajectory(false)
        setEarthAtArtemisTarget(false)
        setArtemisProgress(0)
        setArtemisComplete(false)
        setShowMissionComplete(false)
        setIsClosingMissionComplete(false)
      }, 1000)

      // Remove transition state after camera animation
      createTimeout(() => {
        setIsTransitioning(false)
      }, 600)
    } else {
      // Navigate to artemis from any mode — enter Stage 1 (no sun/earth rotation yet)
      trackEvent('artemis_enter')
      setIsTransitioning(true)
      setCurrentSection(nav.sections.ARTEMIS)
      setCurrentMode(nav.modes.ARTEMIS)
      setArtemisStage(1)
      setEarthAtArtemisTarget(false)
      setShowTrajectory(false)
      setArtemisProgress(0)
      setArtemisComplete(false)
      setTrajectoryDrawComplete(false)
      setArtemisMissionLaunched(false)

      createTimeout(() => {
        setIsTransitioning(false)
      }, 600)
    }
  }, [nav, currentMode, createTimeout])

  const handleExploreMissionProfile = useCallback(() => {
    playSound('PlotCourse')
    trackEvent('artemis_explore_profile')
    setFocusedLocation(null)
    setArtemisStage(2)
    setShowTrajectory(true)
    setArtemisSunTarget(ARTEMIS_SUN_START_ROTATION)
  }, [playSound])

  const handleArtemisReset = useCallback(() => {
    // Set artemisComplete false FIRST so the useEffect that watches
    // artemisComplete + showMissionComplete doesn't re-open the window
    // while the closing animation is still running.
    setArtemisComplete(false)

    // Close any open stage lightbox — ABORT/RESET during a running mission should
    // dismiss it (otherwise it lingers over the reset scene).
    setActiveLightboxMedia(null)
    setActiveLightboxLayerLabel(null)

    // Pause the animation immediately so ArtemisOnPath's crossing detector
    // (!isPaused gate at line 477) can't open a new lightbox during the 600ms
    // delay below — the abort is synchronous, the state commit beats the next
    // rAF, so the detector never fires.
    setArtemisPaused(true)

    // Close mission complete window if open
    if (showMissionComplete) {
      handleCloseMissionComplete()
    }
    
    // Step 1: switch to course view immediately so camera transitions smoothly
    setFollowModeActive(false)
    
    // Step 2: reset mission state after a short delay to allow camera to return
    createTimeout(() => {
      if (typeof window.artemisReset === 'function') {
        window.artemisReset()
      }
      setArtemisProgress(0)
      setArtemisMissionLaunched(false)
      setArtemisLaunchChosen(false)
      setArtemisGuidedMode(true)
      // Don't reset trajectoryDrawComplete - keep path visible
    }, 600)
  }, [showMissionComplete, handleCloseMissionComplete, createTimeout])
  
  const handleReturnToArtemisScene1 = useCallback(() => {
    // Full reset to initial load state — Artemis Stage 1, no mission state.
    // Everything that affects camera/mode/HUD is applied synchronously (no
    // createTimeout stagger) — same pattern as handleFocusLocation(null) for
    // Orion/SLS, which returns instantly.
    //
    // showTrajectory/earthAtArtemisTarget are the exception: they gate the
    // TrajectoryEditor AND the ~30 MissionStageMarker Html portals mounted
    // along the path. Flipping them in this same commit force-unmounts all of
    // that synchronously (each marker has its own effect cleanup), which is
    // what caused the visible freeze/hitch on HOME. trajectoryDrawComplete
    // already drives each marker's own `visible` prop (their built-in
    // glitch-out exit), so clear that here and defer the actual unmount one
    // frame so the cheap "fade out" commit and the expensive "unmount" commit
    // aren't the same commit.
    setCurrentSection(nav.sections.ARTEMIS)
    setCurrentMode(nav.modes.ARTEMIS)
    setArtemisStage(1)
    setArtemisGuidedMode(true)
    setArtemisMissionLaunched(false)
    setArtemisLaunchChosen(false)
    setArtemisSunTarget(null)
    setTrajectoryDrawComplete(false)
    setShowJoinMission(false)
    setArtemisProgress(0)
    setArtemisComplete(false)
    setArtemisPaused(false)
    setShowMissionComplete(false)
    setIsClosingMissionComplete(false)
    if (typeof window.artemisReset === 'function') window.artemisReset()
    requestAnimationFrame(() => {
      setShowTrajectory(false)
      setEarthAtArtemisTarget(false)
    })
  }, [nav])

  // HOME — return RIGHT back to the beginning from anywhere: close any focused
  // location, crew screen, and join window, then run the full Scene-1 reset.
  const handleHome = useCallback(() => {
    playSound('HideCourse')
    trackEvent('home_button')
    // Leaving a *running* mission via HOME counts as abandoning it — fire the same
    // abandon event as the ABORT button so the GTM session tracker refreshes (the
    // kiosk-only gating lives GTM-side via the persisted `kiosk` dataLayer value).
    // NOTE: gated on !artemisComplete so a completed mission — which already fired
    // artemis_mission_complete (its own session refresh) — isn't double-counted as a
    // second session end. To make HOME mirror ABORT/RESET exactly (i.e. also abandon
    // on completed missions), drop the `&& !artemisComplete`.
    if (artemisMissionLaunched && !artemisComplete) {
      trackEvent('artemis_abandon_mission', { mission_complete: artemisComplete })
    }
    setShowCrewProfile(false)
    setShowJoinMission(false)
    setFollowModeActive(false)
    setActiveLightboxMedia(null)
    setActiveLightboxLayerLabel(null)
    // Snap the spin controllers back to rest so the models return to their
    // correct orientation (otherwise leaving Orion deep in a section, or returning
    // to the SLS more than once, can leave them rotated to a stale pose — the SLS
    // stays mounted in Scene 1, so its return spring can be cut short mid-slerp).
    orionSnapToRestRef.current = true
    orionOuterSnapToRestRef.current = true
    slsSnapToRestRef.current = true
    if (focusedLocation) handleFocusLocation(null)
    handleReturnToArtemisScene1()
  }, [playSound, focusedLocation, handleFocusLocation, handleReturnToArtemisScene1, artemisMissionLaunched, artemisComplete])

  // Resolve the visible stage / sub-point marker sitting at a given trajectory percent
  const resolveMarkerByPercent = useCallback((pct) => {
    let marker = missionStagesConfig.missionStages.find(s => s.visible && s.trajectoryPercent === pct)
    if (!marker) {
      for (const s of missionStagesConfig.missionStages) {
        const sub = s.subPoints?.find(p => p.visible && p.trajectoryPercent === pct)
        if (sub) { marker = sub; break }
      }
    }
    return marker
  }, [])

  const openLightboxForPercent = useCallback((pct) => {
    const marker = resolveMarkerByPercent(pct)
    if (marker) {
      // Stage reached non-manually (guided auto-arrival, skip back/forward, guided
      // launch) — pairs with the artemis_stage_clicked event on direct clicks
      trackEvent('artemis_stage_guided', { stage_id: marker.id })
      setActiveLightboxLayerLabel(null)
      setActiveLightboxMedia({
        label: marker.lightbox?.title ?? marker.id,
        slides: marker.lightbox?.slides ?? [],
        badge: getMissionStageBadge(marker.id),
        stageId: marker.id,
        openMode: 'guided'
      })
      playSound('DataPoint_Open')
    }
  }, [playSound, resolveMarkerByPercent])

  const handleArtemisMissionLaunch = useCallback(() => {
    setArtemisPaused(true)
    setArtemisProgress(0)
    // Hard-reset the path animator to a clean t=0 at launch. Pre-launch the
    // mission is NOT paused (artemisPaused is false from the moment Artemis mode
    // opens), so ArtemisOnPath's useFrame has been quietly advancing timeRef the
    // whole time you sat on the pre-launch profile. Without this reset, that drift
    // — plus the one-render lag before setArtemisPaused(true) reaches isPaused —
    // lets the crossing detector cross the first real pause marker right after
    // launch, slamming the second stage's lightbox over stage 0's. Seeking to 0
    // zeroes timeRef, clears triggeredMarkersRef, and rewinds lastProgressExactRef.
    if (artemisSeekRef.current) artemisSeekRef.current(0)
    setFollowModeActive(false)
    setArtemisMissionLaunched(true)
    playSound('LaunchMission')
    setArtemisLaunchChosen(true)
    // The mode (guided/auto) is chosen ahead of launch via the toggle next to
    // MUTE, so launching goes straight into whichever mode is currently set —
    // there's no separate post-launch GUIDED/AUTO picker step any more.
    if (artemisGuidedMode) {
      // The first pip (LAUNCH) sits at trajectoryPercent 0, which the
      // crossing-based pause detector can't catch (you start AT 0). In guided
      // mode, open its lightbox immediately and stay paused; CONTINUE then
      // advances to the next pip as normal.
      openLightboxForPercent(0)
    } else {
      setArtemisPaused(false)
    }
  }, [artemisGuidedMode, playSound, openLightboxForPercent])

  const handleArtemisSeek = useCallback((targetPercent) => {
    if (artemisSeekRef.current) artemisSeekRef.current(targetPercent)
    if (targetPercent < 100) {
      setArtemisComplete(false)
      if (showMissionComplete) handleCloseMissionComplete()
    }
  }, [showMissionComplete, handleCloseMissionComplete])

  const handleSeekBarTogglePause = useCallback(() => {
    setArtemisPaused(p => {
      trackEvent('artemis_seekbar_toggle_pause', { paused: !p })
      return !p
    })
  }, [])

  // Track a seek-bar scrub/tap on release (not per pointermove) with the landing
  // percent and the nearest visible stage/sub-point marker
  const handleSeekBarScrub = useCallback((pct) => {
    const markers = []
    for (const s of missionStagesConfig.missionStages) {
      if (s.visible) markers.push(s)
      s.subPoints?.forEach(p => p.visible && markers.push(p))
    }
    const nearest = markers.reduce((a, b) =>
      Math.abs(b.trajectoryPercent - pct) < Math.abs(a.trajectoryPercent - pct) ? b : a, markers[0])
    trackEvent('artemis_seekbar_scrub', { seekbar_percent: Math.round(pct), seekbar_nearest_stage: nearest?.id })
  }, [])

  const handleSeekBarSetGuided = useCallback(() => {
    playSound('LaunchMission')
    trackEvent('artemis_mission_mode_selected', { mode: 'guided' })
    setArtemisGuidedMode(true)
    setArtemisPaused(false)
  }, [playSound])

  const handleSeekBarSetAuto = useCallback(() => {
    playSound('LaunchMission')
    trackEvent('artemis_mission_mode_selected', { mode: 'auto' })
    setArtemisGuidedMode(false)
    setArtemisPaused(false)
  }, [playSound])

  const handleSeekBarSkipBack = useCallback(() => {
    // pauseAtPercents excludes 0 (the LAUNCH pip), so fall back to 0 explicitly —
    // otherwise skip-back bottoms out at stage 1's marker and LAUNCH is unreachable.
    const prev = [...pauseAtPercents].reverse().find(p => p < artemisProgress - 0.5)
    const target = prev != null ? prev : (artemisProgress > 0.5 ? 0 : null)
    if (target != null) {
      trackEvent('artemis_seekbar_skip_back')
      handleArtemisSeek(target)
      if (artemisGuidedMode) { setArtemisPaused(true); openLightboxForPercent(target) }
    }
  }, [pauseAtPercents, artemisProgress, artemisGuidedMode, handleArtemisSeek, openLightboxForPercent])

  const handleSeekBarSkipForward = useCallback(() => {
    const next = pauseAtPercents.find(p => p > artemisProgress + 0.5)
    if (next != null) {
      trackEvent('artemis_seekbar_skip_forward')
      handleArtemisSeek(next)
      if (artemisGuidedMode) { setArtemisPaused(true); openLightboxForPercent(next) }
    }
  }, [pauseAtPercents, artemisProgress, artemisGuidedMode, handleArtemisSeek, openLightboxForPercent])

  const handleChaseCameraRenderTargetUpdate = useCallback((data) => {
    setChaseCameraRenderTarget(data)
  }, [])

  const handleMarkerClick = useCallback((stageId, stageName) => {
    let marker = missionStagesConfig.missionStages.find(s => s.id === stageId)
    if (!marker) {
      for (const s of missionStagesConfig.missionStages) {
        const sub = s.subPoints?.find(p => p.id === stageId)
        if (sub) { marker = sub; break }
      }
    }
    playSound('DataPoint_Open')
    setActiveLightboxLayerLabel(null)
    setActiveLightboxMedia({
      label: marker?.lightbox?.title ?? stageName,
      slides: marker?.lightbox?.slides ?? [],
      badge: getMissionStageBadge(stageId),
      stageId,
      openMode: 'manual'
    })
    setVisitedStageIds(prev => prev.has(stageId) ? prev : new Set(prev).add(stageId))
  }, [playSound])

  const handleLightboxClose = useCallback(() => {
    playSound(artemisGuidedMode && artemisMissionLaunched ? 'LaunchMission' : 'DataPoint_Close')
    setActiveLightboxMedia(null)
    setActiveLightboxLayerLabel(null)
    // Resume playback on close during a launched mission — guided (CONTINUE) as
    // before, and on mobile portrait ALSO in unguided (CLOSE), where opening a
    // stage lightbox pauses the animation but closing should let it play on.
    if (artemisMissionLaunched && (artemisGuidedMode || mediaQueryKey === 'mobilePortrait')) {
      setArtemisPaused(false)
    }
  }, [artemisGuidedMode, artemisMissionLaunched, mediaQueryKey])

  // (Removed 2026-07-03) an effect here forced artemisGuidedMode = true on mobile
  // launch — written when mobile had no waypoint lightboxes. Mobile now has the
  // pre-launch GUIDED/UNGUIDED switch + stage lightboxes, so forcing guided on at
  // launch overrode the user's choice (the mission still paused at stages after
  // they picked UNGUIDED). The chosen mode now carries into the launched mission.

  // Pause mission when tablet rotates to portrait; resume on return to landscape.
  // Only fires on rotation changes — handleArtemisMissionLaunch sets the correct
  // pause state at launch; adding artemisMissionLaunched as a dep would override it.
  useEffect(() => {
    if (!artemisMissionLaunched) return
    if (mediaQueryKey === 'tabletPortrait') {
      setArtemisPaused(true)
    } else {
      setArtemisPaused(false)
    }
  }, [mediaQueryKey])

  // Pause the mission whenever the JOIN US or crew scene is open — the timeline
  // animation should never keep advancing behind those overlays, regardless of
  // Track Orion (follow) mode. Resume when both are closed (per the same
  // always-resume-on-close pattern as the tablet-rotation effect above), unless
  // tablet portrait still holds it paused.
  //
  // Only ACT on a real overlay open→close (or close→open) transition. Previously
  // this effect had artemisMissionLaunched in its deps and unconditionally hit the
  // resume branch on any run, so the launch transition (missionLaunched false→true,
  // both overlays already closed) re-ran it and force-resumed the mission the same
  // tick handleArtemisMissionLaunch had paused it — the animation then played from
  // t=0 and crossed stage 1's marker, slamming the second lightbox over stage 0's.
  const overlayOpenRef = useRef(false)
  useEffect(() => {
    const overlayOpen = showJoinMission || showCrewProfile
    const wasOpen = overlayOpenRef.current
    overlayOpenRef.current = overlayOpen
    if (!artemisMissionLaunched) return
    if (overlayOpen === wasOpen) return // not an overlay transition (e.g. launch tick) — leave pause alone
    if (overlayOpen) {
      setArtemisPaused(true)
    } else if (mediaQueryKey !== 'tabletPortrait') {
      setArtemisPaused(false)
    }
  }, [showJoinMission, showCrewProfile, artemisMissionLaunched, mediaQueryKey])

  // Handle Artemis position updates for mobile portrait launch camera
  const handleArtemisPositionUpdate = useCallback((position) => {
    // Write directly to ref — no setState, no re-render
    artemisPositionRef.current[0] = position[0]
    artemisPositionRef.current[1] = position[1]
    artemisPositionRef.current[2] = position[2]
  }, [])
  
  // Handle mission stage marker click - open lightbox (same style as blue layer markers)
  const handleMissionStageClick = useCallback((stageId, stageName) => {
    trackEvent('artemis_stage_clicked', { stage_id: stageId })
    handleMarkerClick(stageId, stageName)
  }, [handleMarkerClick])
  
  
  useEffect(() => {
    if (artemisComplete && !showMissionComplete) {
      createTimeout(() => { handleOpenMissionComplete() }, 500)
    }
  }, [artemisComplete, showMissionComplete, handleOpenMissionComplete, createTimeout])

  useEffect(() => {
    if (artemisComplete && followModeActive) {
      setFollowModeActive(false)
    }
  }, [artemisComplete, followModeActive])

  // Cleanup window globals and tooltip timer on unmount
  useEffect(() => {
    return () => {
      // Clean up any window-attached functions when component unmounts
      if (typeof window.trajectoryErase === 'function') {
        delete window.trajectoryErase
      }
      if (typeof window.artemisReset === 'function') {
        delete window.artemisReset
      }
      // Clean up tooltip timer
      if (tooltipTimerRef.current) {
        clearTimeout(tooltipTimerRef.current)
      }
    }
  }, [])

  // lightingPosition is now sunPositionRef — updated imperatively by SunRotation

  const handleLeftButtonClick = useCallback(() => {
    setIsTransitioning(true)
    
    if (currentMode === nav.modes.PORTFOLIO) {
      setCurrentSection(nav.sections.MAIN)
      setCurrentMode(nav.modes.MAIN)
    } else {
      trackEvent('nav_starcade')
      setCurrentSection(nav.sections.STARCADE)
      setCurrentMode(nav.modes.STARCADE)
    }
    
    // Remove transition state after animation completes
    createTimeout(() => {
      setIsTransitioning(false)
    }, 600)
  }, [nav, currentMode, createTimeout])

  const handleRightButtonClick = useCallback(() => {
    setIsTransitioning(true)
    
    if (currentMode === nav.modes.STARCADE) {
      setCurrentSection(nav.sections.MAIN)
      setCurrentMode(nav.modes.MAIN)
    } else {
      trackEvent('nav_portfolio')
      setCurrentSection(nav.sections.PORTFOLIO)
      setCurrentMode(nav.modes.PORTFOLIO)
    }
    
    // Remove transition state after animation completes
    createTimeout(() => {
      setIsTransitioning(false)
    }, 600)
  }, [nav, currentMode, createTimeout])

  // Only hide loader when BOTH textures are loaded AND scene is ready
  const showLoader = !preloadComplete || !sceneReady
  // Subscribe to iframe state to hide UI/hotspots when iframe is active.


  // On mission completion, return to Scene 1 after 30 seconds of no user input
  useEffect(() => {
    if (!artemisComplete) return
    let timer = null
    const resetTimer = () => {
      clearTimeout(timer)
      timer = setTimeout(() => handleReturnToArtemisScene1(), 30000)
    }
    const events = ['mousemove', 'mousedown', 'touchstart', 'keydown']
    events.forEach(e => window.addEventListener(e, resetTimer))
    resetTimer()
    return () => {
      clearTimeout(timer)
      events.forEach(e => window.removeEventListener(e, resetTimer))
    }
  }, [artemisComplete, handleReturnToArtemisScene1])

  const showOverlay = currentMode === nav.modes.STARCADE || currentMode === nav.modes.PORTFOLIO || showJoinMission || showCrewProfile

  // Determine which buttons to show based on current mode
  // Hide starcade/portfolio buttons when artemis mode is active
  const showLeftButton = !isArtemisModeActive && !focusedLocation && (currentMode === nav.modes.MAIN || currentMode === nav.modes.PORTFOLIO) && !showCrewProfile
  const showRightButton = !isArtemisModeActive && !focusedLocation && (currentMode === nav.modes.MAIN || currentMode === nav.modes.STARCADE) && !showCrewProfile

  // Show Artemis button on MAIN mode (to enter) and ARTEMIS mode (to exit)
  const showArtemisButton = currentMode === nav.modes.MAIN && sceneReady && !focusedLocation && !showJoinMission && !showCrewProfile

  const currentCameraConfig = useMemo(() => {
    const modeKey = (currentMode === nav.modes.ARTEMIS && artemisStage < 2) ? nav.modes.MAIN : currentMode
    const tierCfg = navigationCameraConfig.modes[modeKey]
    // Tablet landscape + 1440p have no dedicated camera block — both use the
    // desktop1080 camera (tablet is treated as a desktop breakpoint).
    return tierCfg[mediaQueryKey]
      || tierCfg.desktop1080
  }, [currentMode, artemisStage, mediaQueryKey])

  // Override camera config when a location is focused (MAIN mode only)
  const activeCameraConfig = useMemo(() => {
    // scaleFov applies a per-tablet-landscape fov multiplier. Currently 1.0 (a
    // no-op) — kept as a single tuning point. NOTE: camera-config resolution now
    // prefers a config's own `tabletCamera` (via resolveLabelCameraConfig / the
    // cam resolution below) where authored, so the old assumption that tablet
    // always inherits the desktop `.camera` fov no longer holds; this multiplier
    // stacks on top of whichever tier's fov was picked.
    const TABLET_FOV_SCALE = 35 / 35
    const scaleFov = (cfg) => {
      if (!cfg || mediaQueryKey !== 'tabletLandscape' || cfg.fov === undefined) return cfg
      return { ...cfg, fov: cfg.fov * TABLET_FOV_SCALE }
    }
    // Optional camera POSITION for the focused SLS / Orion base views. Focused
    // configs normally carry no `position` (the camera holds its current spot and
    // only re-aims). A location in locations.json can set the physical camera two
    // ways:
    //   1. ABSOLUTE — `mobilePosition` (mobile portrait) / `position` (all tiers):
    //      an explicit world position, INDEPENDENT of the home/main camera. Prefer
    //      this — it does not shift when the home camera is retuned.
    //   2. OFFSET (legacy) — `mobilePositionOffset` / `positionOffset`: added to the
    //      current mode (home) position. This couples the focused view to the home
    //      camera, so retuning home moves SLS/Orion too — the absolute form avoids
    //      that. Kept for anything still authored as an offset.
    // Mobile-portrait model shift for the focused location (SLS/Orion), so the
    // camera EYE translates with the model too — matching the lookAt shift applied
    // to `cam` above. Keeps the focused framing rigid (same composition, moved).
    const mpEyeOff = focusedLocation?.id === 'sls' ? mpSlsOff
      : focusedLocation?.id === 'orion' ? mpOrionOff
      : null
    const shiftEye = (p) => (mpEyeOff && (mpEyeOff[0] || mpEyeOff[1] || mpEyeOff[2]))
      ? [p[0] + mpEyeOff[0], p[1] + mpEyeOff[1], p[2] + mpEyeOff[2]]
      : p
    const applyPositionOffset = (cfg) => {
      if (!cfg) return cfg
      // Absolute position wins — decoupled from the home camera.
      const absPos = (mediaQueryKey === 'mobilePortrait' && focusedLocation?.mobilePosition)
        || focusedLocation?.position
      if (absPos && (absPos[0] || absPos[1] || absPos[2])) {
        return { ...cfg, position: shiftEye([absPos[0], absPos[1], absPos[2]]) }
      }
      const off = (mediaQueryKey === 'mobilePortrait' && focusedLocation?.mobilePositionOffset)
        || focusedLocation?.positionOffset
      // Only override the position when the offset is actually non-zero — a zero
      // (or absent) offset must NOT set config.position, or the camera would jump
      // to the base position instead of holding its current SLS/Orion framing.
      if (!off || (!off[0] && !off[1] && !off[2])) return cfg
      const basePos = cfg.position || currentCameraConfig?.position
      if (!basePos) return cfg
      return {
        ...cfg,
        position: [basePos[0] + (off[0] || 0), basePos[1] + (off[1] || 0), basePos[2] + (off[2] || 0)],
      }
    }
    if (focusedLocation && (currentMode === nav.modes.MAIN || focusedLocation.id === 'sls' || focusedLocation.id === 'orion')) {
      let cam
      if (focusedLocation.id === 'satellite' && activeSatModel?.camera) {
        cam = resolveLabelCameraConfig(activeSatModel, mediaQueryKey)
      } else {
        // Was mobileCamera-or-camera only — tabletCamera (e.g. Orion/SLS's own
        // distinct tablet lookAt/fov in locations.json) was silently skipped,
        // always falling back to desktop's camera on tablet.
        cam = resolveLabelCameraConfig(focusedLocation, mediaQueryKey) || null
      }
      // Mobile portrait: the SLS/Orion models are shifted by mpSlsOff/mpOrionOff
      // (see slsScenePosition / orionScenePosition). The focus lookAt in
      // locations.json is authored against the UNSHIFTED position, so shift the
      // resolved lookAt by the same offset here — otherwise the camera zooms to
      // where the model used to be. Every downstream `return` reads this cam.lookAt
      // (including the deployed-Orion branch), so shifting once covers all of them.
      const mpFocusOff = focusedLocation.id === 'sls' ? mpSlsOff
        : focusedLocation.id === 'orion' ? mpOrionOff
        : null
      if (cam?.lookAt && mpFocusOff && (mpFocusOff[0] || mpFocusOff[1] || mpFocusOff[2])) {
        cam = {
          ...cam,
          lookAt: [cam.lookAt[0] + mpFocusOff[0], cam.lookAt[1] + mpFocusOff[1], cam.lookAt[2] + mpFocusOff[2]],
        }
      }
      // SLS touch point focused: aim the camera at that part. lookAt is the
      // attachment's live world position, tracked by SlsAttachmentLookAtTracker;
      // fov/smoothTime come from the hotspot's per-breakpoint camera config.
      if (focusedLocation.id === 'sls' && slsFocusedHotspot && slsAttachmentLookAtRef.current) {
        const hs = focusedLocation.hotspots?.find(h => h.id === slsFocusedHotspot)
        const hsCam = hs && ((mediaQueryKey === 'mobilePortrait' && hs.mobileCamera) ? hs.mobileCamera : hs.camera)
        if (hsCam) return scaleFov({ ...hsCam, lookAt: slsAttachmentLookAtRef.current })
      }
      // Level 3 — an attachment point is focused: aim the camera at the attachment.
      // Falls back to the focused section's camera (fov/smoothTime) when the hotspot
      // has no camera config of its own, so it works before per-hotspot config exists.
      if (focusedLocation.id === 'orion' && orionDeployed && orionFocusedAttachment && orionAttachmentLookAtRef.current) {
        const hs = focusedLocation.hotspots?.find(h => h.id === orionFocusedAttachment)
        const sectionLabel = focusedLocation.deployedLabels?.find(l => l.id === orionFocusedSection)
        const hsBase = resolveLabelCameraConfig(hs, mediaQueryKey)
        const sectBase = resolveLabelCameraConfig(sectionLabel, mediaQueryKey)
        // Field-level merge: a hotspot can override just fov (or smoothTime),
        // inheriting the rest from its section's camera config.
        const base = { ...(sectBase || {}), ...(hsBase || {}) }
        if (base.fov !== undefined || base.smoothTime !== undefined) {
          return scaleFov({ ...base, lookAt: orionAttachmentLookAtRef.current })
        }
      }
      // Level 2 — a deployed Orion section is focused: use that section's camera
      // config; lookAt is computed at click time from the live group world position
      if (focusedLocation.id === 'orion' && orionDeployed && orionFocusedSection && orionSectionLookAtRef.current) {
        const label = focusedLocation.deployedLabels?.find(l => l.id === orionFocusedSection)
        if (label) {
          const base = resolveLabelCameraConfig(label, mediaQueryKey)
          if (base) return scaleFov({ ...base, lookAt: orionSectionLookAtRef.current })
        }
      }
      // When Orion is deployed, shift camera lookAt X to reveal deployed state.
      // Tablet can optionally use a different FOV/lookAt once deployed (per user)
      // via tabletCamera.deployedFov / deployedLookAt in locations.json — falls
      // back to the normal tabletCamera.fov and the [3.5, y, z] shift when absent,
      // so desktop/mobile (no equivalent fields) and tablet-not-deployed are
      // unaffected.
      // Mobile portrait: do NOT re-aim on deploy — the X-reveal shift caused a
      // small, unwanted lookAt jump when pressing DEPLOY (per user). Hold the
      // pre-deploy lookAt unchanged on this tier.
      if (focusedLocation.id === 'orion' && orionDeployed && cam) {
        if (mediaQueryKey === 'mobilePortrait') {
          return applyPositionOffset(scaleFov(cam))
        }
        const isTablet = mediaQueryKey === 'tabletLandscape' || mediaQueryKey === 'tabletPortrait'
        const deployedFov = isTablet ? focusedLocation.tabletCamera?.deployedFov : undefined
        const deployedLookAt = isTablet ? focusedLocation.tabletCamera?.deployedLookAt : undefined
        return applyPositionOffset(scaleFov({
          ...cam,
          fov: deployedFov ?? cam.fov,
          lookAt: deployedLookAt ?? [3.5, cam.lookAt[1], cam.lookAt[2]],
        }))
      }
      return applyPositionOffset(scaleFov(cam))
    }
    return currentCameraConfig
  }, [focusedLocation, currentMode, currentCameraConfig, activeSatModel, mediaQueryKey, nav.modes.MAIN, orionDeployed, orionFocusedSection, orionFocusedAttachment, slsFocusedHotspot, mpSlsOff, mpOrionOff])
  
  // Level 3 — the locked yaw (radians) for the active attachment, applied only to
  // the focused section's spin controller. null = free (level 2). targetYaw is
  // filled into each hotspot in locations.json later.
  const orionActiveAttachment = orionFocusedAttachment
    ? orionLocation?.hotspots?.find(h => h.id === orionFocusedAttachment)
    : null
  // targetYaw/Pitch/Roll are authored in DEGREES (consistent with flipBand) → radians.
  // Mobile portrait uses its own mobileTarget* trio (falls back to the desktop value
  // per-axis when a mobile override isn't authored).
  const isMobilePortrait = mediaQueryKey === 'mobilePortrait'
  const attachTargetYaw = isMobilePortrait
    ? (orionActiveAttachment?.mobileTargetYaw ?? orionActiveAttachment?.targetYaw)
    : orionActiveAttachment?.targetYaw
  const attachTargetPitch = isMobilePortrait
    ? (orionActiveAttachment?.mobileTargetPitch ?? orionActiveAttachment?.targetPitch)
    : orionActiveAttachment?.targetPitch
  const attachTargetRoll = isMobilePortrait
    ? (orionActiveAttachment?.mobileTargetRoll ?? orionActiveAttachment?.targetRoll)
    : orionActiveAttachment?.targetRoll
  const orionAttachmentLockYaw = attachTargetYaw != null
    ? attachTargetYaw * (Math.PI / 180)
    : null
  const orionAttachmentLockPitch = (attachTargetPitch ?? 0) * (Math.PI / 180)
  const orionAttachmentLockRoll  = (attachTargetRoll  ?? 0) * (Math.PI / 180)
  // The focused section's group ref + pivot — used to predict where the attachment
  // lands at targetYaw so the camera aims there instead of chasing it on entry.
  const orionActiveSectionGroupRef = { icps: orionIcspRef, esm: orionEsmCtrlRef, integrity: orionIntCtrlRef }[orionFocusedSection] ?? null
  const orionActiveSectionPivot = orionFocusedSection
    ? (() => {
        const lbl = orionLocation?.deployedLabels?.find(l => l.id === orionFocusedSection)
        const p = lbl?.spinPivot ?? [0, 0, 0]
        const o = lbl?.spinPivotOffset ?? [0, 0, 0]
        return [p[0] + o[0], p[1] + o[1], p[2] + o[2]]
      })()
    : null

  // Enter level 3 on an attachment point. Shared by the hotspot onClick and the
  // temporary dev forward/back cycling buttons so both behave identically.
  const focusOrionAttachment = (hs) => {
    if (!hs) return
    trackEvent('orion_attachment_clicked', { hotspot_id: hs.id })
    const hsSection = hs.attachmentKey?.startsWith('ESM') ? 'esm'
      : hs.attachmentKey?.startsWith('ICPS') ? 'icps'
      : hs.attachmentKey?.startsWith('Int') ? 'integrity'
      : null
    // Camera look-at depends ONLY on lookAtOffset (not the attachment mesh
    // position or the section rotation). Anchor to the section's ROTATION-FREE
    // origin (basePos), decoupled from spinPivot — same maths as
    // OrionAttachmentLookAtTracker so the entry transition aims at the exact point
    // the tracker then holds. Two points with the same lookAtOffset resolve to the
    // identical target (no camera move); it shifts only when lookAtOffset differs.
    const sg = { icps: orionIcspRef, esm: orionEsmCtrlRef, integrity: orionIntCtrlRef }[hsSection]?.current
    const lbl = orionLocation?.deployedLabels?.find(l => l.id === hsSection)
    if (sg) {
      const sp = lbl?.spinPivot ?? [0, 0, 0], so = lbl?.spinPivotOffset ?? [0, 0, 0]
      const c = [sp[0] + so[0], sp[1] + so[1], sp[2] + so[2]]
      sg.updateWorldMatrix(true, false)
      // restPos = position + R·c − c  (rotation-free section origin in parent space)
      const wp = new Vector3(c[0], c[1], c[2]).applyQuaternion(sg.quaternion)
      wp.x += sg.position.x - c[0]; wp.y += sg.position.y - c[1]; wp.z += sg.position.z - c[2]
      if (sg.parent) {
        // Selecting a module also snaps the outer (deployed) group back to its rest
        // orientation (orionOuterSnapToRestRef). If the user had ROTATED the deployed
        // spacecraft, sg.parent still holds that live rotation right now — reading it
        // here would aim the entry transition at the rotated pose, then jump-cut once
        // the snap-to-rest finishes. Read the parent's world matrix AS IF at rest
        // (temporarily copy the rest quat, restore after) so the look-at matches the
        // settled pose the model animates to — mirrors focusOrionSection.
        let savedOuterQuat = null
        if (orionOuterRef.current) {
          savedOuterQuat = orionOuterRef.current.quaternion.clone()
          orionOuterRef.current.quaternion.copy(orionOuterRestQuat(mediaQueryKey))
        }
        sg.parent.updateWorldMatrix(true, false)
        wp.applyMatrix4(sg.parent.matrixWorld)
        if (savedOuterQuat) {
          orionOuterRef.current.quaternion.copy(savedOuterQuat)
          sg.parent.updateWorldMatrix(true, false)
        }
      }
      const lao = resolveLookAtOffset(hs, mediaQueryKey)
      applyCameraOffset(wp, window.orbitControlsRef?.camera, lao)
      orionAttachmentLookAtRef.current = [wp.x, wp.y, wp.z]
    }
    setOrionFocusedAttachment(hs.id)
  }

  // Perf #5: stable per-hotspot onClick handlers so memoised SatelliteHotspot can skip
  // re-renders. focusOrionAttachment is redefined each render, so route through a ref;
  // the handler map itself only rebuilds when the hotspot list identity changes.
  const focusOrionAttachmentRef = useRef(focusOrionAttachment)
  focusOrionAttachmentRef.current = focusOrionAttachment
  const orionHotspotClickHandlers = useMemo(() => {
    const m = {}
    for (const hs of (orionLocation?.hotspots ?? [])) {
      m[hs.id] = () => focusOrionAttachmentRef.current(hs)
    }
    return m
  }, [orionLocation])

  // Focus an SLS touch point. The look-at is the attachment group's live world
  // position (the origin the marker points to); seed it now so the entry transition
  // aims at the exact point SlsAttachmentLookAtTracker then holds.
  const focusSlsAttachment = (hs) => {
    if (!hs) return
    trackEvent('sls_attachment_clicked', { hotspot_id: hs.id })
    const target = slsAttachmentRefs[hs.attachmentKey]?.current
    if (target) {
      // Seed from the REST-pose position (where the part lands after the model
      // springs back from any drag rotation), so the entry transition aims at the
      // exact point the tracker then holds — no snap when the model settles.
      const wp = new Vector3()
      // Base tilt + the per-part world-Y spin (rotation[1] in degrees), matching the
      // controller's rest pose — so the seed lands exactly where the model settles.
      const spinRad = hs.rotation ? hs.rotation[1] * (Math.PI / 180) : 0
      slsAttachmentRestWorldPos(wp, target, slsModelRef.current, slsInitialRotation, spinRad)
      const lao = resolveLookAtOffset(hs, mediaQueryKey)
      applyCameraOffset(wp, window.orbitControlsRef?.camera, lao)
      slsAttachmentLookAtRef.current = [wp.x, wp.y, wp.z]
    }
    playSound('TrackVehicle')
    setSlsFocusedHotspot(hs.id)
  }
  const focusSlsAttachmentRef = useRef(focusSlsAttachment)
  focusSlsAttachmentRef.current = focusSlsAttachment
  const slsHotspotClickHandlers = useMemo(() => {
    const m = {}
    for (const hs of (slsLocation?.hotspots ?? [])) {
      m[hs.id] = () => focusSlsAttachmentRef.current(hs)
    }
    return m
  }, [slsLocation])

  // The currently-focused SLS hotspot object (for the camera config + panel).
  const slsActiveHotspot = slsFocusedHotspot
    ? slsLocation?.hotspots?.find(h => h.id === slsFocusedHotspot)
    : null
  // SLS per-touch-point spin: each hotspot's `rotation` in locations.json is
  // [x, y, z] DEGREES, but only the Y value is used — it spins the SLS around the
  // SAME world-Y axis the user can drag (the model's tilt/base pose stays fixed),
  // so focusing a part rotates the rocket to face it without moving the camera.
  // X/Z are ignored. The controller premultiplies this onto the base rest pose.
  const slsActiveSpinRad = slsFocusedHotspot && slsActiveHotspot?.rotation
    ? slsActiveHotspot.rotation[1] * (Math.PI / 180)
    : 0
  // Cycle through the SLS touch points (NEXT / PREVIOUS in the contextual panel).
  // SLS has no sub-sections — all hotspots are one list, so cycling wraps the whole
  // set. Mirrors cycleOrionAttachment.
  const slsCycleHotspots = slsLocation?.hotspots ?? []
  const cycleSlsAttachment = (dir) => {
    if (slsCycleHotspots.length < 2) return
    const idx = slsCycleHotspots.findIndex(h => h.id === slsFocusedHotspot)
    const next = (idx + dir + slsCycleHotspots.length) % slsCycleHotspots.length
    focusSlsAttachment(slsCycleHotspots[next])
  }

  // Focus an Orion section (level 2) from a deployedLabels entry. Mirrors the
  // section-label onClick in OrionDeployedScene so the contextual section-switcher
  // buttons drop into a section identically (pivot/lookAt/flip + outer snap). Used
  // when switching directly between sections without going back to the deployed view.
  const focusOrionSection = (label) => {
    if (!label || orionFocusedSection === label.id) return
    trackEvent(`orion_section_${label.id}`)
    const groupRef = { icsp: orionIcspRef, esm: orionEsmCtrlRef, integrity: orionIntCtrlRef }[label.groupRef]
    const piv = label.spinPivot ?? [0, 0, 0]
    const po  = label.spinPivotOffset ?? [0, 0, 0]
    const wp  = new Vector3(piv[0] + po[0], piv[1] + po[1], piv[2] + po[2])
    if (groupRef?.current) {
      let savedOuterQuat = null
      if (orionOuterRef.current) {
        savedOuterQuat = orionOuterRef.current.quaternion.clone()
        orionOuterRef.current.quaternion.copy(orionOuterRestQuat(mediaQueryKey))
      }
      // Read from the SETTLED separation z, not the section's live z (which
      // depends on how far the separation ease had progressed / whether it was
      // skipped while another section was focused). This makes the captured
      // look-at identical on first visit and on return from another section.
      const settledZ = ORION_SECTION_SETTLED_Z[label.id]
      const savedZ = groupRef.current.position.z
      if (settledZ !== undefined) groupRef.current.position.z = settledZ
      groupRef.current.updateWorldMatrix(true, false)
      wp.applyMatrix4(groupRef.current.matrixWorld)
      groupRef.current.position.z = savedZ
      if (savedOuterQuat) orionOuterRef.current.quaternion.copy(savedOuterQuat)
    }
    const camCfg = resolveLabelCameraConfig(label, mediaQueryKey)
    const off = camCfg?.lookAtOffset ?? [0, 0, 0]
    orionSectionLookAtRef.current = [wp.x + off[0], wp.y + off[1], wp.z + off[2]]
    if (groupRef?.current) {
      _flipEulerTmp.setFromQuaternion(groupRef.current.quaternion, 'YXZ')
      setOrionAttachmentLabelsFlipped(computeYFlip(_flipEulerTmp.y, label.flipBand ?? DEFAULT_FLIP_BAND))
    }
    // Switching sections from a focused attachment: drop back to level 2 first.
    orionAttachmentLookAtRef.current = null
    setOrionFocusedAttachment(null)
    orionOuterSnapToRestRef.current = true
    setOrionFocusedSection(label.id)
  }

  // Attachment cycling — step through the focused section's points via the
  // level-3 detail panel's prev/next buttons.
  const orionSectionHotspots = orionFocusedSection
    ? (orionLocation?.hotspots ?? []).filter(h => (
        h.attachmentKey?.startsWith('ESM') ? 'esm'
          : h.attachmentKey?.startsWith('ICPS') ? 'icps'
          : h.attachmentKey?.startsWith('Int') ? 'integrity'
          : null
      ) === orionFocusedSection)
    : []
  const cycleOrionAttachment = (dir) => {
    if (!orionSectionHotspots.length) return
    const idx = orionSectionHotspots.findIndex(h => h.id === orionFocusedAttachment)
    const next = (idx + dir + orionSectionHotspots.length) % orionSectionHotspots.length
    focusOrionAttachment(orionSectionHotspots[next])
  }

  // Mobile portrait: swipe up = next component, down = previous — mirrors the
  // NEXT/PREVIOUS buttons. Active only when a cyclable component is focused (same
  // gating the inline cycle buttons use). Routes to the right cycle fn by context.
  const orionComponentCycleActive = focusedLocation?.id === 'orion' && orionDeployed && orionFocusedAttachment && orionActiveAttachment && orionSectionHotspots.length > 1
  const slsComponentCycleActive = focusedLocation?.id === 'sls' && slsFocusedHotspot && slsCycleHotspots.length > 1
  const canSwipeComponents = mediaQueryKey === 'mobilePortrait' && !showJoinMission && (orionComponentCycleActive || slsComponentCycleActive)
  // Carousel-dot data for the focused-component panel: the active list + the
  // current index, resolved per focus context. Drives the vertical dot strip to
  // the right of the info box (mobile portrait).
  const componentCarouselList = orionComponentCycleActive ? orionSectionHotspots
    : slsComponentCycleActive ? slsCycleHotspots
    : []
  const componentCarouselIndex = orionComponentCycleActive
    ? componentCarouselList.findIndex(h => h.id === orionFocusedAttachment)
    : slsComponentCycleActive
    ? componentCarouselList.findIndex(h => h.id === slsFocusedHotspot)
    : -1
  const cycleFocusedComponent = (dir) => {
    playSound('PlotCourse')
    if (orionComponentCycleActive) cycleOrionAttachment(dir)
    else if (slsComponentCycleActive) cycleSlsAttachment(dir)
  }
  // Mobile portrait: LIVE drag on the info panel. As the user drags up/down the
  // panel follows the finger in real time (imperative translate on the box, no
  // per-frame re-render); on release, past the threshold it commits next/prev
  // (swipe up = next, down = prev), otherwise it springs back. Dragging OVER the
  // text box works too — the box is the drag surface — so it no longer blocks the
  // gesture. Buttons are still excluded so NEXT/PREV/BACK taps keep working.
  const COMPONENT_SWIPE_COMMIT = 60   // px past which release commits a cycle
  const COMPONENT_SWIPE_RESIST = 0.35 // drag-follow damping (box moves a fraction of finger)
  const COMPONENT_SWIPE_MAX = 10      // px — max the box travels; just a small hint, not a big move
  const componentDragRef = useRef(null) // { startX, startY, box, active }
  // Find the currently-visible attachment panel box (Orion or SLS).
  const getAttachmentBox = () => document.querySelector('.orion-attachment-panel .dsl-small-window')
  const setBoxTranslate = (box, y, withTransition) => {
    if (!box) return
    box.style.transition = withTransition ? 'translate 0.22s ease-out' : 'none'
    box.style.translate = y ? `0 ${y}px` : '0 0'
  }
  const handleComponentSwipeStart = (e) => {
    if (!canSwipeComponents) return
    // Exclude only genuine tap targets (buttons/links/inputs) so NEXT/PREV/BACK and
    // form controls still work; the text box / panel body IS a valid drag surface.
    if (e.target.closest?.('button, a, input, textarea, select')) return
    const t = e.touches[0]
    componentDragRef.current = { startX: t.clientX, startY: t.clientY, box: getAttachmentBox(), active: false }
  }
  const handleComponentSwipeMove = (e) => {
    const d = componentDragRef.current
    if (!d) return
    const t = e.touches[0]
    const dx = t.clientX - d.startX
    const dy = t.clientY - d.startY
    // Lock in as a vertical drag once it's clearly vertical-dominant past a small
    // deadzone; a horizontal-dominant move cancels (leaves scroll/other gestures).
    if (!d.active) {
      if (Math.abs(dy) < 8 && Math.abs(dx) < 8) return
      if (Math.abs(dy) <= Math.abs(dx)) { componentDragRef.current = null; return }
      d.active = true
    }
    // Damp then clamp so the box only shifts a little — a hint, not a full follow.
    const damped = dy * COMPONENT_SWIPE_RESIST
    const clamped = Math.max(-COMPONENT_SWIPE_MAX, Math.min(COMPONENT_SWIPE_MAX, damped))
    setBoxTranslate(d.box, clamped, false)
  }
  const handleComponentSwipeEnd = (e) => {
    const d = componentDragRef.current
    componentDragRef.current = null
    if (!d) return
    if (d.active) setBoxTranslate(d.box, 0, true) // spring the box back to rest
    if (!d.active || !canSwipeComponents) return
    const t = e.changedTouches[0]
    const dy = t.clientY - d.startY
    if (Math.abs(dy) < COMPONENT_SWIPE_COMMIT) return
    cycleFocusedComponent(dy < 0 ? 1 : -1)
  }

  // Mobile portrait: focusing a section skips the label-picking step and drops
  // straight into the first attachment (Level 3), navigated via prev/next. The
  // per-section labels are hidden on mobile, so without this a focused section
  // would show nothing. Desktop/tablet keep the label-picking behaviour.
  useEffect(() => {
    if (mediaQueryKey !== 'mobilePortrait') return
    if (!orionDeployed || !orionFocusedSection || orionFocusedAttachment) return
    const first = (orionLocation?.hotspots ?? []).find(h => (
      h.attachmentKey?.startsWith('ESM') ? 'esm'
        : h.attachmentKey?.startsWith('ICPS') ? 'icps'
        : h.attachmentKey?.startsWith('Int') ? 'integrity'
        : null
    ) === orionFocusedSection)
    if (first) focusOrionAttachmentRef.current(first)
  }, [mediaQueryKey, orionDeployed, orionFocusedSection, orionFocusedAttachment, orionLocation])

  // Navigation window — the bottom-right slot. On desktop the back/nav action
  // lives inside a chamfered DSL_SmallWindow (same pattern as the Artemis logo /
  // JOIN windows); on tablet/mobile it stays the bare edge-pinned button.
  // Bottom-right NAVIGATION window. BACK (context) and HOME live in the SAME
  // container — both are navigation. HOME shows everywhere EXCEPT the home screen
  // (the beginning); the context action (BACK / ABORT …) appears above it when one
  // applies. If neither applies (i.e. on the home screen) the window doesn't render.
  const renderNavWindow = (contextLabel, contextOnClick, showHome) => {
    // Mobile portrait: in the SLS/Orion base view, the SLS↔ORION cross-nav button
    // (its own CONTEXT panel on other tiers) moves INTO this NAV panel and the
    // separate CONTEXT panel is hidden. Compute it here so it renders as a NAV
    // action alongside BACK/HOME.
    const showInlineCrossNav = mediaQueryKey === 'mobilePortrait' && !showJoinMission &&
      ((focusedLocation?.id === 'sls' && !slsFocusedHotspot) ||
       (focusedLocation?.id === 'orion' && !orionFocusedSection && !orionFocusedAttachment))
    const crossNavToOrion = focusedLocation?.id === 'sls'
    const crossNavTarget = crossNavToOrion ? orionLocation : slsLocation
    const crossNavLabel = crossNavToOrion ? 'ORION' : 'SLS'
    // Mobile portrait: in a component area (Orion attachment / SLS touch point with
    // siblings) the NEXT/PREVIOUS cycle buttons move INTO this NAV panel as square
    // arrow buttons — PREV at the far left, NEXT at the far right — and the separate
    // mobile-drilldown-actions panel is hidden.
    const orionCycle = focusedLocation?.id === 'orion' && orionDeployed && orionFocusedAttachment && orionActiveAttachment && orionSectionHotspots.length > 1
    const slsCycle = focusedLocation?.id === 'sls' && slsFocusedHotspot && slsCycleHotspots.length > 1
    const showInlineCycle = mediaQueryKey === 'mobilePortrait' && !showJoinMission && (orionCycle || slsCycle)
    const cycle = (dir) => {
      playSound('PlotCourse')
      if (orionCycle) cycleOrionAttachment(dir)
      else if (slsCycle) cycleSlsAttachment(dir)
    }
    // Mobile portrait: the "Pop-ups" (guided) picker is a single GUIDED switch in
    // the NAV panel throughout the mission profile (stage >= 2) — before launch,
    // after launch (to the right of HOME), and while a mission-stage lightbox is
    // open (where it replaces HOME — CONTINUE + GUIDED only). Highlighted when on;
    // tapping toggles guided. (Desktop two-button popups-window is gated off mobile.)
    const missionStageLightboxOpen = mediaQueryKey === 'mobilePortrait' && !!activeLightboxMedia &&
      currentMode === nav.modes.ARTEMIS && artemisStage >= 2 && !showJoinMission
    const showGuidedSwitch = mediaQueryKey === 'mobilePortrait' && !showJoinMission &&
      currentMode === nav.modes.ARTEMIS && artemisStage >= 2
    // While the lightbox is open, only CONTINUE + GUIDED show — HOME is hidden.
    const showHomeEffective = showHome && !missionStageLightboxOpen
    if (!contextLabel && !showHomeEffective && !showInlineCrossNav && !showInlineCycle && !showGuidedSwitch) return null
    // mobilePortrait renders the real chamfered window too (bottom-dock system,
    // 2026-07-02) — the bare-fragment fallback below now only serves
    // mobileLandscape / tabletPortrait (both rotate-prompt tiers).
    const isDesktopBp = ['mobilePortrait', 'tabletLandscape', 'desktop1080', 'desktop1440', 'desktop4k'].includes(mediaQueryKey)
    const actions = (
      <>
        {showInlineCycle && (
          <button className="rect_button-alt nav-window-action nav-window-cycle nav-window-cycle--prev" onClick={() => cycle(-1)} aria-label="Previous">
            <span aria-hidden="true">&#8592;</span>
          </button>
        )}
        {contextLabel && (
          <button className="rect_button-alt rect_button-back rect_button-back--context nav-window-action" onClick={contextOnClick}>
            <span>{contextLabel}</span>
          </button>
        )}
        {showHomeEffective && (
          <button className="rect_button-alt rect_button-back rect_button-back--home nav-window-action" onClick={handleHome}>
            <span>HOME</span>
          </button>
        )}
        {showInlineCrossNav && (
          <button
            /* Keyed by target so the SLS↔ORION flip mounts a FRESH button node.
               The node otherwise survives the switch (only the label changes) and
               keeps the browser's sticky post-tap :hover — touchscreens that
               report hover:hover / pointer:fine bypass the CSS media-query guard,
               leaving the button stuck in its hovered fill until the next tap. */
            key={crossNavLabel}
            className="rect_button-alt nav-window-action rect_button-highlight"
            onClick={() => { playSound('TrackVehicle'); trackEvent(crossNavToOrion ? 'hotspot_orion' : 'hotspot_sls'); handleFocusLocation(crossNavTarget) }}
          >
            <span>{crossNavLabel}</span>
          </button>
        )}
        {showInlineCycle && (
          <button className="rect_button-alt nav-window-action nav-window-cycle nav-window-cycle--next" onClick={() => cycle(1)} aria-label="Next">
            <span aria-hidden="true">&#8594;</span>
          </button>
        )}
        {showGuidedSwitch && (
          <button
            type="button"
            className={`rect_button-alt nav-window-action nav-window-guided${artemisGuidedMode ? ' nav-window-guided--on' : ''}`}
            aria-pressed={artemisGuidedMode}
            onClick={() => {
              playSound('LaunchMission')
              const next = !artemisGuidedMode
              trackEvent('artemis_mission_mode_selected', { mode: next ? 'guided' : 'auto' })
              setArtemisGuidedMode(next)
            }}
          >
            <span>{artemisGuidedMode ? 'GUIDED' : 'UNGUIDED'}</span>
          </button>
        )}
      </>
    )
    if (isDesktopBp) {
      return (
        <DSL_SmallWindow
          isTransitioning={isTransitioning}
          hideCloseButton={true}
          allowButtonClicks={true}
          noBackdrop={true}
          chamferScale={0.6}
          containerClassName="nav-window"
        >
          {actions}
        </DSL_SmallWindow>
      )
    }
    return actions
  }

  // The single context back action for the nav window, derived from current
  // scene. null = the BACK would just go to the home screen (the initial load
  // view), so HOME alone covers it — only a BACK that navigates WITHIN a section
  // (Orion drill-out levels, mission abort) returns an action here.
  const getNavContext = () => {
    // Mobile portrait, mission-stage lightbox open: the CLOSE / CONTINUE button
    // moves INTO the NAV panel (the lightbox's own footer button is hidden — see
    // its hideCloseButton). CONTINUE while guided + launched (advances the mission),
    // else CLOSE. Desktop/tablet keep the in-lightbox footer button.
    if (mediaQueryKey === 'mobilePortrait' && activeLightboxMedia && !showJoinMission) {
      const isContinue = artemisGuidedMode && artemisMissionLaunched
      return { label: isContinue ? 'CONTINUE' : 'CLOSE', onClick: handleLightboxClose }
    }
    // JOIN US open: BACK closes just the JOIN US window (whatever's behind it —
    // crew, a location, the home screen — stays as it was).
    if (showJoinMission) {
      return { label: 'BACK', onClick: handleCloseWindow }
    }
    // SLS touch point focused: BACK returns to the SLS overview (clears the focus
    // so the camera/panel revert and the markers come back). Stays WITHIN SLS.
    if (focusedLocation?.id === 'sls' && slsFocusedHotspot) {
      return { label: 'BACK', onClick: () => {
        playSound('HideCourse')
        trackEvent('back_button', { context: 'sls_attachment' })
        slsAttachmentLookAtRef.current = null
        setSlsFocusedHotspot(null)
      } }
    }
    // Orion deployed drill-out: BACK steps up one level (attachment → section →
    // deployed → focused). These stay WITHIN Orion, so BACK is shown.
    if (focusedLocation?.id === 'orion' && orionDeployed) {
      return { label: 'BACK', onClick: () => {
        playSound('HideCourse')
        if (orionFocusedAttachment) {
          trackEvent('back_button', { context: 'orion_attachment' })
          orionAttachmentLookAtRef.current = null
          setOrionFocusedAttachment(null)
          if (mediaQueryKey === 'mobilePortrait') {
            orionSectionLookAtRef.current = null
            setOrionSectionLabelsFlipped(computeYFlip(0))
            setOrionFocusedSection(null)
          }
        } else if (orionFocusedSection) {
          trackEvent('back_button', { context: 'orion_section' })
          orionSectionLookAtRef.current = null
          setOrionSectionLabelsFlipped(computeYFlip(0))
          setOrionFocusedSection(null)
        } else {
          trackEvent('back_button', { context: 'orion_deployed' })
          setOrionDetailsVisible(false)
          setOrionReverseComplete(!orionSnapComplete)
          setOrionDeployed(false)
        }
      } }
    }
    // Artemis Scene 3 (launched) → ABORT / RESET. Goes back WITHIN the Artemis
    // section (not to the app's home screen), so it's shown.
    if (currentMode === nav.modes.ARTEMIS && artemisMissionLaunched) {
      return { label: artemisComplete ? 'RESET' : 'ABORT', onClick: () => {
        playSound('AbandonMission')
        trackEvent('artemis_abandon_mission', { mission_complete: artemisComplete })
        handleArtemisReset()
      } }
    }
    // Everything else whose BACK lands on the home screen (top-level location
    // focus, Scene-2 trajectory cancel) → no BACK; HOME covers it.
    return null
  }

  return (
    <div style={{ width: '100%', height: '100%' }} onTouchStart={handleComponentSwipeStart} onTouchMove={handleComponentSwipeMove} onTouchEnd={handleComponentSwipeEnd}>
      <HUDFrame
            onLeftButtonClick={handleLeftButtonClick}
            onRightButtonClick={handleRightButtonClick}
            isTransitioning={isTransitioning}
            showLeftButton={showLeftButton}
            showRightButton={showRightButton}
            currentMode={currentMode}
            showJoinMission={showJoinMission}
            onArtemisClick={handleArtemisClick}
            showArtemisButton={showArtemisButton}
            isArtemisModeActive={isArtemisModeActive}
            audioMuted={audioMuted}
            onToggleMute={handleToggleMute}
            showMuteButton={sceneReady && !deviceInfo.isMobile}
          />
          
          <div className="starcade-landscape-message">
            PLEASE ROTATE TO PORTRAIT MODE
          </div>
          <div className="tablet-portrait-message">
            PLEASE ROTATE TO LANDSCAPE MODE
          </div>
          
          <ScreenOverlay isActive={showOverlay} />
          {/* DSL logo is persistent — it must stay visible while tracking Orion
              (follow mode) too, so it is not gated on followModeActive. On mobile
              portrait it's the bottom-centre "powered by Dark Star Labs" block
              (styled like tablet), shown in every state. */}
          <Logo />

          {/* Artemis panel (logo + CREW + JOIN US + MUTE) — persistent across the
              whole app, including the crew page. */}
          {(() => {
            // On mobile portrait, hide the OPS panel when the crew screen is open,
            // a location is focused (SLS / Orion / etc.), OR we're in the Artemis
            // mission profile (SIMULATE, stage >= 2) — those screens show only their
            // own NAV (+ CONTEXT) dock panels, which then sit at the OPS height.
            // Desktop still keeps OPS in all these states.
            const inMissionProfile = currentMode === nav.modes.ARTEMIS && artemisStage >= 2
            if (mediaQueryKey === 'mobilePortrait' && (showCrewProfile || focusedLocation || inMissionProfile)) return null
            // mobilePortrait gets the real chamfered OPS window (bottom-dock
            // system, 2026-07-02); the bare-markup branch below now only serves
            // mobileLandscape / tabletPortrait.
            const isDesktopBp = ['mobilePortrait', 'tabletLandscape', 'desktop1080', 'desktop1440', 'desktop4k'].includes(mediaQueryKey)
            const logoStackInner = (
              <>
                <img src="/images/artemis-color.svg" alt="Artemis" />
                <div className="artemis-logo-actions">
                  <button
                    type="button"
                    className={`artemis-logo-action artemis-logo-crew${showCrewProfile ? ' is-active' : ''}`}
                    onClick={() => {
                      playSound('JoinOurMission_Open')
                      trackEvent('crew_profile_open')
                      if (showJoinMission) handleCloseWindow()
                      setShowCrewProfile(true)
                    }}
                  >
                    <span>CREW</span>
                  </button>
                  <button
                    type="button"
                    className={`artemis-logo-action artemis-logo-join${showJoinMission ? ' is-active' : ''}`}
                    onClick={() => { playSound('JoinOurMission_Open'); handleOpenWindow() }}
                  >
                    <span>JOIN US</span>
                  </button>
                  <button
                    type="button"
                    className={`artemis-logo-action artemis-logo-mute${audioMuted ? ' muted' : ''}`}
                    onClick={(e) => { e.stopPropagation(); handleToggleMute() }}
                    title={audioMuted ? 'Unmute' : 'Mute'}
                  >
                    {audioMuted ? (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                        <line x1="23" y1="9" x2="17" y2="15" />
                        <line x1="17" y1="9" x2="23" y2="15" />
                      </svg>
                    ) : (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                        <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
                        <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
                      </svg>
                    )}
                    <span>MUTE</span>
                  </button>
                </div>
              </>
            )

            // Desktop: render the stack inside a DSL_SmallWindow so the frame
            // chamfer matches the Mission Objective / Orion panels exactly.
            if (isDesktopBp) {
              // Match the Mission Objective window's chamfer exactly so all
              // DSL_SmallWindow corners read identically. It passes a flat 0.6;
              // this prop is what makes 1080/1440/4K match (no per-tier --sw-*
              // overrides on these windows at 1920+).
              const logoChamfer = 0.6
              return (
                <DSL_SmallWindow
                  isTransitioning={isTransitioning}
                  hideCloseButton={true}
                  allowButtonClicks={true}
                  noBackdrop={true}
                  chamferScale={logoChamfer}
                  containerClassName={`artemis-logo-window${mediaQueryKey === 'mobilePortrait' && showJoinMission ? ' mobile-portrait-logo-hidden' : ''}`}
                >
                  {logoStackInner}
                </DSL_SmallWindow>
              )
            }

            // Tablet / mobile: keep the original bare/edge-pinned markup.
            return (
              <div className={`artemis-logo-top-right artemis-logo-stack${mediaQueryKey === 'mobilePortrait' && showJoinMission ? ' mobile-portrait-logo-hidden' : ''}`}>
                {logoStackInner}
              </div>
            )
          })()}

          {!showJoinMission && !showCrewProfile && !(mediaQueryKey === 'mobilePortrait' && !(currentMode === nav.modes.MAIN || (currentMode === nav.modes.ARTEMIS && artemisStage === 1) || (currentMode === nav.modes.ARTEMIS && artemisStage >= 2 && !artemisMissionLaunched))) && (() => {
            let s = focusedLocation ? (nav.locations[focusedLocation.id] ?? { text: focusedLocation.name }) : currentSection
            const isStarcade = currentMode === nav.modes.STARCADE
            const isArtemis = currentMode === nav.modes.ARTEMIS
            if (isArtemis && artemisStage >= 2) {
              // Post-launch: hide the heading, show the mission log in its place
              if (artemisMissionLaunched) return null
              s = { text: 'Mission Profile' }
            }
            if (focusedLocation?.id === 'orion' && orionDeployed && orionFocusedSection) {
              const sectionLabel = orionLocation?.deployedLabels?.find(l => l.id === orionFocusedSection)
              if (sectionLabel) {
                // ICPS's full name is too long for the mobile subheading, so abbreviate
                // just that section there; ESM/OCM keep their full words.
                const useShort = mediaQueryKey === 'mobilePortrait' && sectionLabel.id === 'icps' && sectionLabel.mobileText
                s = { text: useShort ? sectionLabel.mobileText : sectionLabel.text }
              }
            }
            // Subtitle under the main heading. Shown on the Artemis intro, and on the
            // SLS / Orion location screens to prompt interaction. On Orion it switches
            // to a "select a module" prompt once the modules have been deployed.
            let subtitleText = null
            if (isArtemis && artemisStage === 1 && !focusedLocation) {
              subtitleText = 'Explore the technology, science, and crew behind the Artemis II mission.'
            } else if (isArtemis && artemisStage >= 2 && !artemisMissionLaunched) {
              // Mobile has no clickable POIs — prompt to launch instead of "click the POIs".
              subtitleText = mediaQueryKey === 'mobilePortrait'
                ? 'Press launch mission to follow the Artemis II mission and discover its key stages.'
                : "Click the POI's to learn about the stages and highlights from the mission."
            } else if (focusedLocation?.id === 'sls') {
              if (!slsFocusedHotspot) {
                // Whole SLS in view — can be dragged.
                subtitleText = 'Drag to spin the spacecraft'
              } else if (slsCycleHotspots.length > 1) {
                // Inside a component (model locked, can't drag) with siblings to
                // cycle — same prompt as Orion's in-component state, matching when
                // the NEXT / PREVIOUS panel is shown.
                subtitleText = 'Use the next and previous buttons to explore more components'
              }
            } else if (focusedLocation?.id === 'orion') {
              if (!orionFocusedSection) {
                // No module selected yet — prompt to spin/deploy, or pick a module.
                subtitleText = orionDeployed
                  ? 'Select a module to learn more'
                  : 'Drag to spin the spacecraft, or press deploy to see its individual components'
              } else if (!orionFocusedAttachment) {
                // Inside a module, no component picked yet.
                subtitleText = 'Select a component to learn more'
              } else if (orionSectionHotspots.length > 1) {
                // A component is selected and siblings exist — prompt to cycle
                // through them (matches when the NEXT / PREVIOUS panel is shown).
                subtitleText = 'Use the next and previous buttons to explore more components'
              }
            }
            return (
              <div className="dsl-heading-group">
                <Subheading
                  text={s.text}
                  size={s.size}
                  imageSrc={isStarcade ? '/logos/STARCADE Logo Light and Orange.svg' : null}
                  className={isArtemis ? 'dsl-heading--artemis' : ''}
                />
                {subtitleText && (
                  <p className="dsl-subheading">{subtitleText}</p>
                )}
              </div>
            )
          })()}

          {/* Mission Log — DOS-style scrolling event log, replaces heading post-launch */}
          {artemisMissionLaunched && missionStagesVisible && artemisLaunchChosen && (
            <MissionLog
              missionStages={missionStagesConfig.missionStages}
              artemisProgress={artemisProgress}
            />
          )}

          {!showJoinMission && currentMode === nav.modes.STARCADE && (
            <p className="mode-subtitle">"We build branded mini-games because the most effective way to understand a complex mission is to fly it yourself."</p>
          )}


          {!showJoinMission && currentMode === nav.modes.STARCADE && (
            <CyclingText screen="starcade" />
          )}
          {!showJoinMission && currentMode === nav.modes.PORTFOLIO && (
            <CyclingText screen="portfolio" />
          )}
          {!showJoinMission && currentMode === nav.modes.PORTFOLIO && (
            <p className="mode-subtitle">"Our interactive 3D experiences are developed in partnership with the industry's most innovative companies."</p>
          )}

      {(
      <>

      {/* Persistent NAVIGATION window — bottom-right, always present. HOME plus
          the current scene's context back action (BACK / ABORT / RESET) together
          in one container. */}
      {!showCrewProfile && (() => {
        const ctx = getNavContext()
        // The home screen = the beginning HOME returns to (Artemis Scene 1, no
        // focus / crew / join). HOME is hidden there.
        const atHome = currentMode === nav.modes.ARTEMIS && artemisStage === 1 && !artemisMissionLaunched
          && !focusedLocation && !showJoinMission
        return renderNavWindow(ctx?.label ?? null, ctx?.onClick ?? null, !atHome)
      })()}

      {/* Level 2 — module detail panel. Shown when a deployed module (ICPS / ESM /
          Orion Crew Module) is selected but no component yet. Positioned in a
          different corner per module: OCM top-left, ESM bottom-left (above the
          Powered-by logo), ICPS bottom-right at the same height as ESM. Mobile
          portrait falls through the shared .location-panel generic mobile rule
          (bottom-centred dark scrim, same as every other location panel) — no
          desktop-tier gate needed, and it was wrongly gated before 2026-07-02. */}
      {focusedLocation?.id === 'orion' && orionDeployed && orionFocusedSection && !orionFocusedAttachment && (() => {
        const sectionLabel = orionLocation?.deployedLabels?.find(l => l.id === orionFocusedSection)
        if (!sectionLabel?.description) return null
        const corner = orionFocusedSection === 'esm' ? 'panel-bottom-left panel-above-logo'
          : orionFocusedSection === 'icps' ? 'panel-right panel-bottom-right'
          : 'panel-right panel-bottom-right' // OCM — same bottom-right slot as ICPS/ESM
        return (
          <DSL_SmallWindow
            containerClassName={`location-panel orion-module-panel ${corner}${mediaQueryKey === 'mobilePortrait' && locationPanelCollapsed ? ' panel-collapsed' : ''}`}
            chamferScale={0.6}
            noBackdrop={true}
            allowButtonClicks={true}
            hideCloseButton={true}
            isTransitioning={isTransitioning}
          >
            <p className="location-panel-description">{parseHighlights(sectionLabel.description)}</p>
          </DSL_SmallWindow>
        )
      })()}

      {/* Level 3 — attachment detail panel (right). Same DSL_SmallWindow pattern as
          the satellite carousel, with prev/next cycling the focused section's points. */}
      {focusedLocation?.id === 'orion' && orionDeployed && orionFocusedAttachment && orionActiveAttachment && (
        <DSL_SmallWindow
          containerClassName="location-panel orion-attachment-panel"
          side="right"
          chamferScale={0.6}
          noBackdrop={true}
          allowButtonClicks={true}
          hideCloseButton={true}
          isTransitioning={isTransitioning}
        >
          <div className="location-panel-title-row">
            {/* Prev/next moved to the contextual nav panel (NEXT / BACK stacked). */}
            <h1 className="location-panel-title unified-panel-heading">
              {parseHighlights(orionActiveAttachment.text ?? '')}
            </h1>
          </div>
          {orionActiveAttachment.description && (
            <p className="location-panel-description unified-panel-body">{parseHighlights(orionActiveAttachment.description)}</p>
          )}
        </DSL_SmallWindow>
      )}

      {/* SLS touch-point detail panel — mirrors the Orion attachment panel: title +
          description for the focused SLS part. Reuses the same unified panel styling. */}
      {focusedLocation?.id === 'sls' && slsActiveHotspot && (
        <DSL_SmallWindow
          containerClassName="location-panel orion-attachment-panel sls-attachment-panel"
          side="right"
          chamferScale={0.6}
          noBackdrop={true}
          allowButtonClicks={true}
          hideCloseButton={true}
          isTransitioning={isTransitioning}
        >
          <div className="location-panel-title-row">
            <h1 className="location-panel-title unified-panel-heading">
              {parseHighlights(slsActiveHotspot.text ?? '')}
            </h1>
          </div>
          {slsActiveHotspot.description && (
            <p className="location-panel-description unified-panel-body">{parseHighlights(slsActiveHotspot.description)}</p>
          )}
        </DSL_SmallWindow>
      )}

      {/* Carousel dots (mobile portrait) — a vertical strip pinned to the right edge,
          beside the info box, showing how many components there are and which is
          active. Purely an indicator; does not move the info box. Tapping a dot
          jumps to that component. */}
      {mediaQueryKey === 'mobilePortrait' && componentCarouselList.length > 1 && componentCarouselIndex >= 0 && (
        <div className="component-carousel-dots" aria-hidden="true">
          {componentCarouselList.map((item, i) => (
            <button
              key={item.id}
              type="button"
              className={`component-carousel-dot${i === componentCarouselIndex ? ' is-active' : ''}`}
              onClick={() => {
                if (i === componentCarouselIndex) return
                playSound('PlotCourse')
                if (orionComponentCycleActive) focusOrionAttachment(item)
                else if (slsComponentCycleActive) focusSlsAttachment(item)
              }}
            />
          ))}
        </div>
      )}


      {/* The "i" info toggle shows ONLY when a description panel can actually pop
          up — the base location view OR a sub-component detail panel (SLS touch
          point, Orion module/attachment). It must NOT show in the Orion-deployed-
          but-no-section-selected state (no description there). Mirrors the four
          panel render conditions below. */}
      {focusedLocation && !showJoinMission && (() => {
        const isSls = focusedLocation.id === 'sls'
        const isOrion = focusedLocation.id === 'orion'
        const basePanel = currentMode === nav.modes.MAIN || (isSls && !slsFocusedHotspot) || (isOrion && !orionDeployed)
        // Module panel only renders when the section has a description (app.jsx
        // ~3161 returns null otherwise) — mirror that so the "i" tracks the box.
        const moduleHasDescription = !!orionLocation?.deployedLabels?.find(l => l.id === orionFocusedSection)?.description
        const orionModulePanel = isOrion && orionDeployed && orionFocusedSection && !orionFocusedAttachment && moduleHasDescription
        const orionAttachmentPanel = isOrion && orionDeployed && orionFocusedAttachment && orionActiveAttachment
        const slsHotspotPanel = isSls && slsActiveHotspot
        // Component view (an attachment/touch point is focused): on mobile portrait
        // the info panel auto-shows (no toggle), so the "i" button is hidden there.
        const componentView = orionAttachmentPanel || slsHotspotPanel
        if (mediaQueryKey === 'mobilePortrait' && componentView) return false
        return basePanel || orionModulePanel || orionAttachmentPanel || slsHotspotPanel
      })() && (
        <button
          className={`location-panel-toggle rect_button-alt ${locationPanelCollapsed ? 'collapsed rect_button-highlight' : 'expanded'}`}
          onClick={() => { playSound('PlotCourse'); trackEvent('location_panel_toggle', { expanded: locationPanelCollapsed }); setLocationPanelCollapsed(c => !c) }}
          aria-label={locationPanelCollapsed ? 'Show location info' : 'Hide location info'}
          aria-pressed={!locationPanelCollapsed}
        >
          {/* "i" info toggle — same style as the image description info button.
              Label/chevron kept for the non-mobile tiers (shown via CSS there). */}
          <span className="location-panel-toggle-info" aria-hidden="true">i</span>
          <span className="location-panel-toggle-label">{locationPanelCollapsed ? 'INFO' : 'HIDE'}</span>
          <span className="rect_button-chevrons location-panel-toggle-chevron" />
        </button>
      )}

      {(currentMode === nav.modes.MAIN || (focusedLocation?.id === 'sls' && !slsFocusedHotspot) || (focusedLocation?.id === 'orion' && !orionDeployed)) && focusedLocation && !showJoinMission && (
        <DSL_SmallWindow
          containerClassName={`location-panel${locationPanelCollapsed ? ' panel-collapsed' : ''}${focusedLocation?.id === 'orion' ? ' orion-pre-deploy' : ''}${(focusedLocation?.id === 'sls' || focusedLocation?.id === 'orion') ? ' panel-no-title' : ''}`}
          side={focusedLocation?.content?.panelSide ?? 'left'}
          chamferScale={0.6}
          noBackdrop={true}
          allowButtonClicks={true}
          hideCloseButton={true}
          isTransitioning={isTransitioning}
          scrollContentRef={locationPanelScrollRef}
        >
          {/* Title row — nav buttons flank title on mobile portrait */}
          <div className="location-panel-title-row">
            {/* Prev button — earth locations */}
            {focusedLocation.id !== 'satellite' && earthLocations.length > 1 && (() => {
              const idx = earthLocations.findIndex(loc => loc.id === focusedLocation.id)
              if (idx === -1) return null
              const prev = earthLocations[(idx - 1 + earthLocations.length) % earthLocations.length]
              return (
                <button className="rect_button-alt location-panel-prev" onClick={() => { playSound('PlotCourse'); handleFocusLocation(prev) }} aria-label={`Previous: ${prev.name}`}>
                  <span className="rect_button-chevrons">&lt;&lt;&lt;</span>
                </button>
              )
            })()}
            {/* Prev button — satellite carousel */}
            {focusedLocation.id === 'satellite' && satelliteModels.length > 1 && (() => {
              const prevIdx = (focusedSatelliteIndex - 1 + satelliteModels.length) % satelliteModels.length
              return (
                <button className="rect_button-alt location-panel-prev" onClick={() => { playSound('PlotCourse'); setFocusedSatelliteIndex(prevIdx) }} aria-label={`Previous: ${satelliteModels[prevIdx].name}`}>
                  <span className="rect_button-chevrons">&lt;&lt;&lt;</span>
                </button>
              )
            })()}
            {/* SLS / Orion show their name in the main top-of-screen heading
                already, so the in-panel title is omitted for those two. */}
            {focusedLocation?.id !== 'sls' && focusedLocation?.id !== 'orion' && (
              <h1 className="location-panel-title">
                {parseHighlights((focusedLocation?.id === 'satellite' ? (activeSatModel?.panelName ?? activeSatModel?.name) : null) ?? focusedLocation?.name ?? '')}
              </h1>
            )}
            {/* Next button — earth locations */}
            {focusedLocation.id !== 'satellite' && earthLocations.length > 1 && (() => {
              const idx = earthLocations.findIndex(loc => loc.id === focusedLocation.id)
              if (idx === -1) return null
              const next = earthLocations[(idx + 1) % earthLocations.length]
              return (
                <button className="rect_button-alt location-panel-next" onClick={() => { playSound('PlotCourse'); handleFocusLocation(next) }} aria-label={`Next: ${next.name}`}>
                  <span className="rect_button-chevrons">&gt;&gt;&gt;</span>
                </button>
              )
            })()}
            {/* Next button — satellite carousel */}
            {focusedLocation.id === 'satellite' && satelliteModels.length > 1 && (() => {
              const nextIdx = (focusedSatelliteIndex + 1) % satelliteModels.length
              return (
                <button className="rect_button-alt location-panel-next" onClick={() => { playSound('PlotCourse'); setFocusedSatelliteIndex(nextIdx) }} aria-label={`Next: ${satelliteModels[nextIdx].name}`}>
                  <span className="rect_button-chevrons">&gt;&gt;&gt;</span>
                </button>
              )
            })()}
          </div>
          {(() => {
            const desc = focusedLocation?.id === 'satellite'
              ? (activeSatModel?.description ?? focusedLocation?.description)
              : focusedLocation?.description
            if (!desc) return null
            if (Array.isArray(desc)) return desc.map((p, i) => <p key={i} className="location-panel-description">{parseHighlights(p)}</p>)
            return <p className="location-panel-description">{parseHighlights(desc)}</p>
          })()}
          {focusedLocation?.content?.ctaButton && (
            <button
              className="location-panel-cta"
              onClick={() => {
                playSound('PlotCourse')
                trackEvent(`launch-platforms_${focusedLocation.id}`)
                handleOpenIframe(
                  focusedLocation.content.ctaButton.url,
                  focusedLocation.content.ctaButton.label
                )
              }}
            >
              <span>{focusedLocation.content.ctaButton.label}</span>
              <span className="rect_button-chevrons">{focusedLocation.content.ctaButton.chevrons ?? '>>>'}</span>
            </button>
          )}
        </DSL_SmallWindow>
      )}

      {currentMode === nav.modes.MAIN && focusedLocation && !showJoinMission && !locationPanelCollapsed && locationPanelHasOverflow && !locationPanelScrolled && (
        <div className="location-panel-scroll-overlay">
          <div className="location-panel-scroll-gradient" />
          <div className="location-panel-scroll-arrow">&#8964;</div>
        </div>
      )}

      {currentMode === nav.modes.MAIN && focusedLocation && !showJoinMission && !locationPanelCollapsed && locationPanelHasOverflow && locationPanelScrolled && (
        <div className="location-panel-scroll-overlay location-panel-scroll-overlay--top">
          <div className="location-panel-scroll-gradient location-panel-scroll-gradient--top" />
          <div className="location-panel-scroll-arrow location-panel-scroll-arrow--top">&#8963;</div>
        </div>
      )}

      {currentMode === nav.modes.MAIN && !focusedLocation && sceneReady && !showJoinMission && !isArtemisModeActive && !showCrewProfile && (
        <DidYouKnow />
      )}

      {currentMode === nav.modes.MAIN && !focusedLocation && sceneReady && !showJoinMission && !isArtemisModeActive && (
        <CyclingText screen="home" position="bottom-left" />
      )}

      {currentMode === nav.modes.ARTEMIS && trajectoryDrawComplete && !showJoinMission && !showCrewProfile && mediaQueryKey !== 'mobilePortrait' && artemisStage >= 2 && (
        <div className={`artemis-map-key${activeLightboxMedia ? ' artemis-map-key--behind-lightbox' : ''}`}>
          <div className="artemis-map-key__item">
            <span className="artemis-map-key__dot artemis-map-key__dot--circle" />
            <span className="artemis-map-key__label">MISSION STAGE</span>
          </div>
          <div className="artemis-map-key__item">
            <span className="artemis-map-key__dot artemis-map-key__dot--diamond" />
            <span className="artemis-map-key__label">HIGHLIGHT</span>
          </div>
          <span className="artemis-map-key__description">Key points along the mission's journey</span>
        </div>
      )}

      <SatelliteInfoPanel
        model={activeSatHotspot !== null ? satelliteModels[activeSatHotspot] : null}
        visible={focusedLocation?.id === 'satellite' && activeSatHotspot !== null && !showJoinMission}
        offset={focusedLocation?.infoPanelOffset}
        onClose={() => setActiveSatHotspot(null)}
      />

      <SatelliteInfoPanel
        model={gatewayHotspotActive ? gatewaySpacestation : null}
        visible={focusedLocation?.id === 'spacestation' && gatewayHotspotActive && !showJoinMission}
        offset={gatewaySpacestation?.infoPanelOffset}
        position={gatewaySpacestation?.infoPanelPosition}
        autoCloseMs={60_000}
        onClose={() => setGatewayHotspotActive(false)}
      />

      <SatelliteInfoPanel
        model={munPanelActive ? {
          infoPanelName: 'Think you have what it takes to navigate the lunar surface?',
          info: [
            'Take the wheel in MÜN - our high-speed time trial to collect lunar samples and map out key lunar resources.',
            'We believe mini-games are the ultimate tool for engagement; they turn passive viewers into active participants, improving product and mission understanding with fun and memorable experiences.',
          ],
        } : null}
        visible={focusedLocation?.id === 'lunarops' && munPanelActive && !showJoinMission}
        position="top-left"
        offset={mediaQueryKey === 'tabletPortrait'
          ? lunarOpsLocation?.munPanelOffsetTablet
          : lunarOpsLocation?.munPanelOffset}
        thumbnail={mediaQueryKey !== 'tabletPortrait' ? "/card_images/MUN_Thumbnail_Wtext.webp" : undefined}
        ctaLabel="PLAY NOW"
        onCta={() => { trackEvent('lunarops_mun'); handleOpenIframe('https://mun.darkstarlabs.io/', 'Explore the Mün') }}
        onClose={() => setMunPanelActive(false)}
      />

      <SatelliteInfoPanel
        model={meridianPanelActive ? {
          infoPanelName: 'ECONOMIC TRANSFER VEHICLE',
          info: [
            "Dr. Sam Richards at Meridian Space Command is developing the 'Economical Transfer Vehicle' (ETV), which uses Alpha Impulsion's \"self-eating\" technology. The ETV is currently in Preliminary Design Review (PDR) funded by the UK Space Agency.",
            'The ETV would hitch a ride into LEO and then "eat" its own fuel tank to get to the moon. Lowering costs for science beyond earth orbit.',
          ],
        } : null}
        visible={focusedLocation?.id === 'lunarops' && meridianPanelActive && !showJoinMission}
        position="bottom-left"
        offset={lunarOpsLocation?.meridianPanelOffset}
        ctaLabel="OPEN EXPERIENCE"
        onCta={() => { trackEvent('lunarops_meridian-etv'); handleOpenIframe('https://meridian-space-etv.vercel.app/', "Meridian's ETV") }}
        onClose={() => setMeridianPanelActive(false)}
      />

      {currentMode === nav.modes.MAIN && !showJoinMission && isArtemisModeActive && sceneReady && (
        <>
          {/* Plot button with Artemis logo inside - centered */}
          <button
            className="rect_button-alt rect_button-artemis-plot rect_button-highlight"
            onClick={() => {
              playSound('PlotCourse')
              trackEvent('artemis_plot_course')
              handleArtemisClick()
            }}
          >
            <img 
              src="/images/artemis-filled.svg"
              alt="Artemis"
            />
            <span>{['mobilePortrait', 'mobileLandscape', 'tabletPortrait'].includes(mediaQueryKey) ? 'PLOT COURSE' : 'PLOT ARTEMIS II COURSE'}</span>
            <span className="rect_button-chevrons">&gt;&gt;&gt;</span>
          </button>
        </>
      )}
      
      {currentMode === nav.modes.ARTEMIS && !showJoinMission && (
        <>
          {/* JOIN US now lives in the top-right Artemis panel (under CREW),
              not as a standalone bottom-right button. */}
          {currentMode === nav.modes.ARTEMIS && artemisStage === 1 && !showJoinMission && !orionDeployed && orionReverseComplete && (
            <>
              {focusedLocation?.id === 'orion' ? (
                <button
                  className="rect_button-alt rect_button-artemis-plot rect_button-highlight"
                  onClick={() => { playSound('PlotCourse'); trackEvent('orion_deploy'); orionSnapToRestRef.current = true; setOrionDeployed(true) }}
                >
                  <span>DEPLOY MODULES</span>
                  <span className="rect_button-chevrons">&gt;&gt;&gt;</span>
                </button>
              ) : focusedLocation?.id !== 'sls' ? (
                <button
                  className="rect_button-alt rect_button-artemis-plot rect_button-highlight"
                  onClick={() => { handleExploreMissionProfile() }}
                >
                  <span>SIMULATE MISSION</span>
                  <span className="rect_button-chevrons">&gt;&gt;&gt;</span>
                </button>
              ) : null}
            </>
          )}

          {/* Mobile portrait, first time only: explain GUIDED vs UNGUIDED before launch */}
          {mediaQueryKey === 'mobilePortrait' && !modeIntroSeen && !artemisMissionLaunched && trajectoryDrawComplete && artemisStage >= 2 && (
            <DSL_SmallWindow
              onClose={dismissModeIntro}
              hideCloseButton={true}
              chamferScale={0.6}
              side="center"
              containerClassName="mode-intro-window"
            >
              <h1 className="sw-heading">CHOOSE YOUR MODE</h1>
              <p className="sw-body">
                <span className="sw-highlight">GUIDED</span> = The Orion will trigger mission media popups that explain the mission in more detail.
              </p>
              <p className="sw-body">
                <span className="sw-highlight">UNGUIDED</span> = The Orion fly's the mission path with no interruptions.
              </p>
              <p className="sw-body sw-body--last">
                You can swap between these settings at any point during the mission.
              </p>
              <button
                type="button"
                className="rect_button-alt rect_button-highlight mode-intro__got-it"
                onClick={dismissModeIntro}
              >
                <span>GOT IT</span>
              </button>
            </DSL_SmallWindow>
          )}

          {!artemisMissionLaunched && trajectoryDrawComplete && artemisStage >= 2 && (
            <>
              <div className="camera-instruction-text">
                {mediaQueryKey === 'mobilePortrait' ? 'TOUCH AND DRAG TO ROTATE CAMERA' : 'CLICK OR TOUCH AND DRAG TO ROTATE CAMERA'}
              </div>

              <button
                className="rect_button-alt rect_button-artemis-plot rect_button-highlight"
                onClick={() => {
                  playSound('LaunchMission')
                  trackEvent('artemis_launch_mission')
                  handleArtemisMissionLaunch()
                }}
              >
                <span>LAUNCH MISSION</span>
                <span className="rect_button-chevrons">&gt;&gt;&gt;</span>
              </button>
              
            </>
          )}
          
          {artemisMissionLaunched && (
            <>

              {mediaQueryKey !== 'mobilePortrait' && artemisProgress >= 8 && (
                <button
                  className="rect_button-alt rect_button-track-orion"
                  disabled={showMissionComplete}
                  onClick={() => {
                    if (followModeActive) {
                      playSound('HideCourse')
                      setFollowModeActive(false)
                    } else {
                      playSound('TrackVehicle')
                      trackEvent('artemis_track_orion', { follow_mode: true })
                      setFollowModeActive(true)
                    }
                  }}
                >
                  <span>{followModeActive ? 'STOP TRACKING' : 'TRACK ORION'}</span>
                </button>
              )}

            <ArtemisSeekBar
                progress={artemisProgress}
                onSeek={handleArtemisSeek}
                onSeekEnd={handleSeekBarScrub}
                missionStages={seekBarMissionStages}
                extraLayers={seekBarExtraLayers}
                visible={artemisMissionLaunched && artemisLaunchChosen && !showJoinMission && mediaQueryKey !== 'tabletPortrait'}
                paused={artemisPaused}
                onTogglePause={handleSeekBarTogglePause}
                guidedMode={artemisGuidedMode}
                onSetGuided={handleSeekBarSetGuided}
                onSetAuto={handleSeekBarSetAuto}
                onSkipBack={handleSeekBarSkipBack}
                onSkipForward={handleSeekBarSkipForward}
              />
            </>
          )}
        </>
      )}


      <ArtemisMediaLightbox
        media={activeLightboxMedia}
        layerLabel={activeLightboxLayerLabel}
        onClose={handleLightboxClose}
        guided={artemisGuidedMode && artemisMissionLaunched}
        onAutoClose={handleLightboxClose}
        isMobilePortrait={mediaQueryKey === 'mobilePortrait'}
        /* Mobile portrait: the CLOSE/CONTINUE control lives in the NAV panel
           (see getNavContext), so hide the lightbox's own footer button. */
        hideCloseButton={mediaQueryKey === 'mobilePortrait'}
      />

      {/* Contextual panel — Guided/Auto mission mode picker (two buttons, the
          active mode highlighted). The mode picker persists for the whole
          Mission Profile scene (pre- and post-launch), so mode can be changed
          mid-mission. CLOSE/CONTINUE for an open lightbox now lives in the
          shared NAVIGATION window above HOME (see getNavContext), matching
          how the Crew screen's photo lightbox is closed. Desktop only;
          mobile keeps the in-lightbox footer button. */}
      {currentMode === nav.modes.ARTEMIS && artemisStage >= 2 && !showJoinMission && mediaQueryKey !== 'mobilePortrait' && ['tabletLandscape', 'desktop1080', 'desktop1440', 'desktop4k'].includes(mediaQueryKey) && (
        <DSL_SmallWindow
          isTransitioning={isTransitioning}
          hideCloseButton={true}
          allowButtonClicks={true}
          noBackdrop={true}
          chamferScale={0.6}
          containerClassName="contextual-window popups-window"
        >
          <button
            type="button"
            className={`rect_button-alt nav-window-action${artemisGuidedMode ? ' rect_button-current' : ''}`}
            disabled={artemisGuidedMode}
            onClick={artemisGuidedMode ? undefined : () => {
              playSound('LaunchMission')
              trackEvent('artemis_mission_mode_selected', { mode: 'guided' })
              setArtemisGuidedMode(true)
            }}
          >
            <span>On</span>
          </button>
          <button
            type="button"
            className={`rect_button-alt nav-window-action${!artemisGuidedMode ? ' rect_button-current' : ''}`}
            disabled={!artemisGuidedMode}
            onClick={!artemisGuidedMode ? undefined : () => {
              playSound('LaunchMission')
              trackEvent('artemis_mission_mode_selected', { mode: 'auto' })
              setArtemisGuidedMode(false)
              // Switching to Auto mid-lightbox should drop straight back into
              // the running animation rather than leaving it open/paused.
              if (activeLightboxMedia) {
                setActiveLightboxMedia(null)
                setActiveLightboxLayerLabel(null)
                if (artemisMissionLaunched) setArtemisPaused(false)
              }
            }}
          >
            <span>Off</span>
          </button>
        </DSL_SmallWindow>
      )}

      {/* Contextual panel — cross-navigate between the two Artemis hardware
          locations. From SLS → ORION, from Orion → SLS. Highlighted (sheen/pulse)
          to draw attention to the sibling location. Only in the base location view
          (not while an Orion section/attachment sub-panel is showing). Desktop/
          tablet only — on mobile portrait this button moves into the NAV panel
          (see renderNavWindow's showInlineCrossNav) and this panel is hidden. */}
      {((focusedLocation?.id === 'sls' && !slsFocusedHotspot) || (focusedLocation?.id === 'orion' && !orionFocusedSection && !orionFocusedAttachment)) && !showJoinMission && ['tabletLandscape', 'desktop1080', 'desktop1440', 'desktop4k'].includes(mediaQueryKey) && (
        <DSL_SmallWindow
          isTransitioning={isTransitioning}
          hideCloseButton={true}
          allowButtonClicks={true}
          noBackdrop={true}
          chamferScale={0.6}
          containerClassName="contextual-window"
        >
          {(() => {
            const toOrion = focusedLocation?.id === 'sls'
            const target = toOrion ? orionLocation : slsLocation
            const label = toOrion ? 'ORION' : 'SLS'
            return (
              <button
                /* Keyed by target so the SLS↔ORION flip mounts a FRESH button
                   node — see the matching mobile cross-nav button in
                   renderNavWindow for why (sticky post-tap :hover on
                   touchscreens that report hover:hover). */
                key={label}
                className="rect_button-alt nav-window-action rect_button-highlight"
                onClick={() => { playSound('TrackVehicle'); trackEvent(toOrion ? 'hotspot_orion' : 'hotspot_sls'); handleFocusLocation(target) }}
              >
                <span>{label}</span>
              </button>
            )
          })()}
        </DSL_SmallWindow>
      )}

      {/* Contextual panel — Orion section switcher (ICPS / ESM / Integrity) shown
          while a section is focused but no attachment is selected yet (level 2).
          The current section is red + non-clickable; the others switch sections.
          Mobile portrait: bottom-right pill row (mobile-orion-switcher), above
          the bottom-left BACK/HOME stack — see 2026-07-02 session log (this was
          previously desktop/tablet-only, which made section selection — and so
          the whole ESM/ICPS/OCM attachment drill-down — unreachable on mobile). */}
      {focusedLocation?.id === 'orion' && orionDeployed && orionFocusedSection && !orionFocusedAttachment && !showJoinMission && ['tabletLandscape', 'desktop1080', 'desktop1440', 'desktop4k'].includes(mediaQueryKey) && (
        <DSL_SmallWindow
          isTransitioning={isTransitioning}
          hideCloseButton={true}
          allowButtonClicks={true}
          noBackdrop={true}
          chamferScale={0.6}
          containerClassName="contextual-window mobile-drilldown-actions"
        >
          {(orionLocation?.deployedLabels ?? []).map((label) => {
            const isCurrent = orionFocusedSection === label.id
            // Always use the abbreviation here (ICPS / ESM / OCM) — tabletText is
            // inconsistent (ESM's is the full "European Service Module").
            const lbl = ORION_SECTION_ABBR[label.id] ?? label.tabletText ?? label.text
            return (
              <button
                key={label.id}
                className={`rect_button-alt nav-window-action${isCurrent ? ' rect_button-current' : ''}`}
                disabled={isCurrent}
                onClick={isCurrent ? undefined : () => { playSound('PlotCourse'); focusOrionSection(label) }}
              >
                <span>{lbl}</span>
              </button>
            )
          })}
        </DSL_SmallWindow>
      )}

      {/* Contextual panel — Orion attachment NEXT / BACK (stacked) when viewing a
          single attachment point with siblings to cycle through. Replaces the
          prev/next that used to flank the attachment title. Desktop/tablet only —
          on mobile portrait these cycle controls move into the NAV panel as square
          ◀ ▶ arrow buttons (see renderNavWindow's showInlineCycle). */}
      {focusedLocation?.id === 'orion' && orionDeployed && orionFocusedAttachment && orionActiveAttachment && orionSectionHotspots.length > 1 && !showJoinMission && mediaQueryKey !== 'mobilePortrait' && (
        <DSL_SmallWindow
          isTransitioning={isTransitioning}
          hideCloseButton={true}
          allowButtonClicks={true}
          noBackdrop={true}
          chamferScale={0.6}
          containerClassName="contextual-window mobile-drilldown-actions"
        >
          <button className="rect_button-alt nav-window-action" onClick={() => { playSound('PlotCourse'); cycleOrionAttachment(1) }}>
            <span>NEXT</span>
          </button>
          <button className="rect_button-alt nav-window-action" onClick={() => { playSound('PlotCourse'); cycleOrionAttachment(-1) }}>
            <span>PREVIOUS</span>
          </button>
        </DSL_SmallWindow>
      )}

      {/* Contextual panel — SLS NEXT / PREVIOUS (stacked) when a touch point is
          focused, to cycle through the parts. Mirrors the Orion attachment panel.
          Desktop/tablet only — on mobile portrait these cycle controls move into
          the NAV panel as square ◀ ▶ arrow buttons (see renderNavWindow). */}
      {focusedLocation?.id === 'sls' && slsFocusedHotspot && slsCycleHotspots.length > 1 && !showJoinMission && mediaQueryKey !== 'mobilePortrait' && (
        <DSL_SmallWindow
          isTransitioning={isTransitioning}
          hideCloseButton={true}
          allowButtonClicks={true}
          noBackdrop={true}
          chamferScale={0.6}
          containerClassName="contextual-window mobile-drilldown-actions"
        >
          <button className="rect_button-alt nav-window-action" onClick={() => { playSound('PlotCourse'); cycleSlsAttachment(1) }}>
            <span>NEXT</span>
          </button>
          <button className="rect_button-alt nav-window-action" onClick={() => { playSound('PlotCourse'); cycleSlsAttachment(-1) }}>
            <span>PREVIOUS</span>
          </button>
        </DSL_SmallWindow>
      )}

      {currentMode === nav.modes.STARCADE && !showJoinMission && <DSL_Cards section="starcade" data={starcadeGames} isTransitioning={isTransitioning} onClose={handleCloseStarcade} isClosing={isClosingStarcade} scrollContainerRef={starcadeScrollRef} />}
      {currentMode === nav.modes.STARCADE && !showJoinMission && <DSL_ScrollBar scrollContainerRef={starcadeScrollRef} />}
      
      {currentMode === nav.modes.PORTFOLIO && !showJoinMission && <DSL_Cards section="portfolio" data={portfolioProjects} isTransitioning={isTransitioning} onClose={handleClosePortfolio} isClosing={isClosingPortfolio} scrollContainerRef={portfolioScrollRef} />}
      {currentMode === nav.modes.PORTFOLIO && !showJoinMission && <DSL_ScrollBar scrollContainerRef={portfolioScrollRef} />}
      
      {showJoinMission && <div className="dsl-dot-overlay" />}
      {showJoinMission && <DSL_Window onClose={handleCloseWindow} isClosing={isClosingWindow} isTransitioning={isTransitioning} scrollContentRef={windowScrollRef} />}
      {showJoinMission && <DSL_ScrollBar scrollContainerRef={windowScrollRef} />}

      {showCrewProfile && (
        <CrewProfileScreen mediaQueryKey={mediaQueryKey} onHome={handleHome} hideContextual={showJoinMission} onCloseJoinMission={handleCloseWindow} onClose={handleCloseCrewProfile} />
      )}
      </>
      )}

      {showLoader && (
        <CustomLoader 
          progress={preloadProgress}
          dataInterpolation={(p) => `${loadingMessage} ${Math.floor(p)}%`}
        />
      )}

      {/* Render-stats panel hidden for now — re-enable with `isDevMode &&` when needed. */}
      {false && <RendererStatsUI />}
      
      {/* Mute/unmute button — styled via .mute-button in style.css.
          In ARTEMIS mode the mute lives in the top-right logo stack instead,
          so hide this one there (desktop) via the mute-button--artemis class. */}
      {sceneReady && (
        <button
          className={`mute-button${audioMuted ? ' muted' : ''}${currentMode === nav.modes.ARTEMIS ? ' mute-button--artemis' : ''}`}
          onClick={(e) => { e.stopPropagation(); handleToggleMute() }}
          title={audioMuted ? 'Unmute' : 'Mute'}
        >
          {audioMuted ? (
            <svg width="100%" height="100%" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
              <line x1="23" y1="9" x2="17" y2="15" />
              <line x1="17" y1="9" x2="23" y2="15" />
            </svg>
          ) : (
            <svg width="100%" height="100%" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
              <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
              <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
            </svg>
          )}
        </button>
      )}

      {/* Ambient music — starts when scene is ready, fades in over 3 seconds */}
      <AmbientMusic
        src="/music/Artemis_Ambient-Loop.mp3"
        volume={0.4}
        fadeInDuration={3.0}
        enabled={sceneReady}
        muted={audioMuted}
      />


      <WebGPUCanvas 
        onPreloadProgress={handlePreloadProgress} 
        onSceneReady={handleSceneReady}
        onCreated={({ gl }) => setWebgpuRenderer(gl)}
        mediaQueryKey={mediaQueryKey}
      >
      {/* Tracker disabled alongside the hidden stats panel above. */}
      {false && <RendererStatsTracker />}

        <PostProcessing
          enabled={postProcessingConfig.enabled}
          bloomConfig={postProcessingConfig.bloom}
          lensflareConfig={postProcessingConfig.lensflare}
          chromaticAberrationConfig={postProcessingConfig.chromaticAberration}
          crtConfig={postProcessingConfig.crt}
        />
        
        <SunRotation
          rotationSpeed={sunRotationSpeed}
          sunDistance={sunDistance}
          sunPositionRef={sunPositionRef}
          targetRotation={artemisSunTarget ?? (focusedLocation?.sun?.reposition ? ((mediaQueryKey === 'mobilePortrait' ? focusedLocation.sun.mobileTargetRotation : null) ?? focusedLocation.sun.targetRotation ?? null) : null)}
          artemisTRef={artemisMissionLaunched ? artemisTRef : null}
          artemisStartAngle={ARTEMIS_SUN_START_ROTATION}
        />
        
        <SunSprite sunPositionRef={sunPositionRef} />

        <Lighting
          positionRef={sunPositionRef}
          shadowTargetRef={shadowTargetRef}
          shadowFrustumSize={deferredShadow.frustumSize}
          shadowNear={deferredShadow.near}
          shadowFar={deferredShadow.far}
          shadowBias={focusedLocation?.id === 'orion' ? -0.0001 : -0.001}
          ambientIntensity={focusedLocation?.ambient?.intensity ?? 0.1}
          ambientColor={focusedLocation?.ambient?.color ?? '#4db2ff'}
          showShadowHelper={false}
          shadowMapSize={mediaQueryKey === 'mobilePortrait' ? 512 : 2048}
        />

        <SpaceEnvironment active={deferredHdri > 0} intensity={deferredHdri} />
        
        <DotGridBackground visible={!!focusedLocation || currentMode === nav.modes.STARCADE || currentMode === nav.modes.PORTFOLIO} />
        
        <Starfield
          count={5000}
          radius={1000}
          rotationSpeed={starfieldRotationSpeed}
          /* Stars read too chunky on tablet — 4K's size 3 is the reference, so
             tablet renders a third of it (1); mobile portrait is half of 3 (1.5,
             per user). Other tiers keep 3. */
          size={mediaQueryKey === 'tabletLandscape' ? 1 : mediaQueryKey === 'mobilePortrait' ? 1.5 : 3}
          dense={!!focusedLocation}
          /* 4K: denser base field — the full 10000-star allocation is always
             visible (other tiers keep 5000). Uniform-driven — no sprite rebuild.
             Note this is the allocation ceiling (count * 2), so on 4K the
             focused-view dense ramp adds nothing further. */
          baseVisibleCount={mediaQueryKey === 'desktop4k' ? 10000 : 5000}
          sizeScaleRef={starSizeScaleRef}
        />
        
        <Camera
          config={activeCameraConfig}
          trajectoryEditorMode={currentMode === nav.modes.ARTEMIS && artemisStage >= 2}
          mobilePortraitLaunchMode={artemisMissionLaunched && deviceInfo.isPhone && deviceInfo.isPortrait}
          followModeActive={artemisMissionLaunched && followModeActive}
          disableMouseFollow={!!focusedLocation}
          snapToConfig={snapCameraRef}
        />
        
        {/* Mobile portrait launch camera - activates when LAUNCH pressed on mobile portrait OR follow mode toggled */}
        <MobilePortraitLaunchCamera
          isActive={artemisMissionLaunched}
          followModeActive={followModeActive}
          artemisPositionRef={artemisPositionRef}
          cameraOffset={[0.5, 1.5, 0.95]}
          lookAtOffset={[0, -0.1, 0]}
          smoothTime={0.6}
          returnPosition={currentCameraConfig.position}
          returnLookAt={currentCameraConfig.lookAt}
        />
        
        <Earth
          radius={1}
          rotation={[0, 0, -23.5 * Math.PI / 180]}
          autoRotate={true}
          rotationSpeed={earthRotationSpeed}
          textureQuality="auto"
          enableAtmosphere={true}
          opacityOverrideRef={earthOpacityOverrideRef}
          position={earthPosition}
          onLoad={() => {}}
          preloadComplete={preloadComplete}
          sunPositionRef={sunPositionRef}
          artemisActive={currentMode === nav.modes.ARTEMIS && artemisStage >= 2}
          onArtemisTargetReached={() => setEarthAtArtemisTarget(true)}
          onRotationUpdate={handleRotationUpdate}
          forceLOD={focusedLocation?.content?.type === 'launchplatform' ? (isMobile ? 'LOD_2' : 'LOD_1') : null}
          focusTargetRotation={focusedLocation?.targetEarthRotation ?? null}
          innerGroupRef={earthInnerGroupRef}
        />
        
        {(currentMode === nav.modes.MAIN || currentMode === nav.modes.ARTEMIS) && isArtemisModeActive && artemisStage === 1 && sceneReady && !focusedLocation && (
          <StaticMarker
            position={[slsScenePosition[0] + 0.15, slsScenePosition[1] - 0.1, slsScenePosition[2]]}
            dotColor="#EE3A17"
            bracketColor="#E3E3E3"
            blink={hotspotBlinkRef.current}
            text="EXPLORE SLS"
            textPosition="right"
            textColor="#E3E3E3"
            onClick={() => { playSound('TrackVehicle'); trackEvent('hotspot_sls'); handleFocusLocation(slsLocation); }}
          />
        )}

        {(currentMode === nav.modes.MAIN || currentMode === nav.modes.ARTEMIS) && isArtemisModeActive && artemisStage === 1 && sceneReady && !focusedLocation && (
          <StaticMarker
            position={[3.5 + 0.075 + mpOrionOff[0], 0.0 + 0.1 + mpOrionOff[1], 3.5 + mpOrionOff[2]]}
            dotColor="#EE3A17"
            bracketColor="#E3E3E3"
            blink={hotspotBlinkRef.current}
            text="EXPLORE ORION"
            textPosition="right"
            textColor="#E3E3E3"
            onClick={() => { playSound('TrackVehicle'); trackEvent('hotspot_orion'); handleFocusLocation(orionLocation); }}
          />
        )}

        {currentMode === nav.modes.MAIN && !isArtemisModeActive && sceneReady && !focusedLocation && !showJoinMission && (
          <>
            <SatelliteOrbit
              radius={1.5}
              segments={128}
              rotationX={Math.PI / 3}
              rotationY={0}
              rotationZ={0}
              color="#E3E3E3"
              opacity={0.33}
              dashSize={0.025}
              gapSize={0.075}
              rotationSpeed={earthRotationSpeed}
            />
            <OrbitMarker
              orbitRadius={1.5}
              angle={-Math.PI / 0.8}
              rotationX={Math.PI / 3}
              rotationZ={0}
              dotColor="#EE3A17"
              bracketColor="#E3E3E3"
              blink={hotspotBlinkRef.current}
              text="SATELLITES"
              textPosition="right"
              textColor="#E3E3E3"
              onClick={() => { playSound('TrackVehicle'); trackEvent('hotspot_satellites'); handleFocusLocation(satelliteLocation); }}
            />
            <StaticMarker
              position={[3.4, -0.155, 3.5]}
              dotColor="#EE3A17"
              bracketColor="#E3E3E3"
              blink={spacestationHotspotBlinkRef.current}
              text="SPACE STATIONS"
              textPosition="left"
              textColor="#E3E3E3"
              onClick={() => { playSound('TrackVehicle'); trackEvent('hotspot_spacestation'); handleFocusLocation(focusedLocation ? null : gatewaySpacestation); }}
            />
            <EarthSurfaceMarker
              latitude={60.25}
              longitude={-10}
              latitudeOffset={earthCoordinateOffset.latitude}
              longitudeOffset={earthCoordinateOffset.longitude}
              earthRadius={1}
              earthInnerGroupRef={earthInnerGroupRef}
              dotColor="#EE3A17"
              bracketColor="#E3E3E3"
              blink={londonHotspotBlinkRef.current}
              text="LAUNCH PLATFORMS"
              textPosition="right"
              textColor="#E3E3E3"
              onClick={() => { playSound('TrackVehicle'); trackEvent('hotspot_launch-platforms'); handleFocusLocation(focusedLocation ? null : launchPlatform); }}
            />
          </>
        )}

        {currentMode === nav.modes.MAIN && !isArtemisModeActive && sceneReady &&
          focusedLocation?.content?.type === 'launchplatform' &&
          focusedLocation?.marker && (
          <EarthSurfaceMarker
            latitude={focusedLocation.marker.latitude}
            longitude={focusedLocation.marker.longitude}
            latitudeOffset={earthCoordinateOffset.latitude}
            longitudeOffset={earthCoordinateOffset.longitude}
            earthRadius={1}
            earthInnerGroupRef={earthInnerGroupRef}
            dotColor="#EE3A17"
            bracketColor="#E3E3E3"
            blink={londonHotspotBlinkRef.current}
            text={focusedLocation.marker.text}
            variant="line"
            onClick={() => { if (focusedLocation.marker.iframeUrl) { playSound('PlotCourse'); trackEvent(`launch-platforms_${focusedLocation.id}`); handleOpenIframe(focusedLocation.marker.iframeUrl, focusedLocation.marker.iframeLabel); } }}
          />
        )}
        
        <MoonOrbit
          earthPosition={earthPosition}
          orbitRadius={moonOrbitRadius}
          orbitSpeed={earthRotationSpeed}
          isOrbiting={false}
        >
          {(position) => (
            <group position={position}>
              <Moon
                radius={0.183}
                rotation={[0, -Math.PI/1, -1.54 * Math.PI / 180]}
                autoRotate={true}
                rotationSpeed={earthRotationSpeed}
                textureQuality="auto"
                position={[0, 0, 0]}
                onLoad={() => {}}
                preloadComplete={preloadComplete}
                isOrbiting={false}
                forceLOD={focusedLocation?.id === 'lunarops' ? (isMobile ? 'LOD_2' : 'LOD_1') : null}
              />
              {currentMode === nav.modes.MAIN && !isArtemisModeActive && sceneReady && !showJoinMission && !(focusedLocation?.id === 'lunarops' && munPanelActive) && (
                <MoonSurfaceMarker
                  latitude={lunarOpsLat}
                  longitude={lunarOpsLon}
                  moonRadius={0.183}
                  moonTilt={0}
                  dotColor="#EE3A17"
                  bracketColor="#E3E3E3"
                  blink={lunarOpsHotspotBlinkRef.current}
                  text={focusedLocation?.id === 'lunarops' ? 'EXPLORE THE MÜN' : 'LUNAR OPS'}
                  variant={focusedLocation?.id === 'lunarops' ? 'line' : 'brackets'}
                  textColor={focusedLocation?.id === 'lunarops' ? '#EE3A17' : '#E3E3E3'}
                  textPosition="left"
                   onClick={() => {
                     if (focusedLocation?.id === 'lunarops') {
                       playSound('DataPoint_Open')
                       trackEvent('lunarops_hotspot_explore-the-mun')
                       if (mediaQueryKey === 'mobilePortrait') {
                         handleOpenIframe('https://mun.darkstarlabs.io/', 'Explore the Mün')
                       } else {
                         setMunPanelActive(prev => !prev)
                         setMeridianPanelActive(false)
                       }
                     } else {
                       playSound('TrackVehicle')
                       trackEvent('hotspot_lunarops')
                       handleFocusLocation(lunarOpsLocation)
                     }
                   }}
                />
              )}
              {/* Second marker - only at Lunar Ops location, floating above surface */}
              {focusedLocation?.id === 'lunarops' && !meridianPanelActive && !showJoinMission && (
                <MoonSurfaceMarker
                  latitude={-48}
                  longitude={145}
                  moonRadius={0.23}
                  moonTilt={0}
                  dotColor="#EE3A17"
                  bracketColor="#E3E3E3"
                  blink={lunarOpsHotspotBlinkRef.current}
                  text="MERIDIAN'S ETV"
                  variant="line"
                  textColor="#EE3A17"
                   onClick={() => { playSound('DataPoint_Open'); trackEvent('lunarops_hotspot_meridians-etv'); if (mediaQueryKey === 'mobilePortrait') { handleOpenIframe('https://meridian-space-etv.vercel.app/', "Meridian's ETV") } else { setMeridianPanelActive(prev => !prev); setMunPanelActive(false) } }}
                />
              )}
              {/* Meridian model - only at Lunar Ops location */}
              {focusedLocation?.id === 'lunarops' && (
                <MeridianModel
                  position={[
                    0.23 * Math.cos(-48 * Math.PI / 180) * Math.cos(145 * Math.PI / 180),
                    0.23 * Math.sin(-48 * Math.PI / 180),
                    0.23 * Math.cos(-48 * Math.PI / 180) * Math.sin(145 * Math.PI / 180)
                  ]}
                  rotation={[0, 3, 1]}
                  scale={0.008}
                />
              )}
            </group>
          )}
        </MoonOrbit>
        
        {/* Mission Objective hotspot removed — window retained for future reuse */}
        
        {showTrajectory && earthAtArtemisTarget && (
          <TrajectoryEditor
            color="#ff6b6b"
            tension={0.95}
            showGizmos={false}
            onGrowComplete={() => setTrajectoryDrawComplete(true)}
            tubeRadius={followModeActive ? 0.002 : 0.005}
          />
        )}

        {/* All markers — unified from artemisMissionStages.json */}
        {showTrajectory && earthAtArtemisTarget && (() => {
          let circleCount = 0
          return missionStagesConfig.missionStages.map((stage) => {
            if (!stage.visible) return null
            const isCircle = (stage.dot?.shape ?? 'circle') === 'circle'
            if (isCircle) circleCount++
            const stageNumber = isCircle ? circleCount : null
            const reached = artemisProgress >= stage.trajectoryPercent
            const subMarkers = (stage.subPoints ?? []).filter(p => p.visible).map(sub => {
              return (
              <MissionStageMarker
                key={sub.id}
                controlPoints={artemisControlPoints}
                trajectoryPercent={sub.trajectoryPercent}
                stageName={sub.lightbox?.title ?? sub.id}
                stageNumber={sub.label ?? null}
                stageId={sub.id}
                dotColor={sub.dot?.color ?? '#60A5FA'}
                dotShape={sub.dot?.shape ?? 'diamond'}
                reached={artemisProgress >= sub.trajectoryPercent}
                onClick={mediaQueryKey !== 'mobilePortrait' && !artemisMissionLaunched && !activeLightboxMedia ? handleMissionStageClick : null}
                isActive={false}
                launched={artemisMissionLaunched || !!activeLightboxMedia || mediaQueryKey === 'mobilePortrait'}
                visible={trajectoryDrawComplete && !showJoinMission && missionStagesVisible}
                visited={visitedStageIds.has(sub.id)}
                occluders={missionMarkerOccluders}
                isFocused={mediaQueryKey !== 'mobilePortrait' && focusedStageId === sub.id}
                focusImageSrc={mediaQueryKey === 'mobilePortrait' ? null : (sub.lightbox?.slides?.find(s => !s.type || s.type === 'image')?.src ?? null)}
                focusCaption={MISSION_FOCUS_CONTENT[sub.id]?.caption ?? null}
              />
            )})
            return [
              <MissionStageMarker
                key={stage.id}
                controlPoints={artemisControlPoints}
                trajectoryPercent={stage.trajectoryPercent}
                stageName={stage.name}
                stageNumber={stageNumber}
                stageId={stage.id}
                dotColor={stage.dot?.color ?? '#EE3A17'}
                dotShape={stage.dot?.shape ?? 'circle'}
                reached={reached}
                onClick={mediaQueryKey !== 'mobilePortrait' && !artemisMissionLaunched && !activeLightboxMedia ? handleMissionStageClick : null}
                isActive={false}
                launched={artemisMissionLaunched || !!activeLightboxMedia || mediaQueryKey === 'mobilePortrait'}
                visible={trajectoryDrawComplete && !showJoinMission && missionStagesVisible}
                visited={visitedStageIds.has(stage.id)}
                occluders={missionMarkerOccluders}
                isFocused={mediaQueryKey !== 'mobilePortrait' && focusedStageId === stage.id}
                focusImageSrc={mediaQueryKey === 'mobilePortrait' ? null : (stage.lightbox?.slides?.find(s => !s.type || s.type === 'image')?.src ?? null)}
                focusCaption={MISSION_FOCUS_CONTENT[stage.id]?.caption ?? null}
              />,
              ...subMarkers
            ]
          })
        })()}

        {showTrajectory && earthAtArtemisTarget && artemisMissionLaunched && artemisLaunchChosen && (
          <>
            <ArtemisOnPath
              controlPoints={artemisControlPoints}
              duration={120} // Duration in seconds for rocket to complete full trajectory (increase for slower, decrease for faster)
              visible={true}
              onProgressUpdate={setArtemisProgress}
              onComplete={() => setArtemisComplete(true)}
              trajectoryDrawComplete={trajectoryDrawComplete}
              onPositionUpdate={handleArtemisPositionUpdate}
              showBrackets={artemisMissionLaunched && artemisProgress >= 10}
              bracketBlinkRef={artemisBlinkRef}
              onSeekRef={artemisSeekRef}
              artemisTRef={artemisTRef}
              isPaused={artemisPaused}
              pauseAtPercents={pauseAtPercents}
              onCapsuleRef={(obj) => { orionCapsuleRef.current = obj }}
              onArmatureRef={(obj) => { orionArmatureRef.current = obj }}
              onMarkerReached={(pct) => {
                // Read guided mode from the ref (always current) — the inline
                // closure could otherwise carry a stale value into the per-frame
                // crossing check, firing the lightbox after guided was turned off.
                if (artemisGuidedModeRef.current) {
                  setArtemisPaused(true)
                  openLightboxForPercent(pct)
                } else {
                  // Auto mode: no pause/lightbox, but still log the stage pass-through
                  const marker = resolveMarkerByPercent(pct)
                  if (marker) trackEvent('artemis_stage_auto', { stage_id: marker.id })
                }
              }}
            >
              {/* Chase camera only rendered on non-mobile devices */}
              {!deviceInfo.isPhone && (
                <ArtemisChaseCamera
                  onTextureUpdate={handleChaseCameraRenderTargetUpdate}
                  followModeActive={followModeActive}
                  orionCapsuleRef={orionCapsuleRef}
                  opacityOverrideRef={earthOpacityOverrideRef}
                  starSizeScaleRef={starSizeScaleRef}
                />
              )}
            </ArtemisOnPath>


            {/* Simple plane in 3D space displaying chase camera texture - only on non-mobile */}
            {!deviceInfo.isPhone && (
              <ArtemisChaseCameraView
                renderTarget={chaseCameraRenderTarget}
                isActive={artemisMissionLaunched && artemisProgress >= 8}
                artemisProgress={artemisProgress}
                followModeActive={followModeActive}
              />
            )}
          </>
        )}
        
        {/* Satellite models - stacked vertically, scale animated by SatelliteScaleAnimator.
            Mounted only when focused on the satellite location (deferred GLB load). */}
        {satModelVisible && (
         <group position={satellitePosition}>
           {/* DslSat — index 0; starts at scale 0, scaled up by SatelliteScaleAnimator after camera zoom */}
           <group ref={satelliteModelRef} scale={[0, 0, 0]} position={[0, (satelliteModels[0]?.positionOffset?.[1] ?? -0.5), 0]}>
             <DslSatInstances>
               <DslSat scale={satelliteModels[0]?.scale ?? 0.05} />
             </DslSatInstances>
           </group>
            {/* IRUS — index 1; starts at scale 0, scaled up by SatelliteScaleAnimator after camera zoom */}
            <group ref={irusModelRef} scale={[0, 0, 0]} position={[0, (satelliteModels[1]?.positionOffset?.[1] ?? 0.5), 0]}>
              <IrusModel scale={satelliteModels[1]?.scale ?? 0.05} />
            </group>
            {/* Prospero — index 2; starts at scale 0, scaled up by SatelliteScaleAnimator after camera zoom */}
            <group ref={prosperoModelRef} scale={[0, 0, 0]} position={[0, (satelliteModels[2]?.positionOffset?.[1] ?? -1.5), 0]}>
              <Prospero scale={satelliteModels[2]?.scale ?? 0.05} />
            </group>
         </group>
        )}

        {/* SLS — own group at its own worldPosition (locations.json sls entry), visible in Artemis Scene 1 only.
            Mounted in Artemis Scene 1 or when focused on sls (deferred GLB load). */}
        {slsMounted && (
        <group ref={slsModelRef} position={slsScenePosition} visible={((isArtemisModeActive && artemisStage === 1) || focusedLocation?.id === 'sls') && !(mediaQueryKey === 'mobilePortrait' && focusedLocation?.id === 'orion')}>
          <SLSModel scale={slsScale} attachmentRefs={slsAttachmentRefs} />
        </group>
        )}


        {/* Gateway model - location spin controller handles drag, inertia, spring return.
            Mounted only in the main scene or when focused on spacestation (deferred GLB load). */}
        {gatewayMounted && (
        <group ref={locationModelRef} position={gatewayPosition}>
          <GatewayInstances visible={!isArtemisModeActive && ((currentMode === nav.modes.MAIN && !focusedLocation) || focusedLocation?.id === 'spacestation')}>
            <GatewayModel
              scale={gatewayScale}
              visible={!isArtemisModeActive && ((currentMode === nav.modes.MAIN && !focusedLocation) || focusedLocation?.id === 'spacestation')}
            />
          </GatewayInstances>
        </group>
        )}

        <LocationSpinController groupRef={locationModelRef} isFocused={focusedLocation?.id === 'spacestation'} initialRotation={gatewayRotation} />

        {/* Orion model — Artemis Scene 1, or when focused on orion (deployed scene).
            Mounted only when needed (deferred GLB load); `visible` widened to match the mount
            gate so the deployed-focus view isn't mounted-but-invisible. */}
        {orionMounted && (
        <group ref={orionOuterRef} position={orionScenePosition} visible={((isArtemisModeActive && artemisStage === 1) || focusedLocation?.id === 'orion') && !(mediaQueryKey === 'mobilePortrait' && focusedLocation?.id === 'sls')}>
          <group ref={orionModelRef} rotation={orionInitialRotation}>
            <OrionInstances castShadow receiveShadow>
              <OrionModel scale={orionScale} position={[0, 0.0, 0.05]} umbilicalRef={orionUmbilicalRef} esmCtrlRef={orionEsmCtrlRef} integrityCtrlRef={orionIntCtrlRef} icspRef={orionIcspRef} sEsmRef={orionSEsmRef} sIcpsRef={orionSIcpsRef} sIntegrityRef={orionSIntegrityRef} attachmentRefs={orionAttachmentRefs} />
            </OrionInstances>
          </group>
        </group>
        )}
        {/* Inner spin controller — free arcball when not deployed, locked (animation only) when deployed */}
        <LocationSpinController groupRef={orionModelRef} isFocused={focusedLocation?.id === 'orion'} initialRotation={orionDetailsVisible ? orionDetailsRotation : orionInitialRotation} locked={orionDetailsVisible || orionDeployed} snapToRestRef={orionSnapToRestRef} />
        {/* Outer spin controller — world-Y only, active when deployed and no single section is focused */}
        <LocationSpinController groupRef={orionOuterRef} isFocused={focusedLocation?.id === 'orion' && orionDeployed && !orionFocusedSection} initialRotation={mediaQueryKey === 'mobilePortrait' ? [0, Math.PI / 4, 0] : [0, 0, 0]} lockToWorldY snapToRestRef={orionOuterSnapToRestRef} onYFlip={setOrionSectionLabelsFlipped} flipOffsetDeg={90} />
        {/* Three-point spot rig that lights the focused module. Rendered at scene
            root and positioned each frame at the focused module's spin-pivot
            world centre (same anchor as the dev magenta SectionPivotGizmo), so it
            re-centres per module. No shadows. */}
        <OrionFocusLighting
          active={focusedLocation?.id === 'orion' && orionDeployed && !!orionFocusedSection}
          section={orionFocusedSection}
          sectionGroupRef={orionActiveSectionGroupRef}
          sectionPivot={orionActiveSectionPivot}
          config={orionLightCtl}
          helper={isDevMode && orionLightCtl.helper}
        />
        {/* Perf #1+#6: the per-section spin controllers, look-at tracker, dev gizmos, and the
            attachment/section-label hotspot maps live in OrionDeployedScene, mounted ONLY
            while focused on Orion (so ~24 <Html> hotspots don't run when not in use) and
            isolated from App's frequent re-renders. */}
        {focusedLocation?.id === 'orion' && (
          <OrionDeployedScene
            orionLocation={orionLocation} isDevMode={isDevMode} isArtemisModeActive={isArtemisModeActive} mediaQueryKey={mediaQueryKey}
            orionDeployed={orionDeployed} orionHotspotsReady={orionHotspotsReady} orionFocusedSection={orionFocusedSection} orionFocusedAttachment={orionFocusedAttachment}
            orionAttachmentLabelsFlipped={orionAttachmentLabelsFlipped} orionSectionLabelsFlipped={orionSectionLabelsFlipped}
            orionAttachmentLockYaw={orionAttachmentLockYaw} orionAttachmentLockPitch={orionAttachmentLockPitch} orionAttachmentLockRoll={orionAttachmentLockRoll}
            orionActiveAttachment={orionActiveAttachment} orionActiveSectionGroupRef={orionActiveSectionGroupRef} orionActiveSectionPivot={orionActiveSectionPivot}
            orionIcspRef={orionIcspRef} orionEsmCtrlRef={orionEsmCtrlRef} orionIntCtrlRef={orionIntCtrlRef} orionOuterRef={orionOuterRef}
            orionSIcpsRef={orionSIcpsRef} orionSEsmRef={orionSEsmRef} orionSIntegrityRef={orionSIntegrityRef}
            orionAttachmentRefs={orionAttachmentRefs} orionAttachmentLookAtRef={orionAttachmentLookAtRef} orionSectionLookAtRef={orionSectionLookAtRef} orionOuterSnapToRestRef={orionOuterSnapToRestRef}
            orionHotspotClickHandlers={orionHotspotClickHandlers}
            setOrionFocusedSection={setOrionFocusedSection} setOrionAttachmentLabelsFlipped={setOrionAttachmentLabelsFlipped}
          />
        )}
        <OrionUmbilicalAnimator umbilicalRef={orionUmbilicalRef} deployed={orionSnapComplete} />
        <OrionSeparationAnimator esmRef={orionEsmCtrlRef} intRef={orionIntCtrlRef} icspRef={orionIcspRef} deployed={orionSnapComplete} onReverseComplete={() => setOrionReverseComplete(true)} focusedSection={orionFocusedSection} />
        <OrionSectionVisibility icspRef={orionIcspRef} esmRef={orionEsmCtrlRef} intRef={orionIntCtrlRef} focusedSection={orionFocusedSection} enabled={orionDeployed} />
        <ParticleBurst originRef={orionUmbilicalRef} orientRef={orionUmbilicalRef} trigger={orionSnapComplete} direction={[0, 1, 0]} spread={0.6} particleColor="#898989" size={1} speed={0.04} count={100} lifetime={2.4} delay={0.8} originOffset={[0, -0.75, 1.1]} />
        <ParticleBurst originRef={orionIcspRef} trigger={orionSnapComplete} direction={[0, 0, -1]} spread={Math.PI / 2} minSpread={Math.PI / 3} particleColor="#868686" size={1} speed={0.08} count={400} lifetime={3.0} delay={0} originOffset={[0, 0, 0]}  />
        <ParticleBurst originRef={orionIntCtrlRef} trigger={orionSnapComplete} direction={[0, 0, 1]} spread={Math.PI / 2} minSpread={Math.PI / 3} particleColor="#868686" size={1} speed={0.08} count={400} lifetime={3.0} delay={0.8} originOffset={[0, 0, 1]}  />

        {/* Gateway hotspot — hidden on mobile portrait */}
        {gatewaySpacestation?.hotspot && mediaQueryKey !== 'mobilePortrait' && (() => {
          const hs = gatewaySpacestation.hotspot
          return (
            <SatelliteHotspot
              satelliteGroupRef={locationModelRef}
              offset={hs.offset ?? [0, 0, 0]}
              direction={hs.direction ?? 'right'}
              line={hs.line ?? {}}
              text={hs.text ?? gatewaySpacestation.name}
              visible={focusedLocation?.id === 'spacestation' && !gatewayHotspotActive}
              onClick={() => { playSound('DataPoint_Open'); trackEvent('spacestation_hotspot_explore-station'); setGatewayHotspotActive(true) }}
            />
          )
        })()}
        <LocationSpinController groupRef={satelliteModelRef} isFocused={focusedLocation?.id === 'satellite' && focusedSatelliteIndex === 0} initialRotation={satelliteModels[0]?.rotation ?? [0, 2.5, 0]} />
        <LocationSpinController groupRef={irusModelRef} isFocused={focusedLocation?.id === 'satellite' && focusedSatelliteIndex === 1} initialRotation={satelliteModels[1]?.rotation ?? [0, 0, 0]} />
        <LocationSpinController groupRef={prosperoModelRef} isFocused={focusedLocation?.id === 'satellite' && focusedSatelliteIndex === 2} initialRotation={satelliteModels[2]?.rotation ?? [0, 0, 0]} />
        <LocationSpinController groupRef={slsModelRef} isFocused={focusedLocation?.id === 'sls'} initialRotation={slsInitialRotation} extraWorldYRad={slsActiveSpinRad} locked={focusedLocation?.id === 'sls' && !!slsFocusedHotspot} lockToWorldY onYFlip={setSlsHotspotsFlipped} snapToRestRef={slsSnapToRestRef} holdRestWhenIdle />
        {isDevMode && focusedLocation?.id === 'sls' && <SlsRotationDebug groupRef={slsModelRef} />}

        {/* SLS attachment point hotspots — visible when focused on sls location */}
        {slsLocation?.hotspots?.map((hs) => {
          const ref = slsAttachmentRefs[hs.attachmentKey]
          if (!ref) return null
          const direction = slsHotspotsFlipped
            ? (hs.direction === 'right' ? 'left' : 'right')
            : hs.direction
          const isFocusedHotspot = slsFocusedHotspot === hs.id
          return (
            <SatelliteHotspot
              key={hs.id}
              satelliteGroupRef={ref}
              offset={[0, 0, 0]}
              mobileOffset={hs.mobileOffset}
              direction={direction}
              line={hs.line}
              text={mobileLabel(hs, mediaQueryKey)}
              // Overview: all markers (line + label + dot). Once a part is focused,
              // only that part stays — as a dot-only pulse at its origin (like Orion's
              // focused attachment), so the origin is marked while zoomed in.
              visible={focusedLocation?.id === 'sls' && (slsFocusedHotspot ? isFocusedHotspot : true)}
              dotOnly={!!slsFocusedHotspot}
              noMobileDrop={true}
              flipDiagOnMobile={false}
              onClick={slsHotspotClickHandlers[hs.id]}
            />
          )
        })}
        {/* Keep the camera locked onto the focused SLS touch point — at its rest-pose
            position so there's no snap when the model springs back from a drag rotation. */}
        <SlsAttachmentLookAtTracker active={!!slsFocusedHotspot} attachmentRef={slsActiveHotspot ? slsAttachmentRefs[slsActiveHotspot.attachmentKey] : null} modelRef={slsModelRef} restRotation={slsInitialRotation} extraWorldYRad={slsActiveSpinRad} lookAtRef={slsAttachmentLookAtRef} lookAtOffset={resolveLookAtOffset(slsActiveHotspot, mediaQueryKey)} />
        <SatelliteScaleAnimator targetScalesRef={targetScalesRef} />

        {/* Satellite hotspots — one per model, only visible when that model is focused */}
        {satelliteModels.map((model, i) => {
          const ref = [satelliteModelRef, irusModelRef, prosperoModelRef][i]
          const isFocused = focusedLocation?.id === 'satellite' && focusedSatelliteIndex === i
          const panelOpen = activeSatHotspot === i
          return (
            <group key={model.id}>
              {/* Primary hotspot — opens info panel (hidden on mobile portrait) */}
              {model.hotspot && mediaQueryKey !== 'mobilePortrait' && (
                <SatelliteHotspot
                  satelliteGroupRef={ref}
                  offset={model.hotspot.offset ?? [0, 0, 0]}
                  direction={model.hotspot.direction ?? 'right'}
                  line={model.hotspot.line ?? {}}
                  text={model.hotspot.text ?? model.name}
                  visible={isFocused && !panelOpen}
                  onClick={() => { playSound('DataPoint_Open'); trackEvent(`satellite_hotspot_${model.hotspot.text.toLowerCase().replace(/\s+/g, '-')}`); setActiveSatHotspot(prev => prev === i ? null : i) }}
                />
              )}
              {/* Extra hotspots (e.g. URL launchers) */}
              {model.hotspots?.map((hs, j) => (
                <SatelliteHotspot
                  key={j}
                  satelliteGroupRef={ref}
                  offset={hs.offset ?? [0, 0, 0]}
                  mobileOffset={hs.mobileOffset ?? null}
                  direction={hs.direction ?? 'right'}
                  line={hs.line ?? {}}
                  text={mobileLabel(hs, mediaQueryKey) ?? ''}
                  visible={isFocused}
                  onClick={hs.url ? () => { playSound('PlotCourse'); trackEvent(`satellite_hotspot_${hs.text.toLowerCase().replace(/\s+/g, '-')}`); handleOpenIframe(hs.url, hs.iframeLabel ?? '') } : null}
                />
              ))}
            </group>
          )
        })}
      </WebGPUCanvas>
      
      {/* Mission completion text below chase camera - only on non-mobile.
          Stays visible while a lightbox is open (the chase camera visuals hide,
          but this label + the minimize button persist). */}
      {artemisMissionLaunched && artemisProgress >= 8 && !deviceInfo.isPhone && (
        <div className="chase-camera-mission-text">
          MISSION COMPLETION /// {artemisProgress}%
        </div>
      )}
      
      {/* Mission stage tooltip - appears when marker is clicked */}
      
      {(currentMode === nav.modes.MAIN || currentMode === nav.modes.ARTEMIS) && isArtemisModeActive && artemisStage === 1 && sceneReady && !focusedLocation && !showJoinMission && ['tabletLandscape', 'desktop1080', 'desktop1440', 'desktop4k'].includes(mediaQueryKey) && (
        <DSL_SmallWindow
          isTransitioning={isTransitioning}
          hideCloseButton={true}
          allowButtonClicks={true}
          noBackdrop={true}
          chamferScale={0.6}
          containerClassName="mission-objective-panel"
        >
          <h1 className="sw-heading unified-panel-heading">
            MISSION OBJECTIVE:
          </h1>
          <p className="sw-body unified-panel-body">
            The Artemis II mission was a historic 10-day crewed lunar flyby that successfully took four astronauts farther into deep space than humanity has ever traveled before.
          </p>
          <p className="sw-body sw-body--last unified-panel-body">
            Launched on <span className="sw-highlight">April 1, 2026</span>, the milestone flight thoroughly tested the Orion spacecraft's life-support and deep-space systems in preparation for our return to the moon.
          </p>
          <button
            type="button"
            className="sw-play-mission"
            onClick={() => {
              handleExploreMissionProfile()
            }}
          >
            <span className="sw-highlight">[ Simulate mission ]</span> to learn more.
          </button>
        </DSL_SmallWindow>
      )}
      
      {showMissionComplete && !showJoinMission && (
        <DSL_SmallWindow
          onClose={handleCloseMissionComplete}
          isClosing={isClosingMissionComplete}
          isTransitioning={isTransitioning}
          hideCloseButton={true}
          allowButtonClicks={true}
          noBackdrop={true}
          containerClassName="mission-complete-panel"
        >
          <h1 className="sw-heading">
            MISSION COMPLETE
          </h1>
          <p className="sw-body sw-body--center sw-body--last">
            Follow us for more interactive missions
          </p>
          <div className="sw-qr-container">
            {mediaQueryKey === 'mobilePortrait' ? (
              /* Mobile: show the full social row (icons), same as the JOIN US menu. */
              <DSL_Socials isTransitioning={isTransitioning} />
            ) : (
              <QRCodeSVG
                value="https://www.linkedin.com/company/darkstarlabs/"
                size={isTrue4K ? 360 : is4K ? 260 : 180}
                level="H"
                fgColor="#EE3A17"
                bgColor="transparent"
              />
            )}
          </div>
          <button
            className="rect_button sw-cta-button"
            onClick={() => {
              handleCloseMissionComplete()
              handleArtemisReset()
            }}
          >
            RESTART
          </button>
        </DSL_SmallWindow>
      )}
      
    </div>
  )
}

// Perf #6: the Orion deployed sub-tree (per-section spin controllers, look-at tracker,
// dev gizmos, and the attachment/section-label hotspot maps) extracted from App so that
// App's frequent re-renders (and the rest of its large return) don't re-reconcile this
// block. It only re-renders when one of its own props changes. Mounted only while focused
// on Orion (the caller still gates it), preserving the Perf #1 behaviour.
function OrionDeployedScene(props) {
  const {
    orionLocation, isDevMode, isArtemisModeActive, mediaQueryKey,
    orionDeployed, orionHotspotsReady, orionFocusedSection, orionFocusedAttachment,
    orionAttachmentLabelsFlipped, orionSectionLabelsFlipped,
    orionAttachmentLockYaw, orionAttachmentLockPitch, orionAttachmentLockRoll,
    orionActiveAttachment, orionActiveSectionGroupRef, orionActiveSectionPivot,
    orionIcspRef, orionEsmCtrlRef, orionIntCtrlRef, orionOuterRef,
    orionSIcpsRef, orionSEsmRef, orionSIntegrityRef,
    orionAttachmentRefs, orionAttachmentLookAtRef, orionSectionLookAtRef, orionOuterSnapToRestRef,
    orionHotspotClickHandlers,
    setOrionFocusedSection, setOrionAttachmentLabelsFlipped,
  } = props
  return (<>
        {/* Per-section spin controllers — world-Y only, pivoting on each section's own centre */}
        <SectionSpinController groupRef={orionIcspRef}    isFocused={orionDeployed && orionFocusedSection === 'icps'}      pivot={orionLocation?.deployedLabels?.find(l => l.id === 'icps')?.spinPivot}      pivotOffset={orionLocation?.deployedLabels?.find(l => l.id === 'icps')?.spinPivotOffset} lockYaw={orionFocusedSection === 'icps' ? orionAttachmentLockYaw : null} lockPitch={orionFocusedSection === 'icps' ? orionAttachmentLockPitch : 0} lockRoll={orionFocusedSection === 'icps' ? orionAttachmentLockRoll : 0} locked={orionFocusedSection === 'icps' && !!orionFocusedAttachment} onYFlip={orionFocusedSection === 'icps' ? setOrionAttachmentLabelsFlipped : null} flipBand={orionLocation?.deployedLabels?.find(l => l.id === 'icps')?.flipBand ?? DEFAULT_FLIP_BAND} />
        <SectionSpinController groupRef={orionEsmCtrlRef} isFocused={orionDeployed && orionFocusedSection === 'esm'}       pivot={orionLocation?.deployedLabels?.find(l => l.id === 'esm')?.spinPivot}       pivotOffset={orionLocation?.deployedLabels?.find(l => l.id === 'esm')?.spinPivotOffset} lockYaw={orionFocusedSection === 'esm' ? orionAttachmentLockYaw : null} lockPitch={orionFocusedSection === 'esm' ? orionAttachmentLockPitch : 0} lockRoll={orionFocusedSection === 'esm' ? orionAttachmentLockRoll : 0} locked={orionFocusedSection === 'esm' && !!orionFocusedAttachment} onYFlip={orionFocusedSection === 'esm' ? setOrionAttachmentLabelsFlipped : null} flipBand={orionLocation?.deployedLabels?.find(l => l.id === 'esm')?.flipBand ?? DEFAULT_FLIP_BAND} />
        <SectionSpinController groupRef={orionIntCtrlRef} isFocused={orionDeployed && orionFocusedSection === 'integrity'} pivot={orionLocation?.deployedLabels?.find(l => l.id === 'integrity')?.spinPivot} pivotOffset={orionLocation?.deployedLabels?.find(l => l.id === 'integrity')?.spinPivotOffset} lockYaw={orionFocusedSection === 'integrity' ? orionAttachmentLockYaw : null} lockPitch={orionFocusedSection === 'integrity' ? orionAttachmentLockPitch : 0} lockRoll={orionFocusedSection === 'integrity' ? orionAttachmentLockRoll : 0} locked={orionFocusedSection === 'integrity' && !!orionFocusedAttachment} onYFlip={orionFocusedSection === 'integrity' ? setOrionAttachmentLabelsFlipped : null} flipBand={orionLocation?.deployedLabels?.find(l => l.id === 'integrity')?.flipBand ?? DEFAULT_FLIP_BAND} />
        {/* Level 3 — keep the camera locked onto the active attachment as the section rotates */}
        <OrionAttachmentLookAtTracker active={!!orionFocusedAttachment} lookAtRef={orionAttachmentLookAtRef} lookAtOffset={resolveLookAtOffset(orionActiveAttachment, mediaQueryKey)} sectionGroupRef={orionActiveSectionGroupRef} sectionPivot={orionActiveSectionPivot} />
        {/* Dev-only pivot gizmos at each section's spin centre (spinPivot + spinPivotOffset) */}
        {isDevMode && isArtemisModeActive && ['icps', 'esm', 'integrity'].map((id) => {
          const label = orionLocation?.deployedLabels?.find(l => l.id === id)
          const groupRef = { icps: orionIcspRef, esm: orionEsmCtrlRef, integrity: orionIntCtrlRef }[id]
          const p = label?.spinPivot ?? [0, 0, 0]
          const o = label?.spinPivotOffset ?? [0, 0, 0]
          return <SectionPivotGizmo key={id} groupRef={groupRef} pivot={[p[0] + o[0], p[1] + o[1], p[2] + o[2]]} />
        })}

        {/* Orion attachment point hotspots */}
        {orionLocation?.hotspots?.map((hs) => {
          const ref = orionAttachmentRefs[hs.attachmentKey]
          if (!ref) return null
          const hsSection = hs.attachmentKey?.startsWith('ESM') ? 'esm'
            : hs.attachmentKey?.startsWith('ICPS') ? 'icps'
            : hs.attachmentKey?.startsWith('Int') ? 'integrity'
            : null
          const hsDirection = orionAttachmentLabelsFlipped
            ? hs.direction
            : (hs.direction === 'right' ? 'left' : 'right')
          const isFocusedAttachment = orionFocusedAttachment === hs.id
          const inSection = orionFocusedSection === hsSection
          // Tablet callout-line override (see deployedLabels below for the why) —
          // these are the sub-component labels shown after picking a section.
          const hsLine = (isTabletLineActive(mediaQueryKey) && hs.tabletLine)
            ? { ...hs.line, ...hs.tabletLine }
            : hs.line
          return (
            <SatelliteHotspot
              key={hs.id}
              satelliteGroupRef={ref}
              direction={hsDirection}
              line={hsLine}
              text={mobileLabel(hs, mediaQueryKey)}
              secondaryGroupRefs={isFocusedAttachment
                ? (hs.secondaryAttachmentKeys ?? (hs.secondaryAttachmentKey ? [hs.secondaryAttachmentKey] : []))
                    .map(k => orionAttachmentRefs[k]).filter(Boolean)
                : null}
              /* Mobile portrait: never show component labels — the attachment
                 markers are dot-only the moment a section is entered (not just
                 once an attachment is focused), so the labels don't flicker up
                 while selecting a part. Desktop/tablet keep the pick-from-labels
                 flow (dotOnly only once an attachment is focused). */
              dotOnly={mediaQueryKey === 'mobilePortrait' ? true : !!orionFocusedAttachment}
              /* Mobile: show ONLY the focused attachment's dot — never all of them.
                 In the brief transient after selecting a section but before the
                 auto-focus effect picks the first attachment, `orionFocusedAttachment`
                 is null; the desktop `: true` would flash EVERY in-section dot for
                 that frame. On mobile require a focused attachment (the effect fills
                 it in immediately), so only the one dot ever appears. */
              visible={mediaQueryKey === 'mobilePortrait'
                ? (inSection && !!orionFocusedAttachment && isFocusedAttachment)
                : (inSection && (orionFocusedAttachment ? isFocusedAttachment : true))}
              flipDiagOnMobile={false}
              onClick={orionHotspotClickHandlers[hs.id]}
            />
          )
        })}
        {/* Deployed section labels — driven by deployedLabels in locations.json orion entry */}
        {orionLocation?.deployedLabels?.map((label) => {
          const ref = { sIcps: orionSIcpsRef, sEsm: orionSEsmRef, sIntegrity: orionSIntegrityRef }[label.ref]
          const groupRef = { icsp: orionIcspRef, esm: orionEsmCtrlRef, integrity: orionIntCtrlRef }[label.groupRef]
          if (!ref) return null
          const direction = orionSectionLabelsFlipped
            ? label.direction
            : (label.direction === 'right' ? 'left' : 'right')
          // Tablet callout-line override via a tabletLine field in locations.json,
          // merged over the base line (only the differing fields, e.g. seg3).
          const activeLine = (isTabletLineActive(mediaQueryKey) && label.tabletLine)
            ? { ...label.line, ...label.tabletLine }
            : label.line
          return (
            <SatelliteHotspot
              key={label.id}
              satelliteGroupRef={ref}
              mobileOffset={label.mobileOffset}
              direction={direction}
              line={activeLine}
              text={mobileLabel(label, mediaQueryKey)}
              visible={orionDeployed && orionHotspotsReady && !orionFocusedSection}
              flipDiagOnMobile={false}
              onClick={() => {
                if (orionFocusedSection === label.id) {
                  orionSectionLookAtRef.current = null
                  setOrionFocusedSection(null)
                } else {
                  trackEvent(`orion_section_${label.id}`)
                  const piv = label.spinPivot ?? [0, 0, 0]
                  const po = label.spinPivotOffset ?? [0, 0, 0]
                  const wp = new Vector3(piv[0] + po[0], piv[1] + po[1], piv[2] + po[2])
                  if (groupRef?.current) {
                    let savedOuterQuat = null
                    if (orionOuterRef.current) {
                      // Neutralise to the outer's REST pose (the assembly snaps back
                      // to rest when a section is focused), not identity — on mobile
                      // the rest pose is rotated 45°, so identity would mis-place the lookAt.
                      savedOuterQuat = orionOuterRef.current.quaternion.clone()
                      orionOuterRef.current.quaternion.copy(orionOuterRestQuat(mediaQueryKey))
                    }
                    // Read from the SETTLED separation z (see focusOrionSection) so
                    // the look-at is the same on first visit and on return from
                    // another section — not the section's live in-flight z.
                    const settledZ = ORION_SECTION_SETTLED_Z[label.id]
                    const savedZ = groupRef.current.position.z
                    if (settledZ !== undefined) groupRef.current.position.z = settledZ
                    groupRef.current.updateWorldMatrix(true, false)
                    wp.applyMatrix4(groupRef.current.matrixWorld)
                    groupRef.current.position.z = savedZ
                    if (savedOuterQuat) orionOuterRef.current.quaternion.copy(savedOuterQuat)
                  }
                  const camCfg = resolveLabelCameraConfig(label, mediaQueryKey)
                  const off = camCfg?.lookAtOffset ?? [0, 0, 0]
                  orionSectionLookAtRef.current = [wp.x + off[0], wp.y + off[1], wp.z + off[2]]
                  if (groupRef?.current) {
                    _flipEulerTmp.setFromQuaternion(groupRef.current.quaternion, 'YXZ')
                    setOrionAttachmentLabelsFlipped(computeYFlip(_flipEulerTmp.y, label.flipBand ?? DEFAULT_FLIP_BAND))
                  }
                  orionOuterSnapToRestRef.current = true
                  setOrionFocusedSection(label.id)
                }
              }}
            />
          )
        })}
  </>)
}

export default function App() {
  return (
    <ArtemisModeProvider>
      <PasswordGate>
        <SceneContent />
      </PasswordGate>
    </ArtemisModeProvider>
  )
}
